import React, { useState, useEffect, useMemo, useRef } from "react";
import { http, api } from "@/lib/api";
import { PlayerHeadshot, TeamLogo, useHeadshotMap } from "@/components/TeamVisuals";
import { teamTextColor } from "@/lib/teams";
import { toast } from "sonner";
import { Calculator, CaretDown, CaretUp, Fingerprint, ArrowsClockwise } from "@phosphor-icons/react";
import { RawRulesBar, flexBoundsIssue } from "@/components/raw/RawRulesBar";
import { StackBoundsCard } from "@/components/raw/StackBoundsCard";
import { ShapeBreakdown } from "@/components/raw/ShapeBreakdown";
import { DEFAULT_STACK_BOUNDS, normalizeBounds, stackBoundsIssue, stacksEnabled } from "@/lib/rawStacks";
import SundayBanner from "@/components/SundayBanner";
import FieldModelPanel from "@/components/FieldModelPanel";
import CompetitorPoolGrid from "@/components/CompetitorPoolGrid";
import { TeamStackHeatmap } from "@/components/TeamStackHeatmap";
import DkTemplateFill from "@/components/DkTemplateFill";
import GameCardGrid from "@/components/GameCardGrid";
import OwnershipCurveViz from "@/components/OwnershipCurveViz";
import ScenarioCompareView from "@/components/raw/ScenarioCompareView";
import GroupRulesPanel from "@/components/raw/GroupRulesPanel";
import RawOnboardingTour from "@/components/raw/RawOnboardingTour";
import { RawBuildFailPanel } from "@/components/raw/RawBuildFailPanel";
import { sortPlayersDKOrder } from "@/lib/dkRoster";
import { useAuth } from "@/lib/auth";
import { useFieldCoverage, FieldCoverageBadge, CoverageConfirmDialog, needsCoverageConfirm } from "@/components/FieldCoverage";

/**
 * Sprint 60.67 — Manual Optimizer page (formerly "Raw Optimizer" / "Raw Math Mode").
 *
 * DFS Army-style manual optimizer with our value-add layers:
 *   • Fingerprint Variance (per-user pre-ILP jitter — no two subscribers get identical lineups)
 *   • Cluster Rotation (post-ILP peer swap for widened exposure without EV loss)
 *
 * User controls: # of lineups, salary caps, exposures, stack topology,
 * per-position projection floors, team exposure caps, Group Rules / IF-THEN
 * engine, locks/excludes/boosts.
 *
 * This page's lineups are isolated from Smart Optimizer in
 * `state.raw_lineups` — the two workflows never interact.
 */
// Numeric extractor per sortable column. Falls back through the DK pool's
// known projection columns so offseason data (avg_ppg only) still sorts.
// Uses "first positive" semantics rather than JS `??` — the DK pool often
// returns `market_mean_proj: 0` (real integer zero) during offseason.
// `??` locks onto that 0 and never reaches avg_ppg, silently breaking
// sort-by-proj and dropping 0-projection players to the bottom regardless
// of direction. `firstPositive` skips zeros so the Manual Optimizer sort
// matches the Smart Optimizer (which uses the same semantics in PlayerPool.jsx).
const firstPositive = (...vals) => {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    const n = Number(v);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return 0;
};
// Sprint 67 — OUT / IR / Doubtful players project 0 and sit in the Removed tab.
const OUT_STATUSES = new Set(["O", "OUT", "D", "DOUBTFUL", "IR", "PUP", "SUS", "SUSP", "SUSPENDED", "PS", "FA"]);
const RAW_RESTORED = new Set(); // dk_ids the user pulled back over the injury feed
const isOut = (p) => (OUT_STATUSES.has(String(p?.injury_status || "").toUpperCase().trim()) || !!p?.unplayable_reason) && !RAW_RESTORED.has(String(p?.dk_id));
// Removed-tab reasons: why a player left the active pool.
const REMOVED_REASONS = [
  { key: "all", label: "All" },
  { key: "injury", label: "Injury / IR" },
  { key: "ps", label: "Practice squad" },
  { key: "fa", label: "Unrostered" },
  { key: "bench", label: "Backup QB" },
  { key: "mine", label: "My excludes" },
];
const removedReasonOf = (p, excludes) => {
  const s = String(p?.injury_status || "").toUpperCase().trim();
  if (isOut(p)) {
    if (s === "PS") return "ps";
    if (s === "FA") return "fa";
    if (!s && p?.unplayable_reason === "BACKUP_QB") return "bench";
    return "injury";
  }
  return excludes.has(p.dk_id) ? "mine" : null;
};
const projOf = (p) => (isOut(p) ? 0 : firstPositive(p?.our_proj, p?.projection, p?.market_mean_proj, p?.avg_ppg));
const ownOf = (p) => firstPositive(p?.market_mean_own, p?.our_own);
// Sprint 62.2 — cockpit parity with Smart pool: ceiling + leverage.
const _POS_CV = { QB: 0.28, RB: 0.36, WR: 0.42, TE: 0.46, DST: 0.52, K: 0.40 };
const ceilOf = (p) => {
  const c = Number(p?.ceiling || 0);
  if (c > 0) return c;
  const pr = projOf(p);
  return pr > 0 ? pr * (1 + 1.2816 * (Number(p?.player_cv) > 0 ? Number(p.player_cv) : (_POS_CV[(p?.position || "").toUpperCase()] ?? 0.40))) : 0;
};
const levOf = (p) => (ceilOf(p) > 0 ? ceilOf(p) / Math.max(0.5, ownOf(p)) : 0);
const mktDeltaOf = (p) => {
  const ours = firstPositive(p?.our_proj, p?.projection);
  const mkt = Number(p?.market_mean_proj || 0);
  return ours > 0 && mkt > 0 ? ours - mkt : null;
};
// Sprint 62.8 — competitor consensus reference columns (read-only; ours stays ours).
const fldProjOf = (p) => Number(p?.field_proj || 0);
const fldOwnOf = (p) => Number(p?.field_own_max || p?.field_own || 0);
// Sprint 60.78 — LocalStorage config persistence.
const RAW_CFG_STORAGE_KEY = "wp_raw_cfg_v1";
// Sprint 60.83 — Named user presets. Storage: array of
// { name, ts, cfg, locks:[], excludes:[], minExposures, maxExposures, boosts, groupRules }
const RAW_PRESETS_STORAGE_KEY = "wp_raw_presets_v1";
function loadPersistedCfg() {
  try {
    const raw = localStorage.getItem(RAW_CFG_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return (p && typeof p === "object") ? p : null;
  } catch { return null; }
}
function loadNamedPresets() {
  try {
    const raw = localStorage.getItem(RAW_PRESETS_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function saveNamedPresets(list) {
  try { localStorage.setItem(RAW_PRESETS_STORAGE_KEY, JSON.stringify(list)); } catch { /* noop */ }
}

// Sprint 60.130 — Named Override Sets. Persist just the per-player
// projection + ownership override maps under a user-picked name so a
// grinder can save "My Sunday Projections" once and re-apply after
// re-loading a fresh slate (which resets the pool cache).
const RAW_OVERRIDE_SETS_KEY = "wp_raw_override_sets_v1";
function loadOverrideSets() {
  try {
    const raw = localStorage.getItem(RAW_OVERRIDE_SETS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function saveOverrideSets(list) {
  try { localStorage.setItem(RAW_OVERRIDE_SETS_KEY, JSON.stringify(list)); } catch { /* noop */ }
}

// Sprint 60.131 — What-If Scenarios. Save the ENTIRE lineups batch under
// a user-picked name so grinders can build (settings A) → save → build
// (settings B) → save → compare exposures side-by-side without losing
// either. Distinct from named presets (which are settings snapshots)
// and override sets (which are proj/own snapshots).
const RAW_SCENARIOS_KEY = "wp_raw_scenarios_v1";
function loadScenarios() {
  try {
    const raw = localStorage.getItem(RAW_SCENARIOS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function saveScenarios(list) {
  try { localStorage.setItem(RAW_SCENARIOS_KEY, JSON.stringify(list)); } catch { /* noop */ }
}

// Sprint 60.92 — DraftKings compliance gate for Raw Optimizer.
// DK ToS forbids one-click 150-lineup generation from a stock projection
// set; the user must make ≥2 substantive tuning decisions (touches)
// before the engine will build. Every locked / excluded / exposed /
// boosted player counts as one touch, as do non-default config knobs
// (stack mix, salary bands, team caps, variance, ceiling mode, etc.).
export const RAW_MIN_TOUCHES = 2;

export function countRawTouches({ cfg, locks, excludes, minExposures, maxExposures, boosts, groupRules, projectionOverrides, ownershipOverrides, cptMinPct, cptMaxPct, restores }) {
  let n = 0;
  n += (restores?.size || 0);
  n += Object.keys(cptMinPct || {}).length + Object.keys(cptMaxPct || {}).length;
  n += locks?.size || 0;
  n += excludes?.size || 0;
  n += Object.keys(minExposures || {}).length;
  n += Object.keys(maxExposures || {}).length;
  n += Object.keys(boosts || {}).length;
  n += (groupRules || []).length;
  // Sprint 60.129 — per-player projection & ownership overrides count each.
  n += Object.values(projectionOverrides || {}).filter((v) => v !== "" && +v > 0).length;
  n += Object.values(ownershipOverrides || {}).filter((v) => v !== "" && +v >= 0 && v !== null && v !== undefined).length;
  const c = cfg || {};
  if ((c.global_min_exposure ?? "") !== "") n++;
  if ((c.global_max_exposure ?? "") !== "") n++;
  if ((c.max_per_team ?? "") !== "") n++;
  if ((c.min_salary ?? "") !== "") n++;
  if ((c.max_salary ?? "") !== "") n++;
  if (c.bring_back) n++;
  if (c.ceiling_mode) n++;
  if ((c.variance ?? 5) !== 5) n++;
  if ((c.min_projection ?? 0) > 0) n++;
  if ((c.min_uniqueness ?? 1) !== 1) n++;
  // Edge layers count as strategic touches when they differ from the defaults:
  // Shadow Field ON (default off), Fingerprint / Rotation OFF (default on).
  if (c.shadow_field_enabled) n++;
  if (c.fingerprint_variance_enabled === false) n++;
  if (c.cluster_rotation_enabled === false) n++;
  if ((c.qb_stack_boost_pct ?? 0) > 0) n++;
  if ((c.max_vs_defense ?? "") !== "") n++;
  // Sprint 60.125 — Automated Leverage Modifiers.
  if (c.leverage_booster_enabled) n++;
  if (c.chalk_tax_enabled) n++;
  if (c.position_proj_floors_enabled) n++;
  if (c.min_distinct_qbs_enabled) n++;
  if (c.team_exposures_enabled) n++;
  // Stack shapes: default is 100% naked QB. Allowing any stacked shape counts.
  if (stacksEnabled(c.stack_bounds)) n++;
  const fb = c.flex_bounds || {};
  if (Object.values(fb).some((v) => Number(v?.min ?? 0) > 0 || Number(v?.max ?? 100) < 100)) n++;
  return n;
}


// Sprint 60.142 — Conflict Detection Banner. Detects redundant or
// mathematically-contradictory configurations and surfaces them
// inline with one-click fixes. Runs client-side against the current
// cfg + groupRules state on every render (cheap — pure logic, no
// derived-pool work). Empty state renders nothing (zero visual noise
// when the user's config is clean).
function detectConflicts(cfg, groupRules, lockedPlayers = []) {
  const rules = Array.isArray(groupRules) ? groupRules : [];
  const conflicts = [];
  // Conflict 0: locks that can never fit a DK roster (position count / salary)
  const slotLimit = { QB: 1, RB: 3, WR: 4, TE: 2, DST: 1 };
  const posCounts = {};
  let lockSalary = 0;
  for (const p of lockedPlayers) {
    const pos = String(p?.position || "").toUpperCase();
    posCounts[pos] = (posCounts[pos] || 0) + 1;
    lockSalary += Number(p?.salary) || 0;
  }
  for (const [pos, n] of Object.entries(posCounts)) {
    if (slotLimit[pos] && n > slotLimit[pos]) {
      conflicts.push({
        id: `lock_overflow_${pos}`,
        severity: "error",
        title: `${n} ${pos}s locked — a DK roster only fits ${slotLimit[pos]}`,
        body: `Every lineup must include all locked players, but only ${slotLimit[pos]} ${pos} slot${slotLimit[pos] === 1 ? "" : "s"} exist${slotLimit[pos] === 1 ? "s" : ""} (FLEX included). Unlock ${n - slotLimit[pos]} to build.`,
        fix: null,
      });
    }
  }
  if (lockSalary > 50000) {
    conflicts.push({
      id: "lock_salary",
      severity: "error",
      title: `Locked players cost $${lockSalary.toLocaleString()} — over the $50,000 cap`,
      body: "The solver can't fit your locks under the DraftKings salary cap. Unlock a player.",
      fix: null,
    });
  }
  const _sb = normalizeBounds(cfg?.stack_bounds);
  const nakedPct = _sb.none.max;
  // 3-man game stack preset signature: team_at_least_n with teams:[…], n:3, positions covering QB/WR/TE/RB
  const has3ManGameStack = rules.some((r) =>
    r?.type === "team_at_least_n" &&
    Array.isArray(r?.teams) && r.teams.length === 2 &&
    Number(r?.n) >= 3
  );
  const hasBringBackPreset = rules.some((r) =>
    r?.type === "team_at_least_n" &&
    r?.team && !r?.teams &&
    Array.isArray(r?.positions) &&
    r.positions.length === 2 &&
    r.positions.includes("WR") && r.positions.includes("TE") &&
    Number(r?.n) === 2
  );
  const hasCapChalkTeamPreset = rules.some((r) =>
    r?.type === "team_at_most_n" && r?.team && Number(r?.n) === 2 && (!r?.positions || r.positions.length === 0)
  );

  // Conflict 1: Naked QB % > 0 + Force 3-Man Game Stack preset (math contradiction)
  if (nakedPct > 0 && has3ManGameStack) {
    conflicts.push({
      id: "naked_vs_3man",
      severity: "error",
      title: "Naked QB % + Force 3-Man Game Stack",
      body: `You allow up to ${nakedPct}% of lineups to have NO stack, but the "Force 3-Man Game Stack" rule demands every lineup include ≥3 players from one game. The solver can't satisfy both — expect infeasibility or timeout.`,
      fix: { action: "set_naked_zero", label: "Set Naked QB to 0%" },
    });
  }
  // Conflict 2: Bring-Back checkbox + Bring-Back preset (redundant)
  if (cfg?.bring_back && hasBringBackPreset) {
    conflicts.push({
      id: "bringback_dup",
      severity: "warn",
      title: "Bring-Back checkbox + Bring-Back preset both active",
      body: `The "Bring-Back · ≥2 of Highest-OU Road WR/TE" preset already forces pass-catcher bring-backs. Keeping the checkbox on stacks another constraint on top — usually harmless but redundant.`,
      fix: { action: "unset_bringback", label: "Turn off Bring-Back checkbox" },
    });
  }
  // Conflict 3: unsolvable stack min/max ranges
  const stackIssue = stackBoundsIssue(cfg?.stack_bounds);
  if (stackIssue) {
    conflicts.push({
      id: "stack_total",
      severity: "error",
      title: `Stack shape ranges can't be solved · ${stackIssue}`,
      body: "Minimums must add up to at most 100% and maximums to at least 100% so every lineup has a shape.",
      fix: { action: "normalize_stack", label: "Reset to Naked QB" },
    });
  }
  const flexIssue = flexBoundsIssue(cfg);
  if (flexIssue) {
    conflicts.push({ id: "flex_bounds", severity: "error", title: `FLEX share ranges can't be solved · ${flexIssue}`,
      body: "FLEX minimums must add up to at most 100% and maximums to at least 100%.", fix: { action: "reset_flex", label: "Reset FLEX to 0–100" } });
  }
  // Conflict 4: Multi-QB target > 25% of lineup count (dilutes conviction)
  if (cfg?.min_distinct_qbs_enabled) {
    const numLineups = Number(cfg?.num_lineups) || 0;
    const minQbs = Number(cfg?.min_distinct_qbs) || 0;
    if (numLineups > 0 && minQbs > 0 && (minQbs / numLineups) > 0.25) {
      conflicts.push({
        id: "multi_qb_dilution",
        severity: "warn",
        title: `Multi-QB target ${minQbs} across ${numLineups} lineups (${Math.round((minQbs / numLineups) * 100)}%) dilutes conviction`,
        body: `Forcing ${minQbs} distinct QBs into only ${numLineups} lineups pulls the solver toward low-projection backup quarterbacks. Rule of thumb: keep this ≤25% of your lineup count.`,
        fix: { action: "cap_multi_qb", label: `Cap at ${Math.max(1, Math.floor(numLineups * 0.25))}` },
      });
    }
  }
  // Conflict 5: Bring-Back preset + 3-Man Game Stack preset (both push same topology)
  if (has3ManGameStack && hasBringBackPreset) {
    conflicts.push({
      id: "3man_vs_bringback",
      severity: "warn",
      title: "3-Man Game Stack + Bring-Back preset both active",
      body: `Both presets force pass-catcher pool topology. Keeping both stacks two overlapping constraints — the tighter (3-man) usually wins, making the softer (bring-back) redundant.`,
      fix: null,
    });
  }
  // Conflict 6: Cap Chalk Team preset appears twice (duplicate)
  const chalkTeamCount = rules.filter((r) =>
    r?.type === "team_at_most_n" && r?.team && Number(r?.n) === 2 && (!r?.positions || r.positions.length === 0)
  ).length;
  if (chalkTeamCount > 1) {
    conflicts.push({
      id: "chalk_team_dup",
      severity: "warn",
      title: `Cap Chalk Team rule appears ${chalkTeamCount}× (duplicate)`,
      body: `You have ${chalkTeamCount} identical "team ≤2" rules — extra copies do nothing but slow the solver. Remove duplicates from the Active Rules list below.`,
      fix: null,
    });
  }
  return conflicts;
}

function ConflictBanner({ cfg, groupRules, lockedPlayers, onFix }) {
  const conflicts = React.useMemo(() => detectConflicts(cfg, groupRules, lockedPlayers), [cfg, groupRules, lockedPlayers]);
  const [expanded, setExpanded] = React.useState(true);
  if (conflicts.length === 0) return null;
  const errCount = conflicts.filter((c) => c.severity === "error").length;
  const warnCount = conflicts.filter((c) => c.severity === "warn").length;
  const dominantTone = errCount > 0 ? "error" : "warn";
  const borderColor = dominantTone === "error" ? "border-[#FF3B30]/60" : "border-[#FFCC00]/60";
  const bgColor = dominantTone === "error" ? "bg-[#FF3B30]/10" : "bg-[#FFCC00]/10";
  const headText = dominantTone === "error" ? "text-[#FF3B30]" : "text-[#FFCC00]";
  return (
    <div
      data-testid="raw-conflict-banner"
      className={`border ${borderColor} ${bgColor} mb-4`}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        data-testid="raw-conflict-banner-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-black/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`mono text-[11px] font-black uppercase tracking-widest ${headText}`}>
            {dominantTone === "error" ? "⚠ Conflict Detected" : "⚠ Redundancy Detected"}
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">
            {errCount > 0 && <span className="text-[#FF3B30] mr-2">{errCount} error{errCount !== 1 ? "s" : ""}</span>}
            {warnCount > 0 && <span className="text-[#FFCC00]">{warnCount} warning{warnCount !== 1 ? "s" : ""}</span>}
          </div>
        </div>
        <div className={`mono text-[10px] uppercase tracking-widest ${headText}`}>
          {expanded ? "▾ Hide" : "▸ Show"}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-3 space-y-2 border-t border-zinc-800/60 pt-3">
          {conflicts.map((c) => (
            <div
              key={c.id}
              data-testid={`raw-conflict-${c.id}`}
              className={`border ${c.severity === "error" ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : "border-[#FFCC00]/30 bg-[#FFCC00]/5"} p-2.5`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className={`mono text-[10px] font-bold uppercase tracking-widest mb-1 ${c.severity === "error" ? "text-[#FF3B30]" : "text-[#FFCC00]"}`}>
                    {c.title}
                  </div>
                  <div className="text-[11px] text-zinc-300 leading-relaxed">{c.body}</div>
                </div>
                {c.fix && (
                  <button
                    type="button"
                    onClick={() => onFix(c.fix.action)}
                    data-testid={`raw-conflict-fix-${c.id}`}
                    className="flex-shrink-0 mono text-[10px] uppercase tracking-widest border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors px-2.5 py-1 font-bold"
                  >
                    {c.fix.label}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RawOptimizer() {
  const headshots = useHeadshotMap();
  // Sprint 60.132 — Gate initial API probes on auth-ready state so the
  // /slate/games, /optimize/raw/lineups, and pool probes don't fire two
  // pre-auth 401s into Sentry on every page load.
  const { user, loading: authLoading } = useAuth();
  const persisted = loadPersistedCfg();
  const [pool, setPool] = useState([]);
  const [lineups, setLineups] = useState([]);
  const [buildDiag, setBuildDiag] = useState(null);
  // Sprint 60.127 — Late-Swap Diff. Snapshot the prior lineup batch each
  // time we get a fresh result. When the user re-runs after adding a lock
  // or tweaking a setting, the Diff tab shows added/removed players per
  // lineup id. `prevLineups` never renders directly — only via useMemo diff.
  const [prevLineups, setPrevLineups] = useState([]);
  const [showDiff, setShowDiff] = useState(false);

  // Sprint 60.127 — Late-Swap Diff. Pair current and previous lineups by
  // lineup_id when available, else by ordinal index. Compute added /
  // removed player sets per pair. Fully client-side (no backend call).
  const lineupDiff = useMemo(() => {
    if (!lineups.length || !prevLineups.length) return [];
    const prevById = new Map();
    prevLineups.forEach((L, idx) => {
      const key = L.lineup_id ?? `idx_${idx}`;
      prevById.set(key, L);
    });
    return lineups.map((L, idx) => {
      const key = L.lineup_id ?? `idx_${idx}`;
      const prev = prevById.get(key);
      const currIds = new Set((L.players || []).map((p) => String(p.dk_id)));
      const prevIds = new Set((prev?.players || []).map((p) => String(p.dk_id)));
      const added = (L.players || []).filter((p) => !prevIds.has(String(p.dk_id)));
      const removed = (prev?.players || []).filter((p) => !currIds.has(String(p.dk_id)));
      return {
        idx,
        lineup_id: key,
        added, removed,
        proj_delta: (L.projection || 0) - (prev?.projection || 0),
        sal_delta: (L.salary || 0) - (prev?.salary || 0),
        unchanged: added.length === 0 && removed.length === 0,
      };
    });
  }, [lineups, prevLineups]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [posFilter, setPosFilter] = useState("ALL");
  const [locks, setLocks] = useState(() => new Set(persisted?.locks || []));
  // Sprint 69.2 — feed-removed (OUT/D/IR) players the user restored to the active pool.
  const [restores, setRestores] = useState(() => new Set((persisted?.restores || []).map(String)));
  useMemo(() => { RAW_RESTORED.clear(); for (const id of restores) RAW_RESTORED.add(String(id)); }, [restores]);
  const toggleRestore = (dk_id) => setRestores((s) => { const n = new Set(s); n.has(String(dk_id)) ? n.delete(String(dk_id)) : n.add(String(dk_id)); return n; });
  const [excludes, setExcludes] = useState(() => new Set(persisted?.excludes || []));
  // Sprint 64.3 — Showdown captain pins (only rendered on 1-game slates).
  const [cptLocks, setCptLocks] = useState(() => new Set());
  const [cptExcludes, setCptExcludes] = useState(() => new Set());
  // Sprint 68 — per-player captain exposure floor / cap (% of lineups).
  const [cptMinPct, setCptMinPct] = useState({});
  const [cptMaxPct, setCptMaxPct] = useState({});
  const setCptPct = (setter) => (dk_id, v) => setter((prev) => {
    const next = { ...prev };
    if (v === "" || v == null) delete next[dk_id]; else next[dk_id] = Number(v);
    return next;
  });
  const setCptMin = setCptPct(setCptMinPct);
  const setCptMax = setCptPct(setCptMaxPct);
  const toggleCptLock = (dk_id) => {
    setCptLocks((s) => { const n = new Set(s); n.has(dk_id) ? n.delete(dk_id) : n.add(dk_id); return n; });
    setCptExcludes((s) => { const n = new Set(s); n.delete(dk_id); return n; });
  };
  const toggleCptExclude = (dk_id) => {
    setCptExcludes((s) => { const n = new Set(s); n.has(dk_id) ? n.delete(dk_id) : n.add(dk_id); return n; });
    setCptLocks((s) => { const n = new Set(s); n.delete(dk_id); return n; });
  };
  const [minExposures, setMinExposures] = useState(persisted?.minExposures || {});
  const [maxExposures, setMaxExposures] = useState(persisted?.maxExposures || {});
  const [boosts, setBoosts] = useState(persisted?.boosts || {});
  // Sprint 60.129 — per-player projection & ownership overrides. Keyed by
  // dk_id. Backend pipes projectionOverrides into player_overrides.proj_override
  // (wholesale replacement, bypasses model + boost) and ownershipOverrides
  // onto the pool's ownership columns BEFORE leverage modifiers key off it.
  const [projectionOverrides, setProjectionOverrides] = useState(persisted?.projectionOverrides || {});
  const [ownershipOverrides, setOwnershipOverrides] = useState(persisted?.ownershipOverrides || {});
  const [groupRules, setGroupRules] = useState(persisted?.groupRules || []);
  const [expandedLineups, setExpandedLineups] = useState(() => new Set());
  // Sprint 60.92 — position-aware bulk exposure inputs (drives the
  // Set on Visible controls in the bulk toolbar).
  const [bulkMinExp, setBulkMinExp] = useState("");
  const [bulkMaxExp, setBulkMaxExp] = useState("");
  // Bulk Min Proj purge — writes to `excludes` for every player in the
  // visible scope whose projection is below the threshold. Locked players
  // are skipped so an explicit "always in" wins over the bulk floor.
  const [bulkMinProj, setBulkMinProj] = useState("");
  // Removed Players tab — flips the pool table to only show players in
  // the `excludes` Set. Gives the user a review surface (and a Restore
  // All escape hatch) after a Min Proj purge.
  const [showRemoved, setShowRemoved] = useState(false);
  const [removedReason, setRemovedReason] = useState("all");
  // Sprint 60.141 — Column-header sort in the Player Pool table. Click a
  // sortable header to toggle asc/desc. Applies to Name, Pos, Team,
  // Salary, Proj, Own% (Status and the input columns aren't
  // meaningfully sortable). When `key` is null we fall back to the
  // global `cfg.pool_sort_mode` behavior (position-grouped Proj desc
  // in ALL). Sort object: `{key: "salary"|"proj"|"own"|"name"|"pos"|"team", dir: "asc"|"desc"}`
  const [poolTableSort, setPoolTableSort] = useState({ key: null, dir: "desc" });
  // Sprint 62.8 — Coverage Guard + optional Fld Proj / Fld Own reference columns.
  const { coverage, ensureCoverage } = useFieldCoverage(pool.length);
  const [coverageGateOpen, setCoverageGateOpen] = useState(false);
  const coverageAckRef = useRef(false);
  const [showFieldCols, setShowFieldCols] = useState(() => {
    try { return localStorage.getItem("wp_raw_field_cols") === "1"; } catch { return false; }
  });
  const toggleFieldCols = () => {
    setShowFieldCols((v) => { try { localStorage.setItem("wp_raw_field_cols", v ? "0" : "1"); } catch { /* private mode */ } return !v; });
  };
  const _toggleTableSort = (key) => {
    setPoolTableSort((prev) => {
      if (prev.key !== key) return { key, dir: key === "name" || key === "pos" || key === "team" ? "asc" : "desc" };
      // Same column tapped again: flip direction, or clear back to default on 3rd tap.
      if (prev.dir === "desc") return { key, dir: "asc" };
      return { key: null, dir: "desc" };
    });
  };
  const [slateMeta, setSlateMeta] = useState(null);
  // Sprint 60.78 — lineup sort
  const [lineupSort, setLineupSort] = useState({ key: "projection", dir: "desc" });
  // Sprint 60.83 — named user presets
  const [namedPresets, setNamedPresets] = useState(() => loadNamedPresets());
  // Sprint 60.130 — Named Override Sets (projection + ownership overrides only)
  const [overrideSets, setOverrideSets] = useState(() => loadOverrideSets());
  // Sprint 60.131 — What-If Scenarios (full lineup-batch snapshots)
  const [scenarios, setScenarios] = useState(() => loadScenarios());
  const [compareScenario, setCompareScenario] = useState(null);
  // Sprint 60.84 — lineup diff viewer (Set of indices; max 2)
  const [diffSelection, setDiffSelection] = useState(() => new Set());
  const [proMode] = useState(true); // Sprint 69 — Advanced drawer shows every rule; Simple/Pro split retired
  // Sprint 68 — rules bar on top, Advanced drawer (the
  // full rules rail) collapsed by default so the pool gets the whole width.
  const [advancedOpen, setAdvancedOpen] = useState(() => localStorage.getItem("wp_raw_advanced_open") === "1");
  const [advancedSeen, setAdvancedSeen] = useState(() => localStorage.getItem("wp_raw_advanced_seen") === "1" || localStorage.getItem("wp_raw_advanced_open") === "1");
  const toggleAdvanced = () => {
    localStorage.setItem("wp_raw_advanced_seen", "1");
    setAdvancedSeen(true);
    setAdvancedOpen((v) => { localStorage.setItem("wp_raw_advanced_open", v ? "0" : "1"); return !v; });
  };
  const [cfg, setCfg] = useState(() => ({
    num_lineups: persisted?.cfg?.num_lineups ?? 20,
    min_projection: persisted?.cfg?.min_projection ?? 0,
    min_uniqueness: persisted?.cfg?.min_uniqueness ?? 1,
    stack_mode: persisted?.cfg?.stack_mode ?? "none",
    stack_bounds: persisted?.cfg?.stack_bounds ?? DEFAULT_STACK_BOUNDS,
    flex_bounds: persisted?.cfg?.flex_bounds ?? { RB: { min: 0, max: 100 }, WR: { min: 0, max: 100 }, TE: { min: 0, max: 100 } },
    bring_back: persisted?.cfg?.bring_back ?? false,
    variance: persisted?.cfg?.variance ?? 5,
    ceiling_mode: persisted?.cfg?.ceiling_mode ?? false,
    max_per_team: persisted?.cfg?.max_per_team ?? "",
    min_salary: persisted?.cfg?.min_salary ?? "",
    max_salary: persisted?.cfg?.max_salary ?? "",
    // Sprint 60.79 — global exposure defaults (apply to every unlisted
    // player). Per-player entries in min/maxExposures always win.
    global_min_exposure: persisted?.cfg?.global_min_exposure ?? "",
    global_max_exposure: persisted?.cfg?.global_max_exposure ?? "",
    // Sprint 60.87 — full stack distribution (percentages summing to 100)
    qb_stack_boost_pct: persisted?.cfg?.qb_stack_boost_pct ?? 0,
    max_vs_defense: persisted?.cfg?.max_vs_defense ?? "",
    // Sprint 60.142 — Elite Value Booster + Suppress Public Chalk removed
    // (contradicted the Manual Optimizer contract by silently muting
    // user projections). Fields kept as `false` to preserve persisted
    // cfg schema; UI toggles no longer exist so they can't be re-enabled.
    leverage_booster_enabled: false,
    leverage_booster_pct: persisted?.cfg?.leverage_booster_pct ?? 15,
    chalk_tax_enabled: false,
    chalk_tax_pct: persisted?.cfg?.chalk_tax_pct ?? 20,
    leverage_proj_pct: persisted?.cfg?.leverage_proj_pct ?? 80,
    leverage_own_threshold: persisted?.cfg?.leverage_own_threshold ?? 10,
    chalk_own_threshold: persisted?.cfg?.chalk_own_threshold ?? 25,
    show_leverage_advanced: false,
    // Sprint 60.126 — per-position projection floor (Mass-Market Content Tier parity)
    position_proj_floors_enabled: persisted?.cfg?.position_proj_floors_enabled ?? false,
    position_proj_floors: persisted?.cfg?.position_proj_floors ?? {
      QB: 15, RB: 10, WR: 8, TE: 6, DST: 3,
    },
    // Sprint 60.126 — pool sort mode: proj | leverage | own | salary
    pool_sort_mode: persisted?.cfg?.pool_sort_mode ?? "proj",
    // Sprint 60.127 — Multi-QB Portfolio Mode + Team Exposure Caps
    min_distinct_qbs_enabled: persisted?.cfg?.min_distinct_qbs_enabled ?? false,
    min_distinct_qbs: persisted?.cfg?.min_distinct_qbs ?? 2,
    team_exposures_enabled: persisted?.cfg?.team_exposures_enabled ?? false,
    team_exposures: persisted?.cfg?.team_exposures ?? {},
    // Sprint 60.128 — Boost mode: "pct" (default, legacy) or "abs" points.
    // Toggling clears the boosts map so users don't accidentally
    // interpret a "+15" as "+15 pts" when it was set as "+15%".
    boost_mode: persisted?.cfg?.boost_mode ?? "pct",
    // Sprint 60.129 — Diversity toggles surfaced in the Raw UI. Cluster
    // Rotation was previously silently on via OptimizerConfig default.
    // Fingerprint Variance is now truly per-user (backend XORs user_id +
    // session_salt into the effective seed) — pre-Sprint-129 Raw ran
    // seed=42 for every user, so the "variance" was universal.
    cluster_rotation_enabled: persisted?.cfg?.cluster_rotation_enabled ?? true,
    shadow_field_enabled: persisted?.cfg?.shadow_field_enabled ?? true,
    fingerprint_variance_enabled: persisted?.cfg?.fingerprint_variance_enabled ?? true,
    // Sprint 62.6 — Field & Diversity layer
    field_size: persisted?.cfg?.field_size ?? 150000,
    leverage_lambda: persisted?.cfg?.leverage_lambda ?? 0,
    own_relative_caps: persisted?.cfg?.own_relative_caps ?? false,
    core_max_shared: persisted?.cfg?.core_max_shared ?? null,
    max_expected_dupes: persisted?.cfg?.max_expected_dupes ?? null,
    flex_bias: persisted?.cfg?.flex_bias ?? "any",
    peer_swap_band_pct: persisted?.cfg?.peer_swap_band_pct ?? 5,
    peer_swap_salary_band: persisted?.cfg?.peer_swap_salary_band ?? 400,
    peer_swap_prob: persisted?.cfg?.peer_swap_prob ?? 0.4,
    // Sprint 60.131 — Ownership Curve Viz toggle.
    show_ownership_curve: persisted?.cfg?.show_ownership_curve ?? false,
  }));

  // Persist cfg + selections whenever they change.
  useEffect(() => {
    try {
      localStorage.setItem(
        RAW_CFG_STORAGE_KEY,
        JSON.stringify({
          cfg,
          locks: Array.from(locks),
          excludes: Array.from(excludes),
          restores: Array.from(restores),
          minExposures,
          maxExposures,
          boosts,
          projectionOverrides,
          ownershipOverrides,
          groupRules,
        }),
      );
    } catch { /* noop — storage disabled */ }
  }, [cfg, locks, excludes, restores, minExposures, maxExposures, boosts, projectionOverrides, ownershipOverrides, groupRules]);

  // Sprint 60.92 — Auto-expand every lineup the moment a build lands.
  // Users refuse to click 150 dropdowns just to eyeball rosters.
  useEffect(() => {
    if (lineups.length > 0) {
      setExpandedLineups(new Set(lineups.map((_, i) => i)));
    }
  }, [lineups]);

  // Sprint 60.92 — DK compliance touch counter (drives the DK Gate CTA).
  const rawTouches = useMemo(() => countRawTouches({
    cfg, locks, excludes, minExposures, maxExposures, boosts, groupRules,
    projectionOverrides, ownershipOverrides, cptMinPct, cptMaxPct, restores,
  }), [cfg, locks, excludes, minExposures, maxExposures, boosts, groupRules, projectionOverrides, ownershipOverrides, cptMinPct, cptMaxPct, restores]);
  const dkGateOk = rawTouches >= RAW_MIN_TOUCHES;

  // Sprint 60.142 — Conflict Banner fix actions. Called from
  // <ConflictBanner onFix={...} />. Each action targets one specific
  // detected conflict and applies the minimal state change to resolve
  // it. Non-destructive: never touches user's Group Rules — the banner
  // surfaces those manually so the user can remove intentionally.
  const applyConflictFix = (action) => {
    if (action === "set_naked_zero") {
      const sb = normalizeBounds(cfg.stack_bounds);
      const next = { ...sb, none: { min: 0, max: 0 } };
      if (next.qb_wr.max + next.qb_wr_wr.max < 100) next.qb_wr = { min: next.qb_wr.min, max: 100 };
      setCfg({ ...cfg, stack_mode: "qb_wr", stack_bounds: next });
      toast.success("Naked QB max set to 0% · stacked shapes carry the portfolio");
    } else if (action === "unset_bringback") {
      setCfg({ ...cfg, bring_back: false });
      toast.success("Bring-Back checkbox turned off");
    } else if (action === "normalize_stack") {
      setCfg({ ...cfg, stack_mode: "none", stack_bounds: DEFAULT_STACK_BOUNDS });
      toast.success("Stack shapes reset to Naked QB");
    } else if (action === "reset_flex") {
      setCfg({ ...cfg, flex_bounds: { RB: { min: 0, max: 100 }, WR: { min: 0, max: 100 }, TE: { min: 0, max: 100 } } });
      toast.success("FLEX share reset");
    } else if (action === "cap_multi_qb") {
      const numLineups = Number(cfg.num_lineups) || 0;
      const cap = Math.max(1, Math.floor(numLineups * 0.25));
      setCfg({ ...cfg, min_distinct_qbs: cap });
      toast.success(`Multi-QB target capped at ${cap} (25% of lineups)`);
    }
  };

  // Sprint 60.83 — Save / Load / Delete named presets
  const savePreset = () => {
    const name = prompt("Preset name (e.g. \"My Milly build\"):", "");
    if (!name || !name.trim()) return;
    const clean = name.trim().slice(0, 60);
    const snapshot = {
      name: clean,
      ts: Date.now(),
      cfg,
      locks: Array.from(locks),
      excludes: Array.from(excludes),
      minExposures,
      maxExposures,
      boosts,
      projectionOverrides,
      ownershipOverrides,
      groupRules,
    };
    const next = [...namedPresets.filter((p) => p.name !== clean), snapshot];
    setNamedPresets(next);
    saveNamedPresets(next);
    toast.success(`Preset saved · "${clean}"`);
  };
  const loadPreset = (name) => {
    const p = namedPresets.find((x) => x.name === name);
    if (!p) return;
    setCfg({ ...cfg, ...(p.cfg || {}) });
    setLocks(new Set(p.locks || []));
    setExcludes(new Set(p.excludes || []));
    setMinExposures(p.minExposures || {});
    setMaxExposures(p.maxExposures || {});
    setBoosts(p.boosts || {});
    setProjectionOverrides(p.projectionOverrides || {});
    setOwnershipOverrides(p.ownershipOverrides || {});
    setGroupRules(p.groupRules || []);
    toast.success(`Loaded preset · "${name}"`);
  };
  const deletePreset = (name) => {
    if (!confirm(`Delete preset "${name}"?`)) return;
    const next = namedPresets.filter((p) => p.name !== name);
    setNamedPresets(next);
    saveNamedPresets(next);
    toast.info(`Preset deleted · "${name}"`);
  };

  // Sprint 60.130 — Named Override Sets. Persist ONLY the per-player
  // projection + ownership override maps under a user-picked name.
  // Separate from full-config presets because grinders re-use their
  // custom projections across many different build configs on the same
  // slate day. Applying a set MERGES onto the current overrides (so a
  // user's manual tweak isn't clobbered by a preset load).
  const saveOverrideSet = () => {
    const projCount = Object.keys(projectionOverrides || {}).length;
    const ownCount = Object.keys(ownershipOverrides || {}).length;
    if (projCount === 0 && ownCount === 0) {
      toast.info("Nothing to save yet — enter at least one Proj Override or Own Override first.");
      return;
    }
    const name = prompt(
      `Name for this override set (e.g. "My Sunday Projections"):\n\n${projCount} projection overrides · ${ownCount} ownership overrides`,
      "",
    );
    if (!name || !name.trim()) return;
    const clean = name.trim().slice(0, 60);
    const snapshot = {
      name: clean,
      ts: Date.now(),
      projectionOverrides: { ...(projectionOverrides || {}) },
      ownershipOverrides: { ...(ownershipOverrides || {}) },
    };
    const next = [...overrideSets.filter((s) => s.name !== clean), snapshot];
    setOverrideSets(next);
    saveOverrideSets(next);
    toast.success(`Override set saved · "${clean}"`);
  };
  const loadOverrideSet = (name) => {
    const s = overrideSets.find((x) => x.name === name);
    if (!s) return;
    // Merge onto current so a partial in-progress edit is preserved.
    setProjectionOverrides((prev) => ({ ...prev, ...(s.projectionOverrides || {}) }));
    setOwnershipOverrides((prev) => ({ ...prev, ...(s.ownershipOverrides || {}) }));
    const projCount = Object.keys(s.projectionOverrides || {}).length;
    const ownCount = Object.keys(s.ownershipOverrides || {}).length;
    toast.success(`Loaded · "${name}" · ${projCount} proj · ${ownCount} own`);
  };
  const deleteOverrideSet = (name) => {
    if (!confirm(`Delete override set "${name}"?`)) return;
    const next = overrideSets.filter((s) => s.name !== name);
    setOverrideSets(next);
    saveOverrideSets(next);
    toast.info(`Override set deleted · "${name}"`);
  };

  // Sprint 60.131 — What-If Scenarios. Save the current lineup batch
  // (plus a cheap settings fingerprint) under a name. Compare toggles
  // a side-by-side exposure view against `lineups`.
  const saveScenario = () => {
    if ((lineups || []).length === 0) {
      toast.info("Build a lineup batch first.");
      return;
    }
    const name = prompt(
      `Name this scenario (e.g. "Chalk-heavy build" vs "Contrarian build"):\n\n${lineups.length} lineups will be saved.`,
      "",
    );
    if (!name || !name.trim()) return;
    const clean = name.trim().slice(0, 60);
    const snapshot = {
      name: clean,
      ts: Date.now(),
      lineups,
      settings_fingerprint: {
        stack_bounds: cfg.stack_bounds,
        num_lineups: cfg.num_lineups,
        min_uniqueness: cfg.min_uniqueness,
        ceiling_mode: cfg.ceiling_mode,
        boost_mode: cfg.boost_mode,
      },
    };
    const next = [...scenarios.filter((s) => s.name !== clean), snapshot];
    setScenarios(next);
    saveScenarios(next);
    toast.success(`Scenario saved · "${clean}"`);
  };
  const loadScenario = (name) => {
    const s = scenarios.find((x) => x.name === name);
    if (!s) return;
    setLineups(s.lineups || []);
    setPrevLineups([]);
    toast.success(`Loaded scenario · "${name}" (${(s.lineups || []).length} lineups)`);
  };
  const deleteScenario = (name) => {
    if (!confirm(`Delete scenario "${name}"?`)) return;
    const next = scenarios.filter((s) => s.name !== name);
    setScenarios(next);
    saveScenarios(next);
    setCompareScenario((prev) => (prev === name ? null : prev));
    toast.info(`Scenario deleted · "${name}"`);
  };

  const loadPool = async () => {
    try {
      const r = await http.get("/dk/pool?limit=1000");
      setPool(r.data.rows || []);
    } catch (e) {
      toast.error("Failed to load player pool — apply a slate first");
    }
  };

  const isShowdownSlate = !!slateMeta && (
    String(slateMeta.slate_type || "").toUpperCase() === "SHOWDOWN" || Number(slateMeta.game_count) === 1
  );
  const loadSlateMeta = async () => {
    try {
      const r = await http.get("/slate/games");
      setSlateMeta(r.data || null);
    } catch { /* silent */ }
  };

  const loadLineups = async () => {
    try {
      const r = await http.get("/optimize/raw/lineups");
      setPrevLineups((prev) => (lineups?.length ? lineups : prev));
      setLineups(r.data.lineups || []);
    } catch (e) { /* silent — none yet */ }
  };

  useEffect(() => {
    // Sprint 60.132 — Wait for auth to settle before probing endpoints
    // that require an authenticated user. Prevents cosmetic 401 noise
    // during the pre-hydrate window on page load.
    if (authLoading || !user) return;
    loadPool();
    loadLineups();
    loadSlateMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  // Sprint 60.82 — background solve with progress + cancel
  const [progress, setProgress] = useState(null); // { current, total, phase, phase_label } or null
  // Sprint 60.92 — pollTimer/finalized must be refs, not state. React
  // state updates are async, so the setInterval callback captured the
  // OLD pollTimer=null value from the render that scheduled it, meaning
  // stopPolling() couldn't actually clear the interval. Result: multiple
  // in-flight polls each hit `done=true` and fired duplicate "Built 150
  // lineups" toasts. Refs give us a synchronous handle.
  const pollTimerRef = useRef(null);
  const finalizedRef = useRef(false);
  const idlePollsRef = useRef(0);

  useEffect(() => () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const cancelSolve = async () => {
    try {
      await http.post("/optimize/raw/cancel");
      toast.info("Cancel requested — stopping after the current lineup.");
    } catch (e) {
      toast.error("Cancel failed — try again.");
    }
  };

  const pollProgress = async () => {
    try {
      const r = await http.get("/optimize/raw/progress");
      const d = r.data || {};
      // Solver hasn't registered the run yet (or a peer pod can't see it).
      // Tolerate ~25s of "idle" before giving up so the UI never hangs.
      if (d.phase === "idle" && !d.done) {
        idlePollsRef.current += 1;
        if (idlePollsRef.current > 40) {
          stopPolling();
          setBusy(false);
          setProgress(null);
          finalizedRef.current = true;
          toast.error("Solver never reported progress — please hit Generate again.", { id: "raw-build-complete" });
        }
        return;
      }
      idlePollsRef.current = 0;
      // Sprint 60.92 — Raw-specific phase copy. Backend generate_lineups
      // sets phase_label = "Solving ILP · Combat Stack Matrix" (a Smart
      // Optimizer trade-secret string). Raw Optimizer users deliberately
      // opted out of that engine so the label makes no sense — swap it
      // for a neutral progress hint.
      let phaseLabel = d.phase_label || "";
      if (/combat stack matrix/i.test(phaseLabel)) {
        phaseLabel = "Solving ILP · Building your lineups";
      } else if (/cluster rotation/i.test(phaseLabel)) {
        phaseLabel = "Diversifying · peer-swap rotation";
      }
      // Never let the visible counter go backwards mid-solve. Backend
      // resets `current` on phase transitions (preparing → solving →
      // rotating), which looked like a "0 → 20 → 0 → 40" jitter to the
      // user. Freeze the max value we've observed until the solve is
      // truly done.
      setProgress((prev) => {
        const nextCurrent = Number(d.current || 0);
        const total = Number(d.total || prev?.total || 0);
        const guardedCurrent = prev && !d.done ? Math.max(prev.current || 0, nextCurrent) : nextCurrent;
        return {
          current: guardedCurrent,
          total,
          phase: d.phase || "running",
          phase_label: phaseLabel,
          stop_requested: !!d.stop_requested,
        };
      });
      if (d.done) {
        // Guard against duplicate finalization from in-flight polls.
        if (finalizedRef.current) return;
        finalizedRef.current = true;
        stopPolling();
        setProgress(null);
        setBusy(false);
        setPrevLineups((prev) => (lineups?.length ? lineups : prev));
        setLineups(d.lineups || []);
        if (d.error) {
          toast.error(`Solve failed · ${d.error}`);
          return;
        }
        const diag = d.diagnostics || [];
        if (!d.count && diag.length) {
          setBuildDiag({ messages: diag, culprits: d.culprit_rules || [] });
          toast.error(`Built 0 lineups — ${diag[0]}`, { id: "raw-build-complete" });
        } else if (d.stopped) {
          toast.info(`Cancelled — kept ${d.count} lineup${d.count === 1 ? "" : "s"}`, { id: "raw-build-complete" });
        } else {
          toast.success(`Built ${d.count} lineups`, { id: "raw-build-complete" });
        }
      }
    } catch (e) {
      // On repeated failures, bail out gracefully
      stopPolling();
      setBusy(false);
      setProgress(null);
      toast.error("Lost connection to solver — refresh and try again.");
    }
  };

  const generate = async () => {
    // Sprint 62.8 — Coverage Guard: thin competitor coverage → one-click confirm (once per session).
    if (!coverageAckRef.current && needsCoverageConfirm(await ensureCoverage(), user?.is_admin)) {
      setCoverageGateOpen(true);
      return;
    }
    // Sprint 60.92 — DK compliance gate. Block one-click 150-lineup
    // generation from stock projections; require ≥2 tuning touches.
    const touches = countRawTouches({ cfg, locks, excludes, minExposures, maxExposures, boosts, groupRules, cptMinPct, cptMaxPct, restores });
    if (touches < RAW_MIN_TOUCHES) {
      toast.warning(
        `DraftKings gate · make ${RAW_MIN_TOUCHES - touches} more tuning touch${RAW_MIN_TOUCHES - touches === 1 ? "" : "es"} first`,
        { description: "Lock a player, cap an exposure, add a stack, or nudge any setting off default." },
      );
      return;
    }
    finalizedRef.current = false;
    idlePollsRef.current = 0;
    setBusy(true);
    setBuildDiag(null);
    setProgress({ current: 0, total: cfg.num_lineups, phase: "preparing", phase_label: "Starting solve…" });
    try {
      const minExp = Object.fromEntries(
        Object.entries(minExposures).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const maxExp = Object.fromEntries(
        Object.entries(maxExposures).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const gMin = cfg.global_min_exposure === "" ? null : Math.max(0, Math.min(100, parseInt(cfg.global_min_exposure, 10) || 0));
      const gMax = cfg.global_max_exposure === "" ? null : Math.max(0, Math.min(100, parseInt(cfg.global_max_exposure, 10) || 0));
      if (gMin !== null && gMin > 0) {
        pool.forEach((p) => { const k = String(p.dk_id); if (minExp[k] === undefined) minExp[k] = gMin; });
      }
      if (gMax !== null && gMax < 100) {
        pool.forEach((p) => { const k = String(p.dk_id); if (maxExp[k] === undefined) maxExp[k] = gMax; });
      }
      const boostMap = Object.fromEntries(
        Object.entries(boosts).filter(([, v]) => v !== "" && v !== 0 && v !== null && v !== undefined)
      );
      const boostMode = cfg.boost_mode === "abs" ? "abs" : "pct";
      await http.post("/optimize/raw", {
        num_lineups: cfg.num_lineups,
        min_projection: cfg.min_projection,
        min_uniqueness: cfg.min_uniqueness,
        max_exposure_pct: 100,
        cpt_locks: Array.from(cptLocks),
        cpt_excludes: Array.from(cptExcludes),
        cpt_min_exposures: cptMinPct,
        cpt_max_exposures: cptMaxPct,
        stack_mode: stacksEnabled(cfg.stack_bounds) ? (cfg.stack_mode === "none" ? "qb_wr" : cfg.stack_mode) : "none",
        // Per-stack min/max share — the engine solves the schedule inside these bounds
        stack_exposure: normalizeBounds(cfg.stack_bounds),
        flex_exposure: cfg.flex_bounds || null,
        qb_stack_boost_pct: Math.max(0, Math.min(20, parseInt(cfg.qb_stack_boost_pct, 10) || 0)),
        max_vs_defense: cfg.max_vs_defense === "" ? null : Math.max(0, Math.min(8, parseInt(cfg.max_vs_defense, 10) || 0)),
        bring_back: !!cfg.bring_back && stacksEnabled(cfg.stack_bounds),
        variance: (cfg.variance || 0) / 100,
        ceiling_mode: cfg.ceiling_mode,
        max_per_team: cfg.max_per_team === "" ? null : Math.max(1, Math.min(9, parseInt(cfg.max_per_team, 10) || 4)),
        min_salary: cfg.min_salary === "" ? null : Math.max(0, Math.min(50000, parseInt(cfg.min_salary, 10) || 0)),
        max_salary: cfg.max_salary === "" ? null : Math.max(0, Math.min(50000, parseInt(cfg.max_salary, 10) || 50000)),
        locks: Array.from(locks).map(String),
        excludes: Array.from(excludes).map(String),
        restores: Array.from(restores).map(String),
        min_exposures: minExp,
        max_exposures: maxExp,
        boosts: boostMap,
        boost_mode: boostMode,
        // Sprint 60.129 — projection & ownership overrides. Filter out
        // empty / zero entries so a cleared row doesn't accidentally
        // override the projection to 0.
        projection_overrides: Object.fromEntries(
          Object.entries(projectionOverrides).filter(([, v]) => v !== "" && +v > 0)
        ),
        ownership_overrides: Object.fromEntries(
          Object.entries(ownershipOverrides).filter(([, v]) => v !== "" && v !== null && v !== undefined && +v >= 0)
        ),
        // Sprint 60.129 — Diversity toggles surfaced in Advanced panel.
        cluster_rotation_enabled: !!cfg.cluster_rotation_enabled,
        fingerprint_variance_enabled: !!cfg.fingerprint_variance_enabled,
        shadow_field_enabled: !!cfg.shadow_field_enabled,
        field_size: cfg.field_size ? Number(cfg.field_size) : null,
        leverage_lambda: Number(cfg.leverage_lambda) || 0,
        own_relative_caps: !!cfg.own_relative_caps,
        core_max_shared: cfg.core_max_shared ? Number(cfg.core_max_shared) : null,
        max_expected_dupes: cfg.max_expected_dupes ? Number(cfg.max_expected_dupes) : null,
        flex_bias: "any",
        peer_swap_band_pct: Number(cfg.peer_swap_band_pct) || 5,
        peer_swap_salary_band: Number(cfg.peer_swap_salary_band) || 400,
        peer_swap_prob: Number(cfg.peer_swap_prob) || 0.4,
        // Sprint 60.125 — Automated Leverage Modifiers.
        // Backend applies these AFTER manual `boosts` so both stack.
        // Elite Value Booster: top-20% projection AND sub-10% ownership → bump.
        // Chalk Tax: >25% ownership → suppress.
        leverage_booster_pct: cfg.leverage_booster_enabled
          ? Math.max(1, Math.min(50, parseInt(cfg.leverage_booster_pct, 10) || 15))
          : null,
        chalk_tax_pct: cfg.chalk_tax_enabled
          ? Math.max(1, Math.min(50, parseInt(cfg.chalk_tax_pct, 10) || 20))
          : null,
        // Sprint 60.126 — Advanced qualifying thresholds (send only when
        // Advanced panel was opened; server falls back to defaults 80/10/25
        // otherwise so casual users don't accidentally lock in stale values).
        leverage_proj_pct: cfg.show_leverage_advanced ? +cfg.leverage_proj_pct : null,
        leverage_own_threshold: cfg.show_leverage_advanced ? +cfg.leverage_own_threshold : null,
        chalk_own_threshold: cfg.show_leverage_advanced ? +cfg.chalk_own_threshold : null,
        // Sprint 60.126 — Per-position projection floor.
        position_proj_floors: cfg.position_proj_floors_enabled
          ? Object.fromEntries(
              Object.entries(cfg.position_proj_floors || {})
                .filter(([, v]) => v && +v > 0)
                .map(([k, v]) => [k, +v])
            )
          : null,
        // Sprint 60.127 — Multi-QB Portfolio Mode + Team Exposure Caps.
        min_distinct_qbs: cfg.min_distinct_qbs_enabled
          ? Math.max(1, Math.min(8, parseInt(cfg.min_distinct_qbs, 10) || 2))
          : null,
        team_exposures: cfg.team_exposures_enabled
          ? Object.fromEntries(
              Object.entries(cfg.team_exposures || {})
                .filter(([, v]) => v && +v > 0 && +v < 100)
                .map(([k, v]) => [k, +v])
            )
          : null,
        group_rules: groupRules
          // Sprint 60.141 — Strip empty rules BEFORE serialization.
          // A rule with "— NONE —" on both sides (no players picked
          // yet) was being POSTed as `{type:"if_then_include", if:[],
          // then:[]}` and, while the backend silently drops it,
          // Pydantic validation on older builds rejected the payload
          // and the whole solve returned 400 → "Optimize failed to start".
          // Better UX: silently ignore not-yet-configured rules so the
          // solve runs anyway, and let the rule composer prompt the
          // user to finish setting it up later.
          .filter((r) => {
            if (!r?.type) return false;
            if (r.type === "if_then_exclude" || r.type === "if_then_include") {
              return (r.if?.length || 0) > 0 && (r.then?.length || 0) > 0;
            }
            if (r.type === "if_then_at_least_n") {
              return (r.if?.length || 0) > 0 && (r.players?.length || 0) > 0 && Number(r.n) > 0;
            }
            if (r.type === "team_at_least_n" || r.type === "team_at_most_n") {
              const hasTeam = (Array.isArray(r.teams) && r.teams.length > 0) || !!r.team;
              return hasTeam && (r.positions?.length || 0) > 0 && Number(r.n) >= 0;
            }
            // exactly_n / at_most_n / at_least_n on `players` list
            return (r.players?.length || 0) > 0 && Number.isFinite(Number(r.n));
          })
          .map((r) => {
          // Sprint 60.141 — Coerce every dk_id list to strings. Backend
          // schema is List[str] but the pool payload sometimes ships
          // numeric ids and lock/exclude/rule composers threaded those
          // through unchanged → Pydantic 422 on solve start ("Input
          // should be a valid string · Field · locks.0"). Now every
          // rule-list gets normalized here.
          const _s = (arr) => (arr || []).map(String);
          const base = { type: r.type };
          if (r.type === "if_then_exclude" || r.type === "if_then_include") {
            base.if = _s(r.if);
            base.then = _s(r.then);
          } else if (r.type === "if_then_at_least_n") {
            base.if = _s(r.if);
            base.players = _s(r.players);
            base.n = Number(r.n) || 0;
          } else if (r.type === "team_at_least_n" || r.type === "team_at_most_n") {
            if (Array.isArray(r.teams) && r.teams.length > 0) {
              base.teams = r.teams.map((t) => String(t).toUpperCase());
            } else {
              base.team = String(r.team || "").toUpperCase();
            }
            base.positions = (r.positions || []).map((p) => String(p).toUpperCase());
            base.n = Number(r.n) || 0;
          } else {
            base.players = _s(r.players);
            base.n = Number(r.n) || 0;
          }
          return base;
        }),
        // Sprint 60.95 — Game Lock board integration. Pull the list of
        // unlocked (excluded) games from localStorage so lineups only
        // draw from games the user has locked in.
        excluded_games: (() => {
          try {
            const raw = localStorage.getItem("wp_excluded_games");
            if (!raw) return [];
            const arr = JSON.parse(raw);
            return Array.isArray(arr) ? arr : [];
          } catch { return []; }
        })(),
      });
      // Start polling
      const timer = setInterval(pollProgress, 600);
      pollTimerRef.current = timer;
    } catch (e) {
      setBusy(false);
      setProgress(null);
      finalizedRef.current = true;
      // Sprint 60.141 — Surface the ACTUAL rejection reason. The old
      // generic "Optimize failed to start" toast hid the real 400/422
      // detail behind an opaque label. Now we render whatever the
      // backend said — string details, {code,message} objects, and
      // Pydantic 422 validation arrays alike. Only the DK gate should
      // be able to block a build; every other failure gets loud.
      const status = e?.response?.status;
      const d = e?.response?.data?.detail;
      let msg = "Optimize failed to start";
      let desc = null;
      if (typeof d === "string") {
        msg = d;
      } else if (d && typeof d === "object" && !Array.isArray(d)) {
        msg = d.message || msg;
        if (d.code) desc = `Code · ${d.code}`;
      } else if (Array.isArray(d) && d.length) {
        // Pydantic 422 — array of {loc, msg, type}. Show the first
        // human-readable one and hint at the field.
        const first = d[0] || {};
        const field = Array.isArray(first.loc) ? first.loc.slice(1).join(".") : "";
        msg = `Invalid input · ${first.msg || "check your config"}`;
        desc = field ? `Field · ${field}` : null;
      } else if (status) {
        msg = `Backend returned ${status} · ${e?.message || "unknown"}`;
      }
      toast.error(msg, desc ? { description: desc, duration: 10000 } : undefined);
      // Also log the full payload for prod DevTools debugging.
      console.error("optimize/raw failed:", { status, detail: d, error: e });
    }
  };

  const toggleLock = (dk_id) => {
    setLocks((s) => {
      const next = new Set(s);
      next.has(dk_id) ? next.delete(dk_id) : next.add(dk_id);
      return next;
    });
    setExcludes((s) => {
      const next = new Set(s);
      next.delete(dk_id);
      return next;
    });
  };
  const toggleExclude = (dk_id) => {
    setExcludes((s) => {
      const next = new Set(s);
      next.has(dk_id) ? next.delete(dk_id) : next.add(dk_id);
      return next;
    });
    setLocks((s) => {
      const next = new Set(s);
      next.delete(dk_id);
      return next;
    });
  };

  // Sprint 60.105 — reactive game-lock exclusion. Mirrors the shared
  // `wp_excluded_games` state so the Raw Optimizer pool view (and
  // bulk operations that operate on `filteredPool`) only see players
  // from LOCKED games. Solve-time filter already applied server-side.
  const [rawExcludedGames, setRawExcludedGames] = useState(() => {
    try {
      const raw = localStorage.getItem("wp_excluded_games");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    const h = () => {
      try {
        const raw = localStorage.getItem("wp_excluded_games");
        setRawExcludedGames(new Set(raw ? JSON.parse(raw) : []));
      } catch { /* noop */ }
    };
    window.addEventListener("wp:excluded-games", h);
    return () => window.removeEventListener("wp:excluded-games", h);
  }, []);

  // Sprint 60.125 — Automated Leverage Modifiers · qualifying-player sets.
  // Same math the backend applies, computed client-side so the pool UI
  // can show badges for the affected rows (transparency = trust).
  // Elite Value Booster → dk_ids in top-20% projection AND sub-10% own.
  // Chalk Tax → dk_ids with market_mean_own > 25%.
  //
  // Sprint 60.141 — Read ownership from `market_mean_own → our_own → 0`
  // fallback. Some payload shapes only ship `our_own` (which is what
  // the pool UI displays), and the earlier `p.market_mean_own || 0`
  // silently coerced `undefined` to 0, tripping `< 10%` for every
  // player and boosting the ENTIRE pool. Fix: coalesce properly.
  const _own = (p) => {
    const mm = p?.market_mean_own;
    if (mm != null && mm !== "") return Number(mm) || 0;
    const oo = p?.our_own;
    if (oo != null && oo !== "") return Number(oo) || 0;
    return 0;
  };
  const leverageBoostedIds = useMemo(() => {
    if (!cfg.leverage_booster_enabled || pool.length === 0) return new Set();
    // Sprint 60.126 — tunable thresholds. Fall back to Sprint 60.125 defaults
    // when Advanced panel hasn't been opened.
    const projPct = cfg.show_leverage_advanced ? (+cfg.leverage_proj_pct || 80) : 80;
    const ownCeiling = cfg.show_leverage_advanced ? (+cfg.leverage_own_threshold || 10) : 10;
    const sorted = [...pool].sort((a, b) => (b.projection || 0) - (a.projection || 0));
    const cutoffIdx = Math.max(0, Math.floor(pool.length * (1 - projPct / 100)));
    const projCutoff = sorted[cutoffIdx]?.projection ?? 0;
    return new Set(
      pool
        .filter((p) => (p.projection || 0) >= projCutoff && _own(p) < ownCeiling)
        .map((p) => String(p.dk_id))
    );
  }, [cfg.leverage_booster_enabled, cfg.show_leverage_advanced, cfg.leverage_proj_pct, cfg.leverage_own_threshold, pool]);

  const chalkTaxedIds = useMemo(() => {
    if (!cfg.chalk_tax_enabled || pool.length === 0) return new Set();
    const ownFloor = cfg.show_leverage_advanced ? (+cfg.chalk_own_threshold || 25) : 25;
    return new Set(
      pool
        .filter((p) => (p.market_mean_own || 0) > ownFloor)
        .map((p) => String(p.dk_id))
    );
  }, [cfg.chalk_tax_enabled, cfg.show_leverage_advanced, cfg.chalk_own_threshold, pool]);



  const lockedPlayers = useMemo(() => pool.filter((p) => locks.has(p.dk_id)), [pool, locks]);
  const removedCount = useMemo(
    () => pool.filter((p) => excludes.has(p.dk_id) || (isOut(p) && !locks.has(p.dk_id))).length,
    [pool, excludes, locks, restores],
  );

  const filteredPool = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = pool;
    // Removed tab — bypass the active-pool filters entirely and show
    // ONLY excluded players. Users still get pos + search to narrow a
    // large removed list, but game locks / active filters are dropped
    // so a purge run is fully visible in one place.
    if (showRemoved) {
      rows = rows.filter((p) => excludes.has(p.dk_id) || (isOut(p) && !locks.has(p.dk_id)));
      if (removedReason !== "all") rows = rows.filter((p) => removedReasonOf(p, excludes) === removedReason);
      if (posFilter !== "ALL") {
        rows = rows.filter((p) => (p.position || "").toUpperCase() === posFilter);
      }
      if (q) {
        rows = rows.filter(
          (p) => (p.name || "").toLowerCase().includes(q) ||
                 (p.team || "").toLowerCase().includes(q) ||
                 (p.position || "").toLowerCase().includes(q),
        );
      }
      return rows.slice(0, 300);
    }
    // Active pool — hide excluded + OUT players (they live in the Removed tab).
    rows = rows.filter((p) => !excludes.has(p.dk_id) && (!isOut(p) || locks.has(p.dk_id)));
    // Hide players from unlocked games first — matches solve-time behavior.
    if (rawExcludedGames.size > 0) {
      rows = rows.filter((p) => !p.game_id || !rawExcludedGames.has(p.game_id));
    }
    if (posFilter !== "ALL") {
      rows = rows.filter((p) => (p.position || "").toUpperCase() === posFilter);
    }
    if (q) {
      rows = rows.filter(
        (p) => (p.name || "").toLowerCase().includes(q) ||
               (p.team || "").toLowerCase().includes(q) ||
               (p.position || "").toLowerCase().includes(q),
      );
    }
    // Sprint 60.141 — If the user has actively tapped a header, honor
    // that sort explicitly (bypasses the sort-mode preset AND the
    // position grouping). Position-priority secondary sort still
    // applies to the sort-mode preset path below.
    if (poolTableSort.key) {
      const k = poolTableSort.key;
      const flip = poolTableSort.dir === "asc" ? 1 : -1;
      const _get = (p) => {
        if (k === "name") return (p.name || "").toLowerCase();
        if (k === "pos")  return (p.position || "").toUpperCase();
        if (k === "team") return (p.team || "").toUpperCase();
        if (k === "salary") return p.salary || 0;
        // Sprint 60.142 — Parity with Smart Optimizer PlayerPool: use
        // firstPositive fallback so a rookie with `projection: 0` but
        // `avg_ppg: 12.5` sorts by 12.5 (not 0). Previous `p.projection || 0`
        // clustered every 0-projection player at the bottom regardless of
        // whether they had a meaningful fallback value.
        if (k === "proj") return projOf(p);
        if (k === "own")  return ownOf(p);
        if (k === "ceil") return ceilOf(p);
        if (k === "lev")  return levOf(p);
        if (k === "mkt")  return mktDeltaOf(p) ?? -999;
        if (k === "fproj") return fldProjOf(p);
        if (k === "fown")  return fldOwnOf(p);
        return 0;
      };
      rows = [...rows].sort((a, b) => {
        const va = _get(a); const vb = _get(b);
        if (typeof va === "string") {
          const primary = flip * va.localeCompare(vb);
          if (primary !== 0) return primary;
          // Tie-break: projection desc so within a team (or a name/pos
          // tie) the higher-projected player floats to the top. Keeps
          // the per-team roster view scannable — e.g. sorting all WRs
          // by Team clusters ATL WRs together in proj order, then BAL,
          // then BUF, etc., so Min/Max Exp % tweaks by team are a
          // straight scroll instead of a search.
          return projOf(b) - projOf(a);
        }
        return flip * (va - vb);
      });
      return rows.slice(0, 300);
    }

    // Sprint 60.126 — Pool sort mode (proj / leverage / own / salary).
    // Leverage score = projection_rank - ownership_rank (both normalized 0-100
    // desc-by-projection means rank 0 = highest proj; asc-by-own means rank 0 =
    // lowest owned). Composite score rewards high-proj low-owned players.
    //
    // Sprint 60.141 — When the "ALL" filter is active, apply a
    // position-priority secondary sort (QB → RB → WR → TE → DST) so
    // users don't land on a wall of defenses (they carry static
    // 8-11pt baseline projections that outrank offseason-zeroed
    // skill players by default). Applies to every sort mode so the
    // grouping stays consistent.
    const sortMode = cfg.pool_sort_mode || "proj";
    const POS_ORDER = { QB: 0, RB: 1, WR: 2, TE: 3, DST: 4 };
    const _posRank = (p) => POS_ORDER[(p.position || "").toUpperCase()] ?? 99;
    const _groupByPos = posFilter === "ALL";
    if (sortMode === "leverage" && rows.length > 1) {
      const projRank = new Map();
      const ownRank = new Map();
      [...rows].sort((a, b) => projOf(b) - projOf(a))
        .forEach((p, i) => projRank.set(p.dk_id, i));
      [...rows].sort((a, b) => ownOf(a) - ownOf(b))
        .forEach((p, i) => ownRank.set(p.dk_id, i));
      rows = [...rows].sort((a, b) => {
        if (_groupByPos) {
          const pr = _posRank(a) - _posRank(b);
          if (pr !== 0) return pr;
        }
        return (projRank.get(a.dk_id) + ownRank.get(a.dk_id)) -
               (projRank.get(b.dk_id) + ownRank.get(b.dk_id));
      });
    } else if (sortMode === "own") {
      rows = [...rows].sort((a, b) => {
        if (_groupByPos) {
          const pr = _posRank(a) - _posRank(b);
          if (pr !== 0) return pr;
        }
        return ownOf(b) - ownOf(a);
      });
    } else if (sortMode === "salary") {
      rows = [...rows].sort((a, b) => {
        if (_groupByPos) {
          const pr = _posRank(a) - _posRank(b);
          if (pr !== 0) return pr;
        }
        return (b.salary || 0) - (a.salary || 0);
      });
    } else {
      rows = [...rows].sort((a, b) => {
        if (_groupByPos) {
          const pr = _posRank(a) - _posRank(b);
          if (pr !== 0) return pr;
        }
        return projOf(b) - projOf(a);
      });
    }
    return rows.slice(0, 300);
  }, [pool, search, posFilter, rawExcludedGames, showRemoved, removedReason, excludes, locks, restores, cfg.pool_sort_mode, poolTableSort]);

  // Sprint 60.78 — lineup sort. Wraps each lineup with its original
  // index so remove buttons can still target state.lineups by index.
  const sortedLineups = useMemo(() => {
    const wrapped = (lineups || []).map((lineup, originalIndex) => ({ lineup, originalIndex }));
    const key = lineupSort.key;
    const dir = lineupSort.dir === "asc" ? 1 : -1;
    if (key === "index") return dir === 1 ? wrapped : [...wrapped].reverse();
    if (key === "field_win") {
      return [...wrapped].sort((a, b) => ((Number(a.lineup?.sim_win_rank) || 1e9) - (Number(b.lineup?.sim_win_rank) || 1e9)) * -dir);
    }
    return [...wrapped].sort((a, b) => {
      const va = Number(a.lineup?.[key] || 0);
      const vb = Number(b.lineup?.[key] || 0);
      return (va - vb) * dir;
    });
  }, [lineups, lineupSort]);

  const setExposure = (setter) => (dkId, val) => {
    setter((prev) => {
      const next = { ...prev };
      if (val === "" || val === null || val === undefined) {
        delete next[dkId];
      } else {
        const n = Math.max(0, Math.min(100, parseInt(val, 10) || 0));
        next[dkId] = n;
      }
      return next;
    });
  };
  const setMinExp = setExposure(setMinExposures);
  const setMaxExp = setExposure(setMaxExposures);
  // Sprint 62.3 — cockpit parity: Like = one-click soft boost; keyboard cursor.
  const LIKE_VAL = () => (cfg.boost_mode === "abs" ? 2 : 10);
  const isLiked = (dkId) => Number(boosts[dkId]) >= LIKE_VAL();
  const toggleLike = (dkId) => setBoost(dkId, isLiked(dkId) ? "" : LIKE_VAL());
  const [rawFocusId, setRawFocusId] = useState(null);
  const rawTableRef = useRef(null);
  const onRawPoolKeyDown = (e) => {
    if (e.target && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
    const n = filteredPool.length;
    if (!n) return;
    const key = e.key;
    const curIdx = filteredPool.findIndex((r) => r.dk_id === rawFocusId);
    if (key === "ArrowDown" || key === "ArrowUp") {
      e.preventDefault();
      const next = key === "ArrowDown" ? Math.min(n - 1, curIdx + 1) : Math.max(0, curIdx - 1);
      setRawFocusId(filteredPool[next].dk_id);
      rawTableRef.current?.querySelector(`[data-row-idx="${next}"]`)?.scrollIntoView({ block: "nearest" });
      return;
    }
    if (key === "Escape") { setRawFocusId(null); return; }
    if (curIdx < 0) return;
    const p = filteredPool[curIdx];
    const k = key.toLowerCase();
    if (k === "l") { e.preventDefault(); toggleLock(p.dk_id); }
    else if (k === "x") { e.preventDefault(); toggleExclude(p.dk_id); }
    else if (k === "f") { e.preventDefault(); toggleLike(p.dk_id); }
  };

  const setBoost = (dkId, val) => {
    setBoosts((prev) => {
      const next = { ...prev };
      if (val === "" || val === null || val === undefined || val === 0) {
        delete next[dkId];
      } else if (cfg.boost_mode === "abs") {
        // Absolute-points mode: clamp to ±15 pts (allow one decimal).
        let n = parseFloat(val);
        if (!Number.isFinite(n)) { delete next[dkId]; return next; }
        n = Math.max(-15, Math.min(15, Math.round(n * 10) / 10));
        if (n === 0) delete next[dkId]; else next[dkId] = n;
      } else {
        const n = Math.max(-25, Math.min(25, parseInt(val, 10) || 0));
        if (n === 0) delete next[dkId]; else next[dkId] = n;
      }
      return next;
    });
  };

  // Sprint 60.129 — Per-player projection & ownership override setters.
  // Projection override is a wholesale replacement (0-60 pts, blank clears).
  // Ownership override is 0-100% (blank clears; 0 is legal to model "no chalk").
  const setProjOverride = (dkId, val) => {
    setProjectionOverrides((prev) => {
      const next = { ...prev };
      if (val === "" || val === null || val === undefined) {
        delete next[dkId];
      } else {
        const n = Math.max(0, Math.min(60, parseFloat(val)));
        if (!Number.isFinite(n) || n <= 0) {
          delete next[dkId];
        } else {
          next[dkId] = Math.round(n * 10) / 10;
        }
      }
      return next;
    });
  };
  const setOwnOverride = (dkId, val) => {
    setOwnershipOverrides((prev) => {
      const next = { ...prev };
      if (val === "" || val === null || val === undefined) {
        delete next[dkId];
      } else {
        const n = Math.max(0, Math.min(100, parseFloat(val)));
        if (!Number.isFinite(n)) {
          delete next[dkId];
        } else {
          next[dkId] = Math.round(n * 10) / 10;
        }
      }
      return next;
    });
  };

  // Sprint 60.129 — Randomize (post-build shuffle) via cluster rotation.
  const randomizeLineups = async () => {
    if (busy || (lineups || []).length === 0) return;
    setBusy(true);
    try {
      const r = await http.post("/optimize/raw/randomize");
      const shuffled = r.data?.lineups || [];
      if (shuffled.length > 0) {
        setLineups(shuffled);
        toast.success(`Shuffled · ${r.data?.swapped_lineups || 0} lineups touched, ${r.data?.total_swaps || 0} peer swaps`);
      } else {
        toast.info("Nothing to shuffle — build a batch first.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Randomize failed";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  // Sprint 60.131 — Late-Swap Auto-Push. Prompts for a scratched dk_id
  // and auto-selects a same-position, salary-band peer as the replacement.
  const autoSwapScratched = async () => {
    if (busy || (lineups || []).length === 0) return;
    // Compute unique rostered player IDs across all lineups so the user
    // can pick which one to swap out (typically an inactive/OUT designation).
    const rosteredIds = new Set();
    lineups.forEach((L) => {
      (L.players || []).forEach((p) => rosteredIds.add(String(p.dk_id)));
    });
    const rosteredPlayers = Array.from(rosteredIds)
      .map((id) => pool.find((p) => String(p.dk_id) === id))
      .filter(Boolean)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (rosteredPlayers.length === 0) {
      toast.info("No rostered players to swap.");
      return;
    }
    const nameToId = new Map(rosteredPlayers.map((p) => [`${p.name} (${p.position}·${p.team})`, String(p.dk_id)]));
    const promptText =
      `Which player was scratched? Type or paste the name.\n\nRostered on your ${lineups.length} lineups:\n` +
      Array.from(nameToId.keys()).slice(0, 30).join("\n") +
      (nameToId.size > 30 ? `\n… + ${nameToId.size - 30} more` : "");
    const input = window.prompt(promptText, "");
    if (!input || !input.trim()) return;
    // Fuzzy match by substring
    const needle = input.trim().toLowerCase();
    let chosenId = null;
    for (const [label, id] of nameToId.entries()) {
      if (label.toLowerCase().includes(needle)) { chosenId = id; break; }
    }
    if (!chosenId) {
      toast.error(`No rostered player matches "${input}".`);
      return;
    }
    const chosenName = rosteredPlayers.find((p) => String(p.dk_id) === chosenId)?.name || chosenId;
    if (!window.confirm(`Auto-swap ${chosenName} out of every lineup they're in? WarPaint will pick the highest-projection same-position peer (±$500 salary) that fits under the cap.`)) return;
    setBusy(true);
    try {
      const r = await http.post("/optimize/raw/auto-swap", { scratched_dk_id: chosenId });
      const updated = r.data?.lineups || [];
      if (updated.length > 0) {
        setLineups(updated);
        const applied = r.data?.swaps_applied || 0;
        const failed = (r.data?.failed_lineups || []).length;
        toast.success(
          `Auto-swap · ${chosenName} → ${applied} lineup${applied === 1 ? "" : "s"}` +
          (failed > 0 ? ` · ${failed} skipped (no viable peer under cap)` : "")
        );
      } else {
        toast.info("No lineups updated.");
      }
    } catch (e) {
      // Sprint 60.132 — Targeted error toasts for structured backend
      // error codes (DK_ID_NOT_IN_POOL, POOL_MISSING, POSITION_MISMATCH,
      // etc.) instead of a single generic 400 message.
      const detail = e?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object" && detail.code) {
        switch (detail.code) {
          case "DK_ID_NOT_IN_POOL":
            msg = `Player not in current pool — reload the slate and try again`;
            break;
          case "POOL_MISSING":
            msg = "Slate pool missing — click Refresh in the Data tab, then rebuild";
            break;
          case "NO_LINEUPS":
            msg = "Build a lineup batch before swapping";
            break;
          case "POSITION_MISMATCH":
            msg = `Position mismatch — replacement (${detail.replacement_position}) doesn't match scratched (${detail.scratched_position})`;
            break;
          case "REPLACEMENT_NOT_IN_POOL":
            msg = "Replacement player not in pool";
            break;
          default:
            msg = detail.message || "Auto-swap failed";
        }
      } else {
        msg = detail || e?.message || "Auto-swap failed";
      }
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  // Sprint 60.133 — Onboarding Tour trigger. Auto-fires on first visit,
  // `restartTour` lets users re-open it later from the Reset ALL bar.
  const [restartTour, setRestartTour] = useState(false);

  return (
    <div data-testid="raw-optimizer" className="space-y-6 pb-20 md:pb-0">
      <RawOnboardingTour
        forceOpen={restartTour}
        onDone={() => setRestartTour(false)}
      />
      <CoverageConfirmDialog
        open={coverageGateOpen}
        coverage={coverage}
        onCancel={() => setCoverageGateOpen(false)}
        onConfirm={() => { coverageAckRef.current = true; setCoverageGateOpen(false); setTimeout(generate, 0); }}
      />
      {/* Sprint 60.132 — Mobile onboarding notice. Explains that Raw
          Optimizer is primarily a desktop tool but works mobile with
          horizontal scroll on wide tables. Dismissible per-session. */}
      <div
        data-testid="raw-mobile-notice"
        className="md:hidden bg-[#0e0e0e] border-l-2 border-[#39FF14] px-3 py-2 mono text-[10px] text-zinc-300 leading-relaxed"
      >
        <span className="text-[#39FF14] font-bold uppercase tracking-widest">Mobile Mode</span>
        <span className="text-zinc-500"> · </span>
        Full workflow works on mobile — wide tables scroll horizontally, key actions pinned to the bottom bar. For the best experience use a tablet or desktop on before lock.
      </div>
      {isShowdownSlate && (
        <div data-testid="raw-showdown-banner" className="border border-[#FF8A00]/60 bg-[#FF8A00]/10 px-4 py-2 mono text-[10px] text-[#FF8A00]">
          <span className="font-black uppercase tracking-widest">Showdown · Captain mode</span>
          <span className="text-zinc-300"> · 1 CPT (1.5× salary &amp; points) + 5 FLEX. Stack &amp; positional settings are replaced by game-script rules; locks, excludes, exposure caps, boosts, salary window and uniqueness still apply. Use the CPT / ✕C buttons on each row to pin or ban captains.</span>
        </div>
      )}
      {/* Sprint 60.77 — Slate context strip */}
      {slateMeta && (slateMeta.slate_name || slateMeta.game_count || pool.length > 0) && (
        <div
          data-testid="raw-slate-strip"
          className="flex flex-wrap items-center justify-between gap-3 border border-zinc-800 bg-[#0a0a0a] px-4 py-2"
        >
          <div className="flex flex-wrap items-center gap-3 mono text-[10px] uppercase tracking-widest">
            <span className="text-zinc-500">Slate ·</span>
            <span className="text-white font-bold">{slateMeta.slate_name || slateMeta.slate_type || "Sun Main"}</span>
            {slateMeta.game_count > 0 && (
              <span className="text-zinc-400">· {slateMeta.game_count} game{slateMeta.game_count === 1 ? "" : "s"}</span>
            )}
            {slateMeta.lock_time_local && (
              <span className="text-zinc-400">· Lock {slateMeta.lock_time_local}</span>
            )}
            <span className="text-[#39FF14]">· {pool.length} player{pool.length === 1 ? "" : "s"} in pool</span>
          </div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
            Raw namespace · isolated from Smart Optimizer
          </div>
        </div>
      )}

      <SundayBanner source="raw" onFixed={() => loadLineups()} />
      <details className="group" data-testid="raw-field-model-fold">
        <summary className="cursor-pointer list-none flex items-center justify-between gap-3 bg-[#121212] border border-zinc-800 hover:border-[#FFCC00]/50 px-4 py-2.5 mono text-[10px] uppercase tracking-widest text-zinc-300">
          <span><span className="text-[#FFCC00] font-bold">Field Model</span> · competitor projections for this slate — shared with the Smart Optimizer</span>
          <span className="text-zinc-500 group-open:rotate-180 transition-transform">▾</span>
        </summary>
        <div className="mt-2 space-y-4">
          <FieldModelPanel />
          <CompetitorPoolGrid />
        </div>
      </details>
      <RawRulesBar
        cfg={cfg} setCfg={setCfg} onGenerate={generate} busy={busy}
        disabled={pool.length === 0 || !dkGateOk}
        disabledReason={pool.length === 0 ? "Load a slate first" : `Set at least ${RAW_MIN_TOUCHES} player or rule touches before generating`}
        advancedOpen={advancedOpen} onToggleAdvanced={toggleAdvanced} advancedSeen={advancedSeen} showdown={isShowdownSlate}
      />
      {/* Sprint 62.8 — Three-zone layout: rules rail (left, now the Advanced drawer) · pool cockpit (center) · lineups deck (bottom) */}
      <div data-testid="raw-three-zone" data-advanced={advancedOpen ? "open" : "closed"}>
      {advancedOpen && (
      <aside className="raw-rail mb-6 border border-zinc-800 bg-[#0d0d0d] p-4" data-testid="raw-rules-rail">
      {/* --- Panel 1: Controls --- */}
      <section className="bg-[#121212] border border-zinc-800 p-4" data-testid="raw-controls-panel">
        {/* Sprint 60.83 — Named Config Presets bar */}
        <div className="flex items-center justify-between gap-3 mb-3" data-testid="raw-mode-header">
          <div className="min-w-0">
            <div className="text-[10px] mono uppercase tracking-[0.25em] text-zinc-500">Manual Optimizer · Advanced rules</div>
            <div className="heading text-base sm:text-lg font-black uppercase leading-tight tracking-tight text-white">Five sections. Plain English. Always in view.</div>
            <div className="text-[11px] text-zinc-500 leading-snug mt-1">The bar above covers most builds. Every section stays open for finer control — every rule here still applies at Generate.</div>
          </div>
        </div>
        <ConflictBanner cfg={cfg} groupRules={groupRules} lockedPlayers={lockedPlayers} onFix={applyConflictFix} />
        <div className="space-y-4" data-testid="adv-sections-grid">
        <section className="raw-adv-section border border-zinc-800 bg-[#0e0e0e]" data-testid="adv-section-salary">
          <div className="px-3 py-2 flex items-center justify-between gap-3 border-b border-zinc-900">
            <span className="heading text-[13px] font-black uppercase tracking-wide text-white">Salary & Value</span>
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 text-right">lineups · projection floor · salary window · team max</span>
          </div>
          <div className="p-3 raw-adv-body">
        <div className="grid grid-cols-2 gap-3">
          <Field label="# Lineups" testid="raw-num-lineups"
            value={cfg.num_lineups}
            onChange={(v) => setCfg({ ...cfg, num_lineups: Math.max(1, Math.min(150, +v || 0)) })}
          />
          <Field label="Min Projection" testid="raw-min-proj"
            value={cfg.min_projection}
            onChange={(v) => setCfg({ ...cfg, min_projection: Math.max(0, +v || 0) })}
          />
          <div>
            <Field label="Min Uniqueness" testid="raw-min-uniqueness"
              value={cfg.min_uniqueness}
              onChange={(v) => setCfg({ ...cfg, min_uniqueness: Math.max(0, Math.min(8, +v || 1)) })}
            />
            <div className="text-[10px] mono text-zinc-500 mt-1 leading-snug">
              <span className="text-[#39FF14]">= fewest different players between any two lineups.</span>
              <span className="block text-zinc-600 mt-0.5">
                1 = near-duplicates allowed · 3 = balanced (Milly-safe) · 5+ = max diversity
              </span>
            </div>
          </div>
        </div>
        {/* Sprint 60.77 — Team caps + Salary bands */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="block" data-testid="raw-max-per-team-row">
            <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">
              Max Players From One Team <span className="normal-case text-zinc-600">· per lineup (DK cap 8 · default 4)</span>
            </div>
            <input
              type="number"
              min="1"
              max="8"
              title="Most players from a single team inside one lineup — not a limit on how many teams you use"
              placeholder="4"
              value={cfg.max_per_team}
              onChange={(e) => setCfg({ ...cfg, max_per_team: e.target.value })}
              data-testid="raw-max-per-team"
              className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
            />
          </label>
          <label className="block" data-testid="raw-min-salary-row">
            <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">
              Min Salary <span className="normal-case text-zinc-600">· (default $49,500)</span>
            </div>
            <input
              type="number"
              min="0"
              max="50000"
              step="100"
              placeholder="49500"
              value={cfg.min_salary}
              onChange={(e) => setCfg({ ...cfg, min_salary: e.target.value })}
              data-testid="raw-min-salary"
              className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
            />
          </label>
          <label className="block" data-testid="raw-max-salary-row">
            <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">
              Max Salary <span className="normal-case text-zinc-600">· (DK cap $50,000)</span>
            </div>
            <input
              type="number"
              min="0"
              max="50000"
              step="100"
              placeholder="50000"
              value={cfg.max_salary}
              onChange={(e) => setCfg({ ...cfg, max_salary: e.target.value })}
              data-testid="raw-max-salary"
              className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
            />
          </label>
        </div>
          </div>
        </section>
        <section className="raw-adv-section border border-zinc-800 bg-[#0e0e0e]" data-testid="adv-section-exposure">
          <div className="px-3 py-2 flex items-center justify-between gap-3 border-b border-zinc-900">
            <span className="heading text-[13px] font-black uppercase tracking-wide text-white">Exposure & Teams</span>
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 text-right">global caps · team caps · multi-QB</span>
          </div>
          <div className="p-3 raw-adv-body">
        {/* Sprint 60.79 — Global exposure defaults */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="flex flex-col" data-testid="raw-global-min-exp-row">
            <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1 min-h-[2.4rem] flex items-end">
              <span>Global Min Exposure % <span className="normal-case text-zinc-600">· every unlisted player</span></span>
            </div>
            <input
              type="number"
              min="0"
              max="100"
              placeholder="—"
              value={cfg.global_min_exposure}
              onChange={(e) => setCfg({ ...cfg, global_min_exposure: e.target.value })}
              data-testid="raw-global-min-exposure"
              className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
            />
          </label>
          <label className="flex flex-col" data-testid="raw-global-max-exp-row">
            <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1 min-h-[2.4rem] flex items-end">
              <span>Global Max Exposure % <span className="normal-case text-zinc-600">· per-player caps win</span></span>
            </div>
            <input
              type="number"
              min="0"
              max="100"
              placeholder="—"
              value={cfg.global_max_exposure}
              onChange={(e) => setCfg({ ...cfg, global_max_exposure: e.target.value })}
              data-testid="raw-global-max-exposure"
              className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
            />
          </label>
        </div>
          {/* Sprint 60.127 — Team Exposure Caps */}
          <div className="border border-zinc-800 bg-[#0e0e0e] p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!cfg.team_exposures_enabled}
                onChange={(e) => setCfg({ ...cfg, team_exposures_enabled: e.target.checked })}
                data-testid="raw-team-exposures-toggle"
                className="w-4 h-4 accent-[#39FF14] cursor-pointer" />
              <div className="mono text-[11px] font-black uppercase tracking-widest text-white">
                Team Exposure Caps
              </div>
            </label>
            <div className="mono text-[9px] text-zinc-500 mt-1 ml-6 leading-relaxed">
              Cap max % of lineups that can include ANY player from each team.
              Post-gen filter drops the lowest-proj lineups exceeding the cap.
            </div>
            {cfg.team_exposures_enabled && (() => {
              const teams = Array.from(new Set(pool.map((p) => (p.team || "").toUpperCase()).filter(Boolean))).sort();
              if (teams.length === 0) {
                return <div className="mono text-[9px] text-zinc-600 mt-2 ml-6">Load a slate to see team list</div>;
              }
              return (
                <div className="mt-3 ml-6 grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                  {teams.map((team) => (
                    <div key={team} className="flex flex-col">
                      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">{team}</div>
                      <input type="number" min="0" max="100" step="5"
                        placeholder="—"
                        value={cfg.team_exposures?.[team] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value === "" ? "" : Math.max(0, Math.min(100, +e.target.value));
                          setCfg({
                            ...cfg,
                            team_exposures: { ...(cfg.team_exposures || {}), [team]: v === "" ? undefined : v },
                          });
                        }}
                        data-testid={`raw-team-exp-${team.toLowerCase()}`}
                        className="bg-black border border-zinc-800 text-white mono text-[11px] px-1 py-1 w-full text-center" />
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          {/* Sprint 60.127 — Multi-QB Portfolio Mode */}
          <div className="border border-zinc-800 bg-[#0e0e0e] p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!cfg.min_distinct_qbs_enabled}
                onChange={(e) => setCfg({ ...cfg, min_distinct_qbs_enabled: e.target.checked })}
                data-testid="raw-multi-qb-toggle"
                className="w-4 h-4 accent-[#39FF14] cursor-pointer" />
              <div className="mono text-[11px] font-black uppercase tracking-widest text-white">
                Multi-QB Portfolio
              </div>
            </label>
            <div className="mono text-[9px] text-zinc-500 mt-1 ml-6 leading-relaxed">
              Force at least N distinct QBs across your batch. Post-gen safeguard —
              drops lowest-proj lineups sharing the most-used QB until the target is met.
              Anti-blow-up for 150-max builders.
            </div>
            {cfg.min_distinct_qbs_enabled && (
              <div className="mt-2 ml-6 flex items-center gap-2">
                <input type="number" min="1" max="8" step="1" value={cfg.min_distinct_qbs}
                  onChange={(e) => setCfg({ ...cfg, min_distinct_qbs: Math.max(1, Math.min(8, +e.target.value || 2)) })}
                  data-testid="raw-multi-qb-count"
                  className="bg-black border border-zinc-800 text-white mono text-[11px] px-2 py-1 w-16" />
                <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                  distinct QBs (minimum)
                </span>
              </div>
            )}
          </div>
          </div>
        </section>
        <section className="raw-adv-section border border-zinc-800 bg-[#0e0e0e]" data-testid="adv-section-stacking">
          <div className="px-3 py-2 flex items-center justify-between gap-3 border-b border-zinc-900">
            <span className="heading text-[13px] font-black uppercase tracking-wide text-white">Stacking</span>
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 text-right">QB + pass-catcher mix · bring-back</span>
          </div>
          <div className="p-3 raw-adv-body">
          <StackBoundsCard cfg={cfg} setCfg={setCfg} />
          <div className="border border-zinc-800 bg-[#0e0e0e] p-3 mono text-[10px] text-zinc-400 leading-relaxed" data-testid="raw-stack-help">
            <div className="text-white font-black uppercase tracking-widest text-[11px] mb-2">How shapes are read</div>
            <div><span className="text-[#39FF14]">Naked QB</span> — no stack rule at all; the QB may ride alone.</div>
            <div className="mt-1"><span className="text-[#39FF14]">QB + 1</span> — at least one of the QB's own WR/TE.</div>
            <div className="mt-1"><span className="text-[#39FF14]">QB + 2</span> — at least two of the QB's own WR/TE.</div>
            <div className="mt-1"><span className="text-[#39FF14]">Bring-back</span> — adds one opposing WR/TE from the QB's game to every stacked lineup.</div>
            <div className="mt-2 text-zinc-500">Set min = max to pin a shape's share exactly. The QB Stack dropdown in the bar is a shortcut for these ranges.</div>
          </div>
          </div>
        </section>
        <section className="raw-adv-section border border-zinc-800 bg-[#0e0e0e]" data-testid="adv-section-field">
          <div className="px-3 py-2 flex items-center justify-between gap-3 border-b border-zinc-900">
            <span className="heading text-[13px] font-black uppercase tracking-wide text-white">Field & Diversity</span>
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 text-right">edge layers · variance · field size · dupes</span>
          </div>
          <div className="p-3 raw-adv-body">
          {/* Variance + Ceiling */}
          <details open className="border border-zinc-800 bg-[#0e0e0e] p-3 raw-fold" data-testid="raw-fold-shaping">
            <summary className="mono text-[11px] font-black uppercase tracking-widest text-white mb-2 cursor-pointer select-none list-none">
              Projection Shaping
            </summary>
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">Variance <span className="text-zinc-600 normal-case tracking-normal">· per-lineup jitter</span></div>
                <div className="mono text-[10px] text-[#39FF14] font-bold">{cfg.variance}%</div>
              </div>
              <input
                type="range"
                min="0"
                max="25"
                step="1"
                value={cfg.variance}
                onChange={(e) => setCfg({ ...cfg, variance: +e.target.value })}
                data-testid="raw-variance-slider"
                className="w-full accent-[#39FF14]"
              />
              <div className="mono text-[9px] text-zinc-500 mt-1">
                How far each lineup may wander from your projections: every player gets a fresh ±{cfg.variance}% draw before each lineup is solved, so lineup 7 differs from lineup 8. 0% = pure deterministic math. Not the same knob as Unique Seed below.
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={cfg.ceiling_mode}
                onChange={(e) => setCfg({ ...cfg, ceiling_mode: e.target.checked })}
                data-testid="raw-ceiling-mode"
                className="w-4 h-4 accent-[#39FF14] cursor-pointer"
              />
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-300">
                Ceiling Mode <span className="text-zinc-500 normal-case">· optimize for P85 upside instead of median</span>
              </div>
            </label>
          </details>
          {/* Sprint 60.142 — Portfolio Diversity panel (formerly "Automated
              Leverage Modifiers"). We stripped the Elite Value Booster
              + Suppress Public Chalk auto-adjusters — those silently
              muted user projections and contradicted the Manual Optimizer
              contract. Only user-controlled diversity toggles remain
              (Fingerprint Variance + Cluster Rotation). */}
          <details open className="border border-zinc-800 bg-[#0e0e0e] p-3 raw-fold" data-testid="raw-fold-diversity">
            <summary className="mono text-[11px] font-black uppercase tracking-widest text-white mb-2 cursor-pointer select-none list-none">
              Portfolio Diversity
            </summary>
            <div className="mono text-[9px] text-zinc-500 mb-3 leading-relaxed">
              Two edge layers that widen your portfolio without touching your projections. Turn either
              off for deterministic (pure-math) runs.
            </div>
            <div data-testid="raw-diversity-toggles">
              <label className="flex items-center gap-2 cursor-pointer mb-1.5">
                <input
                  type="checkbox"
                  checked={!!cfg.fingerprint_variance_enabled}
                  onChange={(e) => setCfg({ ...cfg, fingerprint_variance_enabled: e.target.checked })}
                  data-testid="raw-fingerprint-variance-toggle"
                  className="w-4 h-4 accent-[#39FF14] cursor-pointer"
                />
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-300">
                  Unique Seed <span className="text-zinc-500 lowercase">· per-user randomness — no two users get the same lineups</span>
                </div>
              </label>
              <div className="mono text-[9px] text-zinc-500 mt-0.5 ml-6 leading-relaxed">
                Decides WHOSE random numbers drive the build: your variance draws and peer swaps are seeded from your account (plus a rotating session salt), with a fixed 1.5% pre-solve nudge so identical inputs never yield identical lineups across subscribers. Off = shared seed, fully repeatable (backtests).
              </div>
              <label className="flex items-center gap-2 cursor-pointer mt-3 mb-1.5">
                <input
                  type="checkbox"
                  checked={!!cfg.cluster_rotation_enabled}
                  onChange={(e) => setCfg({ ...cfg, cluster_rotation_enabled: e.target.checked })}
                  data-testid="raw-cluster-rotation-toggle"
                  className="w-4 h-4 accent-[#39FF14] cursor-pointer"
                />
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-300">
                  Peer Swap <span className="text-zinc-500 lowercase">· swap in projection-equivalent players after the solve</span>
                </div>
              </label>
              {proMode && cfg.cluster_rotation_enabled && (
                <div className="ml-6 mt-1 grid grid-cols-3 gap-2" data-testid="raw-peer-swap-controls">
                  <label className="block">
                    <div className="mono text-[8px] uppercase tracking-widest text-zinc-500">± Proj %</div>
                    <input type="number" min={1} max={15} step={1} value={cfg.peer_swap_band_pct}
                      onChange={(e) => setCfg({ ...cfg, peer_swap_band_pct: e.target.value })}
                      onBlur={(e) => setCfg({ ...cfg, peer_swap_band_pct: Math.max(1, Math.min(15, +e.target.value || 5)) })}
                      data-testid="raw-peer-swap-band" className="w-full bg-black border border-zinc-800 px-2 py-1 mono text-[11px]" />
                  </label>
                  <label className="block">
                    <div className="mono text-[8px] uppercase tracking-widest text-zinc-500">± Salary $</div>
                    <input type="number" min={100} max={1500} step={100} value={cfg.peer_swap_salary_band}
                      onChange={(e) => setCfg({ ...cfg, peer_swap_salary_band: e.target.value })}
                      onBlur={(e) => setCfg({ ...cfg, peer_swap_salary_band: Math.max(100, Math.min(1500, +e.target.value || 400)) })}
                      data-testid="raw-peer-swap-salary" className="w-full bg-black border border-zinc-800 px-2 py-1 mono text-[11px]" />
                  </label>
                  <label className="block">
                    <div className="mono text-[8px] uppercase tracking-widest text-zinc-500">Swap chance %</div>
                    <input type="number" min={5} max={100} step={5} value={cfg.peer_swap_prob_text ?? Math.round((cfg.peer_swap_prob || 0.4) * 100)}
                      onChange={(e) => setCfg({ ...cfg, peer_swap_prob_text: e.target.value })}
                      onBlur={(e) => setCfg({ ...cfg, peer_swap_prob_text: undefined, peer_swap_prob: Math.max(0.05, Math.min(1, (+e.target.value || 40) / 100)) })}
                      data-testid="raw-peer-swap-prob" className="w-full bg-black border border-zinc-800 px-2 py-1 mono text-[11px]" />
                  </label>
                </div>
              )}
              <div className="mono text-[9px] text-zinc-500 mt-0.5 ml-6 leading-relaxed">
                After the solver builds, statistically equivalent players (same position, ±5% projection, ±$400 salary) get rotated to widen exposure without sacrificing EV. You can also shuffle any built batch on-demand with the <span className="text-[#39FF14]">🎲 Randomize</span> button on the Lineups tab.
              </div>
              <label className="flex items-center gap-2 cursor-pointer mt-3 mb-1.5">
                <input
                  type="checkbox"
                  checked={!!cfg.shadow_field_enabled}
                  onChange={(e) => setCfg({ ...cfg, shadow_field_enabled: e.target.checked })}
                  data-testid="raw-shadow-field-toggle"
                  className="w-4 h-4 accent-[#39FF14] cursor-pointer"
                />
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-300">
                  Shadow Field <span className="text-zinc-500 lowercase">· dodge the lineups everyone else's optimizer builds</span>
                </div>
              </label>
              <div className="mono text-[9px] text-zinc-500 mt-0.5 ml-6 leading-relaxed">
                Builds the 20 lineups a stock optimizer would produce from your slate (from the competitor consensus when you have uploaded it) and forces every lineup of yours to share at most 6 players with any of them.
              </div>
            </div>
          </details>
          {/* Sprint 62.6 — Field & Diversity: the layer Domination Station doesn't have */}
          <details open className="border border-[#39FF14]/30 bg-[#0e0e0e] p-3 raw-fold" data-testid="raw-field-section">
            <summary className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold mb-1 cursor-pointer select-none list-none">Field &amp; Diversity</summary>
            <div className="mono text-[9px] text-zinc-500 mb-3 leading-relaxed">
              Your projections and rules stay law. This layer only ranks the output against the field and stops your portfolio from being one lineup in 150 costumes.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <div className="mono text-[8px] uppercase tracking-widest text-zinc-500" title="Enables Field Win % and expected-copies ranking on every lineup, and the copies cap below.">Contest field size</div>
                <input type="number" min={10} step={1000} value={cfg.field_size ?? ""}
                  onChange={(e) => setCfg({ ...cfg, field_size: e.target.value === "" ? null : Math.max(10, +e.target.value || 0) })}
                  data-testid="raw-field-size" className="w-full bg-black border border-zinc-800 px-2 py-1 mono text-[11px]" />
              </label>
              <label className="block">
                <div className="mono text-[8px] uppercase tracking-widest text-zinc-500" title="0 = pure projection. 0.05 = moderate lean away from ownership. 0.10 = strong. Continuous — no cliffs like the booster/tax thresholds.">Ownership leverage λ</div>
                <input type="number" min={0} max={0.15} step={0.01} value={cfg.leverage_lambda}
                  onChange={(e) => setCfg({ ...cfg, leverage_lambda: Math.max(0, Math.min(0.15, +e.target.value || 0)) })}
                  data-testid="raw-leverage-lambda" className="w-full bg-black border border-zinc-800 px-2 py-1 mono text-[11px]" />
              </label>
              <label className="block">
                <div className="mono text-[8px] uppercase tracking-widest text-zinc-500" title="Max lineups that may share the same QB + RB + WR core. Stops a 150-max from being one lineup with 150 costumes.">Max sharing same core</div>
                <input type="number" min={2} max={9} value={cfg.core_max_shared ?? ""} placeholder="off"
                  onChange={(e) => setCfg({ ...cfg, core_max_shared: e.target.value === "" ? null : Math.max(2, Math.min(9, +e.target.value || 0)) })}
                  data-testid="raw-core-max-shared" className="w-full bg-black border border-zinc-800 px-2 py-1 mono text-[11px] placeholder:text-zinc-700" />
              </label>
              <label className="block">
                <div className="mono text-[8px] uppercase tracking-widest text-zinc-500" title="Reject lineups the field is expected to duplicate more than this many times (needs field size).">Max copies in field</div>
                <input type="number" min={0.1} max={50} step={0.5} value={cfg.max_expected_dupes ?? ""} placeholder="off"
                  onChange={(e) => setCfg({ ...cfg, max_expected_dupes: e.target.value === "" ? null : Math.max(0.1, +e.target.value || 0) })}
                  data-testid="raw-max-dupes" className="w-full bg-black border border-zinc-800 px-2 py-1 mono text-[11px] placeholder:text-zinc-700" />
              </label>
              <label className="flex items-end gap-2 cursor-pointer pb-1">
                <input type="checkbox" checked={!!cfg.own_relative_caps} onChange={(e) => setCfg({ ...cfg, own_relative_caps: e.target.checked })}
                  data-testid="raw-own-relative-caps" className="w-4 h-4 accent-[#39FF14] cursor-pointer" />
                <span className="mono text-[9px] uppercase tracking-widest text-zinc-300" title="cap × (12 ÷ own%)^0.5 — a 40%-owned player gets a tighter cap, a 6%-owned player a looser one. Needs λ > 0.">Ownership-relative caps</span>
              </label>
            </div>
          </details>
          </div>
        </section>
        <section className="raw-adv-section border border-zinc-800 bg-[#0e0e0e]" data-testid="adv-section-power">
          <div className="px-3 py-2 flex items-center justify-between gap-3 border-b border-zinc-900">
            <span className="heading text-[13px] font-black uppercase tracking-wide text-white">Power Tools</span>
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 text-right">presets · group rules · position floors</span>
          </div>
          <div className="p-3 raw-adv-body">
          <RulePresets cfg={cfg} setCfg={setCfg} />
        {proMode && (<div
          data-testid="raw-config-presets"
          className="flex items-center flex-wrap gap-2 mb-3 border border-[#39FF14]/25 bg-[#0a1a10] px-3 py-2"
        >
          <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] flex-shrink-0">
            My Configs ·
          </div>
          {namedPresets.length === 0 ? (
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
              No saved presets yet — build your setup and hit Save to name it.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
              {namedPresets.map((p) => (
                <div
                  key={p.name}
                  className="inline-flex items-center gap-1 border border-zinc-700 bg-black px-1"
                  data-testid={`raw-preset-${p.name.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}
                >
                  <button
                    type="button"
                    onClick={() => loadPreset(p.name)}
                    title={`Loaded ${new Date(p.ts).toLocaleString()}`}
                    className="mono text-[10px] uppercase tracking-widest text-zinc-300 hover:text-[#39FF14] px-1.5 py-0.5"
                  >
                    ↺ {p.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => deletePreset(p.name)}
                    className="mono text-[10px] text-zinc-600 hover:text-[#FF3B30] px-1 leading-none"
                    title="Delete preset"
                  >×</button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={savePreset}
            data-testid="raw-preset-save"
            className="ml-auto px-2.5 py-1 mono text-[10px] uppercase tracking-widest border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors font-bold"
          >
            + Save Current
          </button>
        </div>)}
          {/* Sprint 60.126 — Per-position projection floor */}
          <div className="border border-zinc-800 bg-[#0e0e0e] p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!cfg.position_proj_floors_enabled}
                onChange={(e) => setCfg({ ...cfg, position_proj_floors_enabled: e.target.checked })}
                data-testid="raw-position-floors-toggle"
                className="w-4 h-4 accent-[#39FF14] cursor-pointer" />
              <div className="mono text-[11px] font-black uppercase tracking-widest text-white">
                Per-Position Projection Floor
              </div>
            </label>
            <div className="mono text-[9px] text-zinc-500 mt-1 ml-6 leading-relaxed">
              Drop players below their position's projection floor from the ILP pool.
              Locked players are always kept. Set 0 per position to disable that position's floor.
            </div>
            {cfg.position_proj_floors_enabled && (
              <div className="mt-3 ml-6 grid grid-cols-5 gap-2">
                {["QB", "RB", "WR", "TE", "DST"].map((pos) => (
                  <div key={pos}>
                    <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">{pos}</div>
                    <input type="number" min="0" max="40" step="0.5"
                      value={cfg.position_proj_floors?.[pos] ?? 0}
                      onChange={(e) => setCfg({
                        ...cfg,
                        position_proj_floors: { ...(cfg.position_proj_floors || {}), [pos]: Math.max(0, Math.min(40, +e.target.value || 0)) },
                      })}
                      data-testid={`raw-pos-floor-${pos.toLowerCase()}`}
                      className="bg-black border border-zinc-800 text-white mono text-[11px] px-2 py-1 w-full" />
                  </div>
                ))}
              </div>
            )}
          </div>
        <div className="raw-adv-full"><GroupRulesPanel
          pool={pool}
          rules={groupRules}
          onChange={setGroupRules}
        /></div>
          </div>
        </section>
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button
            onClick={generate}
            disabled={busy || pool.length === 0 || !dkGateOk}
            data-testid="raw-generate-btn"
            className="bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-30 text-black font-black uppercase tracking-widest text-[11px] px-6 py-2.5"
          >
            {busy ? "Optimizing…" : "Generate Lineups"}
          </button>
          {/* Sprint 60.127 — Live Leverage Preview Chip */}
          {(leverageBoostedIds.size > 0 || chalkTaxedIds.size > 0 || (cfg.position_proj_floors_enabled && Object.values(cfg.position_proj_floors || {}).some((v) => +v > 0))) && (
            <div data-testid="raw-leverage-preview" className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-zinc-800 bg-[#0e0e0e] flex items-center gap-3">
              {leverageBoostedIds.size > 0 && (
                <span title={`${leverageBoostedIds.size} players qualify for the Elite Value Booster (+${cfg.leverage_booster_pct}% proj)`}>
                  <span className="text-[#39FF14]">↑ {leverageBoostedIds.size}</span> <span className="text-zinc-500">boosted</span>
                </span>
              )}
              {chalkTaxedIds.size > 0 && (
                <span title={`${chalkTaxedIds.size} players qualify for the Chalk Tax (-${cfg.chalk_tax_pct}% proj)`}>
                  <span className="text-red-400">↓ {chalkTaxedIds.size}</span> <span className="text-zinc-500">taxed</span>
                </span>
              )}
              {cfg.position_proj_floors_enabled && (
                <span title="Sub-floor players dropped from ILP pool (per-position projection floor active)">
                  <span className="text-orange-400">⌐ {Object.entries(cfg.position_proj_floors || {}).filter(([, v]) => +v > 0).length}</span> <span className="text-zinc-500">pos-floors</span>
                </span>
              )}
            </div>
          )}
          {busy && progress && (
            <button
              type="button"
              onClick={cancelSolve}
              disabled={progress.stop_requested}
              data-testid="raw-cancel-btn"
              className="border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black disabled:opacity-40 font-black uppercase tracking-widest text-[10px] px-4 py-2 transition-colors"
            >
              {progress.stop_requested ? "Cancelling…" : "Cancel"}
            </button>
          )}
          {/* Sprint 60.130 — Diversity Preview Chip. Reads the two toggles
              in the Automated Leverage Modifiers panel and shows their
              state at a glance so users don't have to scroll to check
              whether their build will be genuinely per-user unique.
              Sprint 60.141 — Replaced generic 🎲 dice emoji with two
              feature-specific icons: Fingerprint for per-user variance,
              spinning ArrowsClockwise for cluster rotation. */}
          <div
            data-testid="raw-diversity-chip"
            title="Unique Seed = per-user pre-ILP jitter (seed derived from your account). Peer Swap = post-ILP swap of stat-equivalent players. Both are configurable in the Automated Leverage Modifiers panel."
            className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-zinc-800 bg-[#0e0e0e] flex items-center gap-2"
          >
            <Fingerprint
              size={14}
              weight="duotone"
              className={cfg.fingerprint_variance_enabled ? "text-[#39FF14]" : "text-zinc-600"}
            />
            <span className={cfg.fingerprint_variance_enabled ? "text-[#39FF14]" : "text-zinc-600 line-through"}>
              Unique seed {cfg.fingerprint_variance_enabled ? "ON" : "OFF"}
            </span>
            <span className="text-zinc-700">·</span>
            <ArrowsClockwise
              size={13}
              weight="bold"
              className={
                cfg.cluster_rotation_enabled
                  ? "text-[#39FF14] animate-spin"
                  : "text-zinc-600"
              }
              style={cfg.cluster_rotation_enabled ? { animationDuration: "3s" } : undefined}
            />
            <span className={cfg.cluster_rotation_enabled ? "text-[#39FF14]" : "text-zinc-600 line-through"}>
              Peer swap {cfg.cluster_rotation_enabled ? "ON" : "OFF"}
            </span>
          </div>
          <div className="text-[10px] mono text-zinc-500">
            Pool: {pool.length} · Locks: {locks.size} · Excludes: {excludes.size} · Rules: {groupRules.length}
          </div>
        </div>
        <RawBuildFailPanel
          diag={buildDiag}
          rules={groupRules}
          onRemoveRule={(k) => { setGroupRules(groupRules.filter((_, i) => i !== k)); setBuildDiag(null); }}
          onDismiss={() => setBuildDiag(null)}
        />
        {/* Sprint 60.92 — DK compliance gate banner. Renders whenever the
            user has not yet accumulated ≥2 tuning touches. Explains what
            counts as a touch so first-time users don't guess. Fades away
            (into a compact confirmation strip) once the gate is cleared. */}
        <div
          data-testid="raw-dk-gate"
          className={`mt-3 border px-3 py-2 flex items-start gap-3 ${
            dkGateOk
              ? "border-[#39FF14]/40 bg-[#0a1a10]"
              : "border-[#FFCC00]/50 bg-[#1a1608]"
          }`}
        >
          <div className={`mono text-[10px] uppercase tracking-widest font-black flex-shrink-0 mt-0.5 ${
            dkGateOk ? "text-[#39FF14]" : "text-[#FFCC00]"
          }`}>
            {dkGateOk ? "DK Gate · Cleared" : "DK Gate · Locked"}
          </div>
          <div className="flex-1 min-w-0 text-[11px] leading-relaxed">
            {dkGateOk ? (
              <span className="text-zinc-300">
                <span className="text-[#39FF14] font-bold">{rawTouches}</span> tuning touch{rawTouches === 1 ? "" : "es"} recorded — your portfolio fingerprint is unique. Ship it.
              </span>
            ) : (
              <span className="text-zinc-300">
                Make <span className="text-[#FFCC00] font-bold">{RAW_MIN_TOUCHES - rawTouches}</span> more tuning touch{RAW_MIN_TOUCHES - rawTouches === 1 ? "" : "es"} — DraftKings ToS bans one-click builds from stock projections. A lock, exclude, exposure cap, boost, stack mix, salary band, or any config nudge each counts.
              </span>
            )}
          </div>
          <div className={`mono text-[10px] font-black flex-shrink-0 ml-2 pl-3 border-l ${
            dkGateOk ? "text-[#39FF14] border-[#39FF14]/30" : "text-[#FFCC00] border-[#FFCC00]/40"
          }`}>
            {rawTouches} / {RAW_MIN_TOUCHES}
          </div>
        </div>
        {/* Sprint 60.82 — Progress bar (visible only while solving) */}
        {busy && progress && (
          <div
            data-testid="raw-progress"
            className="mt-3 border border-zinc-800 bg-[#0a0a0a] p-3"
          >
            <div className="flex items-center justify-between mb-2 text-[10px] mono uppercase tracking-widest">
              <div className="text-zinc-400">
                {progress.phase_label || progress.phase}
              </div>
              <div className="text-[#39FF14] font-bold">
                {progress.current} / {progress.total || cfg.num_lineups}
              </div>
            </div>
            <div className="h-2 bg-zinc-900 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-[#39FF14] transition-all duration-300"
                style={{ width: `${progress.total ? Math.min(100, (progress.current / progress.total) * 100) : 5}%` }}
              />
              {progress.total === 0 && (
                <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-[#39FF14] to-transparent mm-scan-bar" />
              )}
            </div>
          </div>
        )}
      </section>
      </aside>
      )}
      <div className="min-w-0 space-y-6 mt-6 lg:mt-0" data-testid="raw-pool-cockpit">

      {/* Sprint 60.95 — Slate-wide Game Lock board above the player
          pool. Locks/unlocks games; unlocked games' players are removed
          from the ILP pool at generate-time via `excluded_games`. Same
          UI as Slate Intel — click-to-toggle state syncs via
          localStorage so the two tabs stay in lockstep. */}
      <GameCardGrid />

      {/* --- Panel 2: Player Pool --- */}
      <section className="bg-[#121212] border border-zinc-800 p-4" data-testid="raw-pool-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="heading text-lg font-black uppercase">Player Pool</div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1">
              {["ALL", "QB", "RB", "WR", "TE", "DST"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPosFilter(p)}
                  data-testid={`raw-pos-${p.toLowerCase()}`}
                  className={`px-2.5 py-1 text-[10px] uppercase border transition-colors mono ${
                    posFilter === p
                      ? "bg-[#39FF14] text-black border-[#39FF14] font-bold"
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search name / team"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="raw-pool-search"
              className="bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-1.5 text-sm md:w-72"
            />
            {/* Sprint 60.126 — Pool sort mode */}
            <select
              value={cfg.pool_sort_mode || "proj"}
              onChange={(e) => setCfg({ ...cfg, pool_sort_mode: e.target.value })}
              data-testid="raw-pool-sort-mode"
              title="Sort by projection (default) · leverage (proj_rank + own_rank composite, low = best leverage) · ownership desc · salary desc"
              className="bg-black border border-zinc-800 text-white text-xs mono uppercase tracking-widest px-2 py-1.5 focus:border-[#39FF14] outline-none"
            >
              <option value="proj">Sort · Proj</option>
              <option value="leverage">Sort · Leverage</option>
              <option value="own">Sort · Own</option>
              <option value="salary">Sort · Salary</option>
            </select>
          </div>
        </div>
        {/* Sprint 60.79 — Bulk actions toolbar */}
        <div
          className="flex items-center flex-wrap gap-2 mb-3 border-t border-b border-zinc-900 py-2"
          data-testid="raw-bulk-actions"
        >
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Bulk on visible ({filteredPool.length}) ·</div>
          <button
            type="button"
            onClick={() => {
              setLocks((prev) => { const n = new Set(prev); filteredPool.forEach((p) => n.add(p.dk_id)); return n; });
              toast.success(`Locked ${filteredPool.length} player${filteredPool.length === 1 ? "" : "s"}`);
            }}
            data-testid="raw-bulk-lock"
            className="px-2 py-1 mono text-[10px] uppercase tracking-widest border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors"
          >Lock All</button>
          <button
            type="button"
            onClick={() => {
              setExcludes((prev) => { const n = new Set(prev); filteredPool.forEach((p) => n.add(p.dk_id)); return n; });
              toast.success(`Excluded ${filteredPool.length} player${filteredPool.length === 1 ? "" : "s"}`);
            }}
            data-testid="raw-bulk-exclude"
            className="px-2 py-1 mono text-[10px] uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black transition-colors"
          >Exclude All</button>
          <button
            type="button"
            onClick={() => {
              setLocks((prev) => { const n = new Set(prev); filteredPool.forEach((p) => n.delete(p.dk_id)); return n; });
              setExcludes((prev) => { const n = new Set(prev); filteredPool.forEach((p) => n.delete(p.dk_id)); return n; });
              toast.info("Cleared locks + excludes on visible");
            }}
            data-testid="raw-bulk-clear-lockexc"
            className="px-2 py-1 mono text-[10px] uppercase tracking-widest border border-zinc-700 text-zinc-400 hover:border-zinc-500"
          >Clear L/X</button>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-700">|</div>
          <button
            type="button"
            onClick={() => {
              setMinExposures((prev) => { const n = { ...prev }; filteredPool.forEach((p) => delete n[p.dk_id]); return n; });
              setMaxExposures((prev) => { const n = { ...prev }; filteredPool.forEach((p) => delete n[p.dk_id]); return n; });
              setBoosts((prev) => { const n = { ...prev }; filteredPool.forEach((p) => delete n[p.dk_id]); return n; });
              toast.info("Reset exposures + boosts on visible");
            }}
            data-testid="raw-bulk-reset-exp"
            className="px-2 py-1 mono text-[10px] uppercase tracking-widest border border-zinc-700 text-zinc-400 hover:border-zinc-500"
          >Reset Exp/Boost</button>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-700">|</div>
          {/* Sprint 60.128 — Boost mode toggle. Switch between % and pts
              interpretation. Toggling clears the boost map so a "+15"
              entered as % isn't misread as "+15 pts" on the next build. */}
          <div className="flex items-center gap-1" data-testid="raw-boost-mode-toggle">
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">Boost Mode</span>
            {[
              { k: "pct", label: "%" },
              { k: "abs", label: "PTS" },
            ].map((m) => {
              const on = (cfg.boost_mode || "pct") === m.k;
              return (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => {
                    if (on) return;
                    if (Object.keys(boosts).length > 0) {
                      const ok = confirm(
                        `Switch Boost Mode to ${m.label}? Existing per-player boosts will be cleared to avoid mis-interpreting % as points (or vice versa).`
                      );
                      if (!ok) return;
                      setBoosts({});
                    }
                    setCfg({ ...cfg, boost_mode: m.k });
                  }}
                  data-testid={`raw-boost-mode-${m.k}`}
                  className={`px-2 py-0.5 mono text-[10px] uppercase tracking-widest border ${
                    on ? "bg-[#39FF14] text-black border-[#39FF14] font-bold" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-700">|</div>
          {/* Sprint 60.130 — Named Override Sets (projection + ownership only) */}
          <div className="flex items-center gap-1" data-testid="raw-override-sets">
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">Override Sets</span>
            <button
              type="button"
              onClick={saveOverrideSet}
              data-testid="raw-override-set-save"
              title="Save the current Proj Override + Own Override values as a reusable named set"
              className="px-2 py-0.5 mono text-[10px] uppercase tracking-widest border border-zinc-800 text-zinc-300 hover:border-[#39FF14] hover:text-[#39FF14]"
            >
              Save
            </button>
            {overrideSets.length > 0 && (
              <select
                onChange={(e) => {
                  const name = e.target.value;
                  if (!name) return;
                  loadOverrideSet(name);
                  e.target.value = "";
                }}
                data-testid="raw-override-set-load"
                title="Load a previously saved override set (merges onto current)"
                defaultValue=""
                className="bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-0.5 text-[10px] mono uppercase tracking-widest text-zinc-300"
              >
                <option value="">Load…</option>
                {overrideSets.map((s) => (
                  <option key={s.name} value={s.name}>{s.name}</option>
                ))}
              </select>
            )}
            {overrideSets.length > 0 && (
              <select
                onChange={(e) => {
                  const name = e.target.value;
                  if (!name) return;
                  deleteOverrideSet(name);
                  e.target.value = "";
                }}
                data-testid="raw-override-set-delete"
                title="Delete a saved override set"
                defaultValue=""
                className="bg-black border border-zinc-800 focus:border-[#FF3B30] outline-none px-2 py-0.5 text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30]"
              >
                <option value="">×</option>
                {overrideSets.map((s) => (
                  <option key={s.name} value={s.name}>{s.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-700">|</div>
          {/* Sprint 60.92 — Position-aware bulk exposure setters. When a
              position filter is active (e.g. DST), the min/max applies
              only to that position group — perfect for "cap all defenses
              at 30%" or "force every WR to at least 5% exposure". Uses
              filteredPool so pos filter + search both compose. */}
          <div
            className="flex items-center gap-1"
            data-testid="raw-bulk-set-min-exp-row"
          >
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">
              Set Min% {posFilter !== "ALL" ? `(${posFilter})` : "(visible)"}
            </span>
            <input
              type="number"
              min="0"
              max="100"
              placeholder="—"
              value={bulkMinExp}
              onChange={(e) => setBulkMinExp(e.target.value)}
              data-testid="raw-bulk-min-exp-input"
              className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-1 py-0.5 text-center text-[11px] mono"
            />
            <button
              type="button"
              onClick={() => {
                if (bulkMinExp === "" || bulkMinExp === null) {
                  toast.warning("Enter a Min% value first");
                  return;
                }
                const v = Math.max(0, Math.min(100, parseInt(bulkMinExp, 10) || 0));
                setMinExposures((prev) => {
                  const n = { ...prev };
                  filteredPool.forEach((p) => { n[p.dk_id] = v; });
                  return n;
                });
                toast.success(`Min ${v}% applied to ${filteredPool.length} ${posFilter === "ALL" ? "player" : posFilter}${filteredPool.length === 1 ? "" : "s"}`, { id: "raw-bulk-min-exp" });
              }}
              data-testid="raw-bulk-min-exp-apply"
              className="px-1.5 py-0.5 mono text-[10px] uppercase tracking-widest border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors"
            >Apply</button>
          </div>
          <div
            className="flex items-center gap-1"
            data-testid="raw-bulk-set-max-exp-row"
          >
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">
              Set Max% {posFilter !== "ALL" ? `(${posFilter})` : "(visible)"}
            </span>
            <input
              type="number"
              min="0"
              max="100"
              placeholder="—"
              value={bulkMaxExp}
              onChange={(e) => setBulkMaxExp(e.target.value)}
              data-testid="raw-bulk-max-exp-input"
              className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-1 py-0.5 text-center text-[11px] mono"
            />
            <button
              type="button"
              onClick={() => {
                if (bulkMaxExp === "" || bulkMaxExp === null) {
                  toast.warning("Enter a Max% value first");
                  return;
                }
                const v = Math.max(0, Math.min(100, parseInt(bulkMaxExp, 10) || 0));
                setMaxExposures((prev) => {
                  const n = { ...prev };
                  filteredPool.forEach((p) => { n[p.dk_id] = v; });
                  return n;
                });
                toast.success(`Max ${v}% applied to ${filteredPool.length} ${posFilter === "ALL" ? "player" : posFilter}${filteredPool.length === 1 ? "" : "s"}`, { id: "raw-bulk-max-exp" });
              }}
              data-testid="raw-bulk-max-exp-apply"
              className="px-1.5 py-0.5 mono text-[10px] uppercase tracking-widest border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors"
            >Apply</button>
          </div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-700">|</div>
          {/* Min Proj purge — exclude every visible player under the
              projection threshold. Locked players are skipped. Removed
              players surface in the Removed tab where they can be restored. */}
          <div
            className="flex items-center gap-1"
            data-testid="raw-bulk-set-min-proj-row"
          >
            <span
              className="mono text-[9px] uppercase tracking-widest text-zinc-500 whitespace-nowrap"
              title="Remove every visible player whose projection is below this value. Locked players are skipped. Real starters at $4k+ salary with a 0.0 projection (SGO data gap) are also skipped — SGO sometimes posts late for Thursday/Q-status players. Removed players land in the Removed tab and can be restored."
            >
              Min Proj {posFilter !== "ALL" ? `(${posFilter})` : "(visible)"}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              placeholder="—"
              value={bulkMinProj}
              onChange={(e) => setBulkMinProj(e.target.value)}
              data-testid="raw-bulk-min-proj-input"
              className="w-14 bg-black border border-zinc-800 focus:border-[#FF3B30] outline-none px-1 py-0.5 text-center text-[11px] mono"
            />
            <button
              type="button"
              onClick={() => {
                if (bulkMinProj === "" || bulkMinProj === null) {
                  toast.warning("Enter a Min Proj value first");
                  return;
                }
                const threshold = Number(bulkMinProj);
                if (!Number.isFinite(threshold) || threshold < 0) {
                  toast.error("Min Proj must be a non-negative number");
                  return;
                }
                // Compute the target dk_ids SYNCHRONOUSLY first — React 18
                // batches setExcludes so any counter mutated inside the
                // updater callback isn't readable in the same tick. Doing
                // the math up-front means the toast can key off the real
                // count instead of the pre-commit 0. StrictMode also
                // invokes the updater twice; keeping side effects out of
                // it prevents double-counting.
                // Sprint 60.141 — Starter safeguard. Some real NFL
                // starters get 0.0 projections from SGO (data gaps,
                // Thursday-only feed lag, listed-Q status), and a
                // naive "purge under X" sweeps them along with real
                // dart-throw benchers. We treat "salary >= $4,000
                // AND proj == 0" as a data gap signal (bench players
                // are almost always <$4k on DK NFL) and SKIP them
                // from the purge. User sees the count in the toast
                // so they can individually exclude if they really
                // want them gone.
                const STARTER_SALARY_FLOOR = 4000;
                const toRemove = [];
                let skippedLocked = 0;
                let skippedStarters = 0;
                filteredPool.forEach((p) => {
                  if (locks.has(p.dk_id)) { skippedLocked += 1; return; }
                  const proj = Number(p.our_proj || p.projection || 0);
                  if (proj >= threshold) return;
                  if (proj === 0 && Number(p.salary || 0) >= STARTER_SALARY_FLOOR) {
                    skippedStarters += 1;
                    return;
                  }
                  toRemove.push(p.dk_id);
                });
                if (toRemove.length === 0) {
                  const detail = skippedStarters > 0
                    ? `${skippedStarters} likely-starter${skippedStarters === 1 ? "" : "s"} @ $4k+ w/ 0 proj skipped — SGO probably hasn't posted yet.`
                    : `No visible players below ${threshold} proj.`;
                  toast.info(detail, { id: "raw-bulk-min-proj" });
                  return;
                }
                setExcludes((prev) => {
                  const n = new Set(prev);
                  toRemove.forEach((id) => n.add(id));
                  return n;
                });
                const skipTail = [
                  skippedLocked > 0 ? `${skippedLocked} locked` : null,
                  skippedStarters > 0 ? `${skippedStarters} starter${skippedStarters === 1 ? "" : "s"} @ $4k+ (0 proj)` : null,
                ].filter(Boolean).join(" · ");
                toast.success(
                  `Removed ${toRemove.length} player${toRemove.length === 1 ? "" : "s"} below ${threshold} proj${skipTail ? ` · ${skipTail} skipped` : ""}`,
                  {
                    id: "raw-bulk-min-proj",
                    description: skippedStarters > 0
                      ? `SGO data gap probably explains why those $4k+ names show 0 — restoring them from the Removed tab if you want them back.`
                      : undefined,
                  },
                );
                setBulkMinProj("");
              }}
              data-testid="raw-bulk-min-proj-apply"
              className="px-1.5 py-0.5 mono text-[10px] uppercase tracking-widest border border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black transition-colors"
            >Purge</button>
          </div>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-700">|</div>
          {/* Sprint 60.141 — Refresh Projections. Re-derives pool
              server-side to pick up latest admin overrides + latest
              SGO cache. No SGO wire hit. Does NOT reset admin
              overrides — those only change on a new admin push. */}
          <button
            type="button"
            data-testid="raw-refresh-projections"
            onClick={async () => {
              try {
                const r = await api.refreshPoolProjections();
                const overr = r.proj_overrides_applied || 0;
                const own = r.own_overrides_applied || 0;
                const tail = (overr || own)
                  ? ` · Admin overrides live: ${overr} proj / ${own} own`
                  : "";
                toast.success(`Refreshed ${r.pool_size} players${tail}`, { duration: 6000 });
                window.dispatchEvent(new CustomEvent("wp:pool-refreshed"));
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Refresh failed");
              }
            }}
            title="Pull the latest projections + ownership from the WarPaint model. If the admin pushed a hard override in the last few minutes, this button ensures you're building against it. Doesn't reset admin overrides — only I (admin) can do that."
            className="px-2 py-1 mono text-[10px] uppercase tracking-widest border border-[#00B4FF]/60 text-[#00B4FF] hover:bg-[#00B4FF] hover:text-black transition-colors font-bold"
          >⟳ Refresh Projections</button>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-700">|</div>
          <button
            type="button"
            onClick={() => {
              if (!confirm(`Reset EVERYTHING? Clears all locks, excludes, exposures, boosts, projection & ownership overrides, and group rules.`)) return;
              setLocks(new Set()); setExcludes(new Set());
              setMinExposures({}); setMaxExposures({}); setBoosts({});
              setProjectionOverrides({}); setOwnershipOverrides({});
              setGroupRules([]);
              toast.success("Pool state reset");
            }}
            data-testid="raw-bulk-reset-all"
            className="px-2 py-1 mono text-[10px] uppercase tracking-widest border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black transition-colors font-bold"
          >Reset ALL</button>
          <button
            type="button"
            onClick={() => setRestartTour(true)}
            data-testid="raw-restart-tour"
            title="Replay the 8-step Manual Optimizer walkthrough"
            className="px-2 py-1 mono text-[10px] uppercase tracking-widest border border-zinc-700 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14] transition-colors ml-auto"
          >? Tour</button>
        </div>
        {/* Sprint 60.131 — Ownership Curve Viz toggle */}
        {pool.length > 0 && (
          <div className="pt-3 pb-1 flex items-center gap-2 flex-wrap" data-testid="raw-own-curve-strip">
            <button
              type="button"
              onClick={() => setCfg({ ...cfg, show_ownership_curve: !cfg.show_ownership_curve })}
              data-testid="raw-toggle-own-curve"
              className={`inline-flex items-center gap-1.5 px-3 py-1 mono text-[10px] uppercase tracking-widest border transition-colors ${
                cfg.show_ownership_curve
                  ? "bg-[#39FF14] text-black border-[#39FF14] font-bold"
                  : "border-zinc-700 text-zinc-400 hover:border-[#39FF14] hover:text-white"
              }`}
              title="Toggle a proj-vs-own scatter with Sharp/Chalky/Trap/Value quadrants"
            >
              {cfg.show_ownership_curve ? "◉" : "◎"} Ownership Curve
            </button>
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
              Sharp = high proj + low own · Trap = low proj + high own
            </span>
          </div>
        )}
        {cfg.show_ownership_curve && pool.length > 0 && (
          <div className="pt-2 pb-2">
            <OwnershipCurveViz
              pool={pool}
              highlightIds={new Set([...(locks || []), ...Object.keys(projectionOverrides || {}), ...Object.keys(ownershipOverrides || {})])}
              onPlayerClick={(p) => {
                setSearch(p.name || "");
                toast.info(`Focused · ${p.name}`);
              }}
            />
          </div>
        )}
        {/* Sprint 60.117 — Active / Removed tab strip (regression fix).
            Was accidentally dropped during an earlier move. Bigger,
            border-2 styling so users notice it. Restore All appears
            only when there are excluded players to restore. */}
        <div
          className="pt-3 pb-2 flex items-center gap-2 flex-wrap"
          data-testid="raw-pool-tab-strip"
        >
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 shrink-0">
            View ·
          </span>
          <button
            type="button"
            onClick={() => setShowRemoved(false)}
            data-testid="raw-pool-tab-active"
            aria-pressed={!showRemoved}
            title="The active player pool — every player available to the optimizer."
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[11px] uppercase tracking-widest border-2 transition-colors ${
              !showRemoved
                ? "bg-[#39FF14] text-black border-[#39FF14] font-bold shadow-[0_0_10px_-3px_rgba(57,255,20,0.55)]"
                : "bg-black border-zinc-800 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14]"
            }`}
          >
            Active Pool
          </button>
          <button
            type="button"
            onClick={() => setShowRemoved(true)}
            data-testid="raw-pool-tab-removed"
            aria-pressed={showRemoved}
            title="Players you've excluded manually (X button) or via the Min Proj purge. Click here to review and restore any of them."
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[11px] uppercase tracking-widest border-2 transition-colors ${
              showRemoved
                ? "bg-[#FF3B30] text-white border-[#FF3B30] font-bold shadow-[0_0_10px_-3px_rgba(255,59,48,0.55)]"
                : "bg-black border-zinc-800 text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"
            }`}
          >
            Removed
            <span
              data-testid="raw-pool-tab-removed-badge"
              className={`ml-1 px-1.5 py-0 border font-bold text-[10px] ${
                showRemoved
                  ? "bg-white text-[#FF3B30] border-white"
                  : removedCount > 0
                    ? "bg-[#FF3B30]/25 text-[#FF3B30] border-[#FF3B30]/60"
                    : "bg-black text-zinc-600 border-zinc-800"
              }`}
            >
              {removedCount}
            </span>
          </button>
          {showRemoved && removedCount > 0 && (
            <button
              type="button"
              onClick={() => {
                const n = removedCount;
                setExcludes(new Set());
                setRestores((prev) => {
                  const next = new Set(prev);
                  for (const p of pool) if (isOut(p) && !locks.has(p.dk_id)) next.add(String(p.dk_id));
                  return next;
                });
                // Sprint 60.117 — flip back to Active view after Restore
                // All so users land on the pool they now care about (not
                // an empty Removed tab with a "0" badge).
                setShowRemoved(false);
                toast.success(`Restored ${n} player${n === 1 ? "" : "s"} to the active pool.`);
              }}
              data-testid="raw-pool-restore-all"
              title="Clear the excludes Set — every removed player becomes active again. Locks, exposure caps, and boosts are preserved."
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 mono text-[11px] uppercase tracking-widest border-2 border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors font-bold"
            >
              ↺ Restore All ({removedCount})
            </button>
          )}
          {showRemoved && (
            <span className="ml-2 mono text-[10px] text-zinc-500">
              Reviewing removed players — click the X icon on any row to restore it individually.
            </span>
          )}
          <span
            className="ml-auto hidden md:inline-flex mono text-[9px] uppercase tracking-widest text-zinc-500"
            data-testid="raw-pool-keyboard-hint"
            title="Click a row (or press ↓) to move the cursor, then: L = lock · X = exclude · F = like · Esc = clear"
          >
            ⌨ ↑↓ · L lock · X exclude · F like
          </span>
        </div>
        {showRemoved && (() => {
          const counts = { all: 0, injury: 0, ps: 0, fa: 0, bench: 0, mine: 0 };
          for (const p of pool) {
            if (locks.has(p.dk_id)) continue;
            const r = removedReasonOf(p, excludes);
            if (r) { counts[r] += 1; counts.all += 1; }
          }
          return (
            <div className="flex items-center gap-1.5 flex-wrap mb-2" data-testid="raw-removed-reason-filter">
              <span className="mono text-[9px] uppercase tracking-widest text-zinc-600 mr-1">Why removed</span>
              {REMOVED_REASONS.map((r) => (
                <button key={r.key} type="button" onClick={() => setRemovedReason(r.key)} aria-pressed={removedReason === r.key}
                        data-testid={`raw-removed-reason-${r.key}`}
                        className={`px-2 py-0.5 border mono text-[9px] uppercase tracking-widest transition-colors ${removedReason === r.key ? "bg-[#39FF14] text-black border-[#39FF14] font-bold" : "border-zinc-800 text-zinc-500 hover:text-zinc-200"}`}>
                  {r.label} <span className={removedReason === r.key ? "text-black/70" : "text-zinc-600"}>{counts[r.key]}</span>
                </button>
              ))}
            </div>
          );
        })()}
        <div className="flex items-center gap-3 flex-wrap mb-2 mono text-[9px] uppercase tracking-widest" data-testid="raw-field-model-strip">
          <FieldCoverageBadge coverage={coverage} compact testId="raw-field-model-badge" />
          <button
            type="button"
            onClick={toggleFieldCols}
            data-testid="raw-toggle-field-cols"
            aria-pressed={showFieldCols}
            title="Show the competitor consensus projection (median) and max ownership next to ours. Read-only reference — our numbers drive the solve."
            className={`px-2 py-0.5 border transition-colors ${showFieldCols ? "bg-[#FFCC00] text-black border-[#FFCC00] font-bold" : "border-zinc-800 text-zinc-500 hover:border-[#FFCC00] hover:text-[#FFCC00]"}`}
          >
            Fld cols {showFieldCols ? "on" : "off"}
          </button>
        </div>
        <div
          ref={rawTableRef}
          tabIndex={0}
          onKeyDown={onRawPoolKeyDown}
          data-testid="raw-pool-table-container"
          className="overflow-x-auto max-h-[520px] outline-none focus:ring-1 focus:ring-inset focus:ring-[#39FF14]/40"
        >
          <table className="raw-pool-table w-full min-w-[1100px] text-[11px] mono">
            <thead className="raw-pool-thead sticky top-0 bg-[#121212] border-b border-zinc-800 z-10">
              <tr className="text-zinc-500 text-left">
                <th className="p-2 select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("name")}
                    data-testid="raw-pool-sort-name"
                    title="Click to sort by name (asc / desc / clear)">
                  Name{poolTableSort.key === "name"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                <th className="p-2">Status</th>
                <th className="p-2 select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("pos")}
                    data-testid="raw-pool-sort-pos"
                    title="Click to sort by position (asc / desc / clear)">
                  Pos{poolTableSort.key === "pos"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                <th className="p-2 select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("team")}
                    data-testid="raw-pool-sort-team"
                    title="Click to sort by team (asc / desc / clear)">
                  Team{poolTableSort.key === "team"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                <th className="p-2 text-right select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("salary")}
                    data-testid="raw-pool-sort-salary"
                    title="Click to sort by salary (asc / desc / clear)">
                  Salary{poolTableSort.key === "salary"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                <th className="p-2 text-right select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("proj")}
                    data-testid="raw-pool-sort-proj"
                    title="Click to sort by projection (asc / desc / clear)">
                  Proj{poolTableSort.key === "proj"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                <th className="p-2 text-center" title="Sprint 60.129 · Override the model projection wholesale (blank = use model). Range 0-60 pts.">Proj Override</th>
                <th className="p-2 text-right select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("own")}
                    data-testid="raw-pool-sort-own"
                    title="Click to sort by ownership (asc / desc / clear)">
                  Own%{poolTableSort.key === "own"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                <th className="p-2 text-center" title="Sprint 60.129 · Override the market ownership before Leverage Modifiers key off it (blank = use market). Range 0-100%.">Own Override</th>
                <th className="p-2 text-right select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("ceil")}
                    data-testid="raw-pool-sort-ceil"
                    title="90th-percentile ceiling (per-player volatility × projection). Click to sort.">
                  Ceil{poolTableSort.key === "ceil"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                <th className="p-2 text-right select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("lev")}
                    data-testid="raw-pool-sort-lev"
                    title="Leverage = Ceiling ÷ Ownership. ≥ 4.0 = elite tournament leverage. Click to sort.">
                  Lev{poolTableSort.key === "lev"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                <th className="p-2 text-right select-none cursor-pointer hover:text-[#39FF14]"
                    onClick={() => _toggleTableSort("mkt")}
                    data-testid="raw-pool-sort-mkt"
                    title="Our projection minus the market consensus. Green = we're higher than the field's number; red = lower. '—' when no market line.">
                  Mkt Δ{poolTableSort.key === "mkt"
                    ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                    : <span className="text-zinc-700 ml-1">↕</span>}
                </th>
                {showFieldCols && (
                  <>
                    <th className="p-2 text-right select-none cursor-pointer hover:text-[#FFCC00] text-[#FFCC00]/80"
                        onClick={() => _toggleTableSort("fproj")}
                        data-testid="raw-pool-sort-fproj"
                        title="Competitor consensus projection (trust-weighted median across loaded sites). Read-only.">
                      Fld Proj{poolTableSort.key === "fproj"
                        ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                        : <span className="text-zinc-700 ml-1">↕</span>}
                    </th>
                    <th className="p-2 text-right select-none cursor-pointer hover:text-[#FFCC00] text-[#FFCC00]/80"
                        onClick={() => _toggleTableSort("fown")}
                        data-testid="raw-pool-sort-fown"
                        title="Competitor consensus ownership (max across loaded sites — the pessimistic number the dup estimate uses). Read-only.">
                      Fld Own{poolTableSort.key === "fown"
                        ? <span className="text-[#39FF14] ml-1">{poolTableSort.dir === "asc" ? "▲" : "▼"}</span>
                        : <span className="text-zinc-700 ml-1">↕</span>}
                    </th>
                  </>
                )}
                <th className="p-2 text-center">Min Exp %</th>
                <th className="p-2 text-center">Max Exp %</th>
                <th className="p-2 text-center">Boost {cfg.boost_mode === "abs" ? "(pts)" : "(%)"}</th>
                <th className="p-2 text-center" title="Like — one-click soft lean (+10% or +2 pts depending on boost mode)">Like</th>
                <th className="p-2 text-center">Lock/Excl</th>
              </tr>
            </thead>
            <tbody>
              {filteredPool.map((p, rowIdx) => (
                <tr
                  key={p.dk_id}
                  data-row-idx={rowIdx}
                  data-testid={`raw-pool-row-${p.dk_id}`}
                  onClick={() => setRawFocusId(p.dk_id)}
                  className={`raw-pool-row border-b border-zinc-900 hover:bg-zinc-900/40 ${p.dk_id === rawFocusId ? "is-focus outline outline-1 outline-dashed outline-zinc-500 bg-zinc-900/60" : ""}`}
                >
                  <td className="p-2 text-white">
                    <span className="inline-flex items-center gap-2">
                      <PlayerHeadshot player={p} sleeperId={headshots[String(p.dk_id)]} size={26} />
                      {p.name}
                    </span>
                  </td>
                  <td className="p-2">
                    {(() => {
                      const _raw = String(p.status || "").toUpperCase();
                      let s = (!_raw || _raw === "NONE" || _raw === "ACTIVE") ? String(p.injury_status || "").toUpperCase() : _raw;
                      if ((!s || s === "NONE" || s === "ACTIVE") && p.unplayable_reason === "BACKUP_QB") s = "BENCH";
                      if (!s || s === "NONE" || s === "ACTIVE") return <span className="text-zinc-700">—</span>;
                      const label = s === "PS" ? "PRACTICE SQUAD" : s === "FA" ? "UNROSTERED" : s === "BENCH" ? "BACKUP QB" : s;
                      const tone =
                        s === "PS" || s === "FA" || s === "BENCH"   ? "text-zinc-500 border-zinc-700 bg-zinc-900" :
                        s === "OUT" || s === "IR" || s === "SUSP" ? "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10" :
                        s === "D" || s === "DOUBTFUL"              ? "text-[#FF9500] border-[#FF9500]/40 bg-[#FF9500]/10" :
                        s === "Q" || s === "QUESTIONABLE"          ? "text-[#FFCC00] border-[#FFCC00]/40 bg-[#FFCC00]/10" :
                                                                     "text-zinc-400 border-zinc-800";
                      return <span className={`mono text-[9px] font-black uppercase tracking-widest px-1 py-0.5 border ${tone}`} data-testid={`raw-pool-status-${p.dk_id}`}>{label}</span>;
                    })()}
                  </td>
                  <td className="p-2 text-zinc-400">{p.position}</td>
                  <td className="p-2 mono font-black" style={{ color: teamTextColor(p.team) }}>{p.team}</td>
                  <td className="p-2 text-right text-zinc-300">${p.salary?.toLocaleString?.() || 0}</td>
                  <td className="p-2 text-right">
                    {(() => {
                      const baseProj = p.our_proj || p.projection || 0;
                      const isBoosted = leverageBoostedIds.has(String(p.dk_id));
                      const isTaxed = chalkTaxedIds.has(String(p.dk_id));
                      const boostPct = Number(cfg.leverage_booster_pct) || 15;
                      const taxPct = Number(cfg.chalk_tax_pct) || 20;
                      // Both stack (rare edge case — a top-20% proj player
                      // with sub-10% AND >25% own is impossible; kept for
                      // safety in case thresholds change).
                      let eff = baseProj;
                      if (isBoosted) eff *= (1 + boostPct / 100);
                      if (isTaxed) eff *= (1 - taxPct / 100);
                      if (!isBoosted && !isTaxed) {
                        return (
                          <span className="text-white" title={p.proj_prior ? "Salary + depth-chart prior — no book market or DK history yet (rookie / new signing). Volatility widened; override if you have a number." : undefined}>
                            {baseProj.toFixed(1)}
                            {p.proj_prior && <span className="ml-1 mono text-[8px] uppercase tracking-widest text-[#FFCC00]" data-testid={`raw-proj-prior-${p.dk_id}`}>prior</span>}
                          </span>
                        );
                      }
                      return (
                        <div className="inline-flex items-center gap-1 justify-end">
                          <span className={isBoosted ? "text-[#39FF14] font-bold" : "text-red-400 font-bold"}>
                            {eff.toFixed(1)}
                          </span>
                          <span
                            className={`mono text-[8px] font-black px-1 border ${
                              isBoosted
                                ? "text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/10"
                                : "text-red-400 border-red-500/40 bg-red-500/10"
                            }`}
                            title={
                              isBoosted
                                ? `Elite Value Booster: top-20% projection + <10% own → +${boostPct}% projection bump`
                                : `Chalk Tax: >25% own → -${taxPct}% projection suppression`
                            }
                          >
                            {isBoosted ? `+${boostPct}%` : `-${taxPct}%`}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-2 text-center">
                    <input
                      type="number"
                      min="0"
                      max="60"
                      step="0.1"
                      placeholder="—"
                      value={projectionOverrides[p.dk_id] ?? ""}
                      onChange={(e) => setProjOverride(p.dk_id, e.target.value)}
                      data-testid={`raw-proj-override-${p.dk_id}`}
                      title="Wholesale projection override (bypasses model + boost). Blank = use model."
                      className={`w-14 bg-black border focus:border-[#39FF14] outline-none px-1 py-0.5 text-center text-[11px] mono ${
                        (projectionOverrides[p.dk_id] ?? "") !== "" ? "border-[#39FF14]/60 text-[#39FF14] font-bold" : "border-zinc-800 text-zinc-300"
                      }`}
                    />
                  </td>
                  <td className="p-2 text-right text-zinc-400">{(p.our_own || 0).toFixed(1)}</td>
                  <td className="p-2 text-center">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      placeholder="—"
                      value={ownershipOverrides[p.dk_id] ?? ""}
                      onChange={(e) => setOwnOverride(p.dk_id, e.target.value)}
                      data-testid={`raw-own-override-${p.dk_id}`}
                      title="Override the market ownership BEFORE Leverage Modifiers key off it. Blank = use market."
                      className={`w-14 bg-black border focus:border-[#39FF14] outline-none px-1 py-0.5 text-center text-[11px] mono ${
                        (ownershipOverrides[p.dk_id] ?? "") !== "" ? "border-[#39FF14]/60 text-[#39FF14] font-bold" : "border-zinc-800 text-zinc-300"
                      }`}
                    />
                  </td>
                  <td className="p-2 text-right text-zinc-300" data-testid={`raw-ceil-${p.dk_id}`}>{ceilOf(p) > 0 ? ceilOf(p).toFixed(1) : "—"}</td>
                  <td className={`p-2 text-right font-bold ${levOf(p) >= 4 ? "text-[#39FF14]" : levOf(p) >= 2 ? "text-white" : "text-zinc-500"}`} data-testid={`raw-lev-${p.dk_id}`}>{levOf(p) > 0 ? levOf(p).toFixed(1) : "—"}</td>
                  <td className={`p-2 text-right ${mktDeltaOf(p) == null ? "text-zinc-700" : mktDeltaOf(p) >= 0.5 ? "text-[#39FF14]" : mktDeltaOf(p) <= -0.5 ? "text-[#FF3B30]" : "text-zinc-400"}`} data-testid={`raw-mkt-${p.dk_id}`}>
                    {mktDeltaOf(p) == null ? "—" : `${mktDeltaOf(p) >= 0 ? "+" : ""}${mktDeltaOf(p).toFixed(1)}`}
                  </td>
                  {showFieldCols && (
                    <>
                      <td className="p-2 text-right text-[#FFCC00]/90" data-testid={`raw-fproj-${p.dk_id}`}>{fldProjOf(p) > 0 ? fldProjOf(p).toFixed(1) : "—"}</td>
                      <td className="p-2 text-right text-[#FFCC00]/90" data-testid={`raw-fown-${p.dk_id}`}>{fldOwnOf(p) > 0 ? `${fldOwnOf(p).toFixed(1)}%` : "—"}</td>
                    </>
                  )}
                  <td className="p-2 text-center">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="—"
                      value={minExposures[p.dk_id] ?? ""}
                      onChange={(e) => setMinExp(p.dk_id, e.target.value)}
                      data-testid={`raw-min-exp-${p.dk_id}`}
                      className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-1 py-0.5 text-center text-[11px] mono"
                    />
                  </td>
                  <td className="p-2 text-center">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="—"
                      value={maxExposures[p.dk_id] ?? ""}
                      onChange={(e) => setMaxExp(p.dk_id, e.target.value)}
                      data-testid={`raw-max-exp-${p.dk_id}`}
                      className="w-14 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-1 py-0.5 text-center text-[11px] mono"
                    />
                  </td>
                  <td className="p-2">
                    <div className="flex items-center justify-center gap-1">
                      <div className="flex gap-0.5">
                        {(cfg.boost_mode === "abs"
                          ? [
                              { v: 3,  label: "♥", tip: "Love +3.0 pts" },
                              { v: 1.5,label: "+", tip: "Like +1.5 pts" },
                              { v: -1.5,label: "−", tip: "Dislike −1.5 pts" },
                              { v: -3, label: "✕", tip: "Hate −3.0 pts" },
                            ]
                          : [
                              { v: 15, label: "♥", tip: "Love +15%" },
                              { v: 8, label: "+", tip: "Like +8%" },
                              { v: -8, label: "−", tip: "Dislike −8%" },
                              { v: -15, label: "✕", tip: "Hate −15%" },
                            ]
                        ).map((b) => {
                          const on = (boosts[p.dk_id] || 0) === b.v;
                          return (
                            <button
                              key={b.v}
                              type="button"
                              title={b.tip}
                              data-testid={`raw-boost-preset-${b.v}-${p.dk_id}`}
                              onClick={() => setBoost(p.dk_id, on ? 0 : b.v)}
                              className={`w-5 h-5 text-[10px] font-black mono border ${
                                on
                                  ? (b.v > 0
                                      ? "bg-[#39FF14] text-black border-[#39FF14]"
                                      : "bg-[#FF3B30] text-black border-[#FF3B30]")
                                  : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                              }`}
                            >
                              {b.label}
                            </button>
                          );
                        })}
                      </div>
                      <input
                        type="number"
                        min={cfg.boost_mode === "abs" ? "-15" : "-25"}
                        max={cfg.boost_mode === "abs" ? "15" : "25"}
                        step={cfg.boost_mode === "abs" ? "0.5" : "1"}
                        placeholder="0"
                        value={boosts[p.dk_id] ?? ""}
                        onChange={(e) => setBoost(p.dk_id, e.target.value)}
                        data-testid={`raw-boost-${p.dk_id}`}
                        className={`w-11 bg-black border focus:border-[#39FF14] outline-none px-1 py-0.5 text-center text-[11px] mono ${
                          (boosts[p.dk_id] || 0) > 0 ? "border-[#39FF14]/40 text-[#39FF14]" :
                          (boosts[p.dk_id] || 0) < 0 ? "border-[#FF3B30]/40 text-[#FF3B30]" : "border-zinc-800 text-zinc-300"
                        }`}
                      />
                    </div>
                  </td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() => toggleLike(p.dk_id)}
                      data-testid={`raw-like-${p.dk_id}`}
                      aria-pressed={isLiked(p.dk_id)}
                      title={isLiked(p.dk_id) ? "Liked — click to clear the boost" : `Like · ${cfg.boost_mode === "abs" ? "+2 pts" : "+10%"} soft lean`}
                      className={`px-2 py-0.5 text-[9px] ${isLiked(p.dk_id) ? "bg-[#FFCC00] text-black" : "border border-zinc-700 text-zinc-500 hover:border-[#FFCC00] hover:text-[#FFCC00]"}`}>
                      ★
                    </button>
                  </td>
                  <td className="p-2 text-center whitespace-nowrap">
                    <button onClick={() => toggleLock(p.dk_id)} data-testid={`raw-lock-${p.dk_id}`} aria-pressed={locks.has(p.dk_id)}
                      className={`px-2 py-0.5 text-[9px] mr-1 ${locks.has(p.dk_id) ? "bg-[#39FF14] text-black" : "border border-zinc-700 text-zinc-500"}`}>
                      L
                    </button>
                    {showRemoved && (
                      <button
                        type="button"
                        data-testid={`raw-restore-${p.dk_id}`}
                        onClick={() => {
                          if (excludes.has(p.dk_id)) toggleExclude(p.dk_id);
                          if (isOut(p)) toggleRestore(p.dk_id);
                          toast.success(`${p.name} restored to the active pool${isOut(p) ? " — set your own projection (model shows 0)" : ""}.`);
                        }}
                        title={isOut(p) ? `Restore ${p.name} despite the ${p.injury_status} tag — you know something the feed doesn't` : `Restore ${p.name} to the active pool`}
                        className="px-2 py-0.5 text-[9px] mr-1 border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black">
                        ↺
                      </button>
                    )}
                    {restores.has(String(p.dk_id)) && (
                      <button type="button" data-testid={`raw-restored-badge-${p.dk_id}`} onClick={() => toggleRestore(p.dk_id)}
                        title="You restored this player over the injury feed. Click to hand him back to the feed."
                        className="px-1.5 py-0.5 text-[8px] mr-1 mono font-bold border border-[#39FF14]/50 text-[#39FF14] bg-[#39FF14]/10 hover:text-[#FF3B30] hover:border-[#FF3B30]/50">
                        RESTORED
                      </button>
                    )}
                    <button onClick={() => toggleExclude(p.dk_id)} data-testid={`raw-excl-${p.dk_id}`} aria-pressed={excludes.has(p.dk_id)}
                      className={`px-2 py-0.5 text-[9px] ${excludes.has(p.dk_id) ? "bg-[#FF3B30] text-black" : "border border-zinc-700 text-zinc-500"}`}>
                      X
                    </button>
                    {isShowdownSlate && (
                      <>
                        <button onClick={() => toggleCptLock(p.dk_id)} data-testid={`raw-cpt-lock-${p.dk_id}`} aria-pressed={cptLocks.has(p.dk_id)}
                          title={`Pin as Captain · $${Math.round((p.salary || 0) * 1.5).toLocaleString()} at CPT`}
                          className={`px-2 py-0.5 text-[9px] ml-1 ${cptLocks.has(p.dk_id) ? "bg-[#FF8A00] text-black" : "border border-zinc-700 text-zinc-500 hover:border-[#FF8A00] hover:text-[#FF8A00]"}`}>
                          CPT
                        </button>
                        <button onClick={() => toggleCptExclude(p.dk_id)} data-testid={`raw-cpt-excl-${p.dk_id}`} aria-pressed={cptExcludes.has(p.dk_id)}
                          title="Never Captain (FLEX only)"
                          className={`px-2 py-0.5 text-[9px] ml-1 ${cptExcludes.has(p.dk_id) ? "bg-zinc-300 text-black" : "border border-zinc-700 text-zinc-500 hover:border-zinc-400"}`}>
                          ✕C
                        </button>
                        <input type="number" min="0" max="100" step="5" placeholder="min%" title="Captain minimum — force this player to CPT in at least this % of lineups"
                          data-testid={`raw-cpt-min-${p.dk_id}`} value={cptMinPct[p.dk_id] ?? ""} onChange={(e) => setCptMin(p.dk_id, e.target.value)}
                          className="w-12 ml-1 bg-black border border-zinc-800 focus:border-[#FF8A00] outline-none px-1 py-0.5 text-[10px] mono" />
                        <input type="number" min="0" max="100" step="5" placeholder="max%" title="Captain cap — never CPT in more than this % of lineups"
                          data-testid={`raw-cpt-max-${p.dk_id}`} value={cptMaxPct[p.dk_id] ?? ""} onChange={(e) => setCptMax(p.dk_id, e.target.value)}
                          className="w-12 ml-1 bg-black border border-zinc-800 focus:border-[#FF8A00] outline-none px-1 py-0.5 text-[10px] mono" />
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </div>
      </div>

      <div className="space-y-6" data-testid="raw-lineups-deck">

      {/* --- Panel 3: Lineups --- */}
      <section className="bg-[#121212] border border-zinc-800 p-4" data-testid="raw-lineups-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="heading text-lg font-black uppercase">Lineups</div>
            <div className="text-[10px] mono text-zinc-500">
              {lineups.length} built · Raw namespace (separate from Smart Optimizer)
            </div>
            <ShapeBreakdown lineups={lineups} showdown={isShowdownSlate} />
          </div>
          {lineups.length > 0 && (
            <div className="flex gap-2 flex-wrap items-center">
              {/* Sprint 60.129 — Randomize (on-demand cluster rotation shuffle) */}
              <button
                type="button"
                onClick={() => {
                  if (!confirm(`Shuffle ${lineups.length} lineups with a fresh seed? Statistically equivalent players (same pos, ±5% proj, ±$400 salary) will rotate to widen your exposure. Salary cap + locks are respected.`)) return;
                  randomizeLineups();
                }}
                disabled={busy}
                data-testid="raw-randomize-lineups"
                title="Post-build peer swap with a fresh seed — same salary cap, same locks, new player mix"
                className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
              >
                🎲 Randomize
              </button>
              {/* Sprint 60.131 — Late-Swap Auto-Push */}
              <button
                type="button"
                onClick={autoSwapScratched}
                disabled={busy}
                data-testid="raw-auto-swap-btn"
                title="A player got ruled OUT after lineups were built? Auto-swap them out for the highest-proj same-position peer under the cap."
                className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#FFCC00]/60 text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ⚠ Auto-Swap Scratched
              </button>
              {/* Sprint 60.131 — What-If Scenarios */}
              <button
                type="button"
                onClick={saveScenario}
                data-testid="raw-save-scenario-btn"
                title="Save this lineup batch as a named scenario for later side-by-side comparison"
                className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-zinc-700 text-zinc-300 hover:border-[#39FF14] hover:text-[#39FF14]"
              >
                💾 Save Scenario
              </button>
              {scenarios.length > 0 && (
                <select
                  onChange={(e) => {
                    const name = e.target.value;
                    if (!name) return;
                    loadScenario(name);
                    e.target.value = "";
                  }}
                  data-testid="raw-load-scenario"
                  defaultValue=""
                  title="Load a previously saved scenario as the active batch"
                  className="bg-black border border-zinc-700 focus:border-[#39FF14] outline-none px-2 py-0.5 text-[9px] mono uppercase tracking-widest text-zinc-300"
                >
                  <option value="">Load Scenario…</option>
                  {scenarios.map((s) => (
                    <option key={s.name} value={s.name}>{s.name}</option>
                  ))}
                </select>
              )}
              {scenarios.length > 0 && (
                <select
                  value={compareScenario || ""}
                  onChange={(e) => setCompareScenario(e.target.value || null)}
                  data-testid="raw-compare-scenario"
                  title="Compare current batch's exposures side-by-side with a saved scenario"
                  className={`bg-black border focus:border-[#39FF14] outline-none px-2 py-0.5 text-[9px] mono uppercase tracking-widest ${
                    compareScenario ? "border-[#39FF14] text-[#39FF14]" : "border-zinc-700 text-zinc-300"
                  }`}
                >
                  <option value="">Compare Vs…</option>
                  {scenarios.map((s) => (
                    <option key={s.name} value={s.name}>{s.name}</option>
                  ))}
                </select>
              )}
              {scenarios.length > 0 && (
                <select
                  onChange={(e) => {
                    const name = e.target.value;
                    if (!name) return;
                    deleteScenario(name);
                    e.target.value = "";
                  }}
                  data-testid="raw-delete-scenario"
                  defaultValue=""
                  title="Delete a saved scenario"
                  className="bg-black border border-zinc-700 focus:border-[#FF3B30] outline-none px-2 py-0.5 text-[9px] mono uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30]"
                >
                  <option value="">×</option>
                  {scenarios.map((s) => (
                    <option key={s.name} value={s.name}>{s.name}</option>
                  ))}
                </select>
              )}
              {/* Sprint 60.127 — Diff Tab toggle */}
              {prevLineups.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowDiff(!showDiff)}
                  data-testid="raw-diff-toggle"
                  title={`Diff vs previous run (${prevLineups.length} lineups)`}
                  className={`mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${
                    showDiff
                      ? "bg-[#39FF14] text-black border-[#39FF14]"
                      : "border-zinc-700 text-zinc-400 hover:border-[#39FF14] hover:text-white"
                  }`}
                >
                  {showDiff ? "◉ Diff" : "◎ Diff vs Prev"}
                </button>
              )}
              {/* Sprint 60.78 — sort */}
              <div className="flex items-center gap-1">
                <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Sort ·</span>
                {[
                  { key: "index", label: "#" },
                  { key: "projection", label: "Proj" },
                  { key: "salary", label: "Salary" },
                  { key: "own_sum", label: "Own Σ" },
                  { key: "field_win", label: "Field Win" },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setLineupSort((prev) => (
                      prev.key === s.key ? { key: s.key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key: s.key, dir: "desc" }
                    ))}
                    data-testid={`raw-sort-${s.key}`}
                    className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                      lineupSort.key === s.key
                        ? "bg-[#39FF14] text-black border-[#39FF14]"
                        : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                    }`}
                  >
                    {s.label} {lineupSort.key === s.key ? (lineupSort.dir === "asc" ? "↑" : "↓") : ""}
                  </button>
                ))}
              </div>
              <button
                type="button"
                data-testid="raw-lineups-expand-all"
                onClick={() => setExpandedLineups(new Set(lineups.map((_, i) => i)))}
                className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#39FF14] border border-zinc-800 hover:border-[#39FF14] px-2 py-1"
              >Expand All</button>
              <button
                type="button"
                data-testid="raw-lineups-collapse-all"
                onClick={() => setExpandedLineups(new Set())}
                className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-500 px-2 py-1"
              >Collapse All</button>
              <button
                type="button"
                data-testid="raw-lineups-clear"
                onClick={async () => {
                  if (!window.confirm(
                    `Clear all ${lineups.length} Manual Optimizer lineups?\n\n` +
                    "This wipes the Manual Optimizer's saved lineups so they won't\n" +
                    "leak into the Simulation tab. Smart Optimizer lineups are\n" +
                    "kept in a separate bucket and won't be affected."
                  )) return;
                  try {
                    const r = await api.clearRawLineups();
                    setLineups([]);
                    setExpandedLineups(new Set());
                    try { window.dispatchEvent(new CustomEvent("wp:lineups-cleared", { detail: { source: "raw" } })); }
                    catch { /* noop */ }
                    toast.success(`Cleared ${r.cleared} lineups from Manual Optimizer.`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Clear failed");
                  }
                }}
                className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FF3B30] border border-zinc-800 hover:border-[#FF3B30] px-2 py-1"
              >Clear</button>
            </div>
          )}
        </div>
        {lineups.length === 0 ? (
          <div className="text-center py-12 text-zinc-500 mono text-xs">
            No lineups yet. Configure above and click Generate.
          </div>
        ) : compareScenario ? (
          <ScenarioCompareView
            currentLineups={lineups}
            scenarioName={compareScenario}
            scenarioLineups={(scenarios.find((s) => s.name === compareScenario) || {}).lineups || []}
            onClose={() => setCompareScenario(null)}
          />
        ) : showDiff && lineupDiff.length > 0 ? (
          <div className="overflow-x-auto max-h-[600px]">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-3">
              <span>Diff vs previous run ({prevLineups.length} lineups)</span>
              <span className="text-zinc-600">·</span>
              <span><span className="text-[#39FF14]">{lineupDiff.filter((d) => d.added.length > 0 || d.removed.length > 0).length}</span> changed</span>
              <span className="text-zinc-600">·</span>
              <span><span className="text-zinc-400">{lineupDiff.filter((d) => d.unchanged).length}</span> unchanged</span>
            </div>
            <div className="space-y-1.5">
              {lineupDiff.filter((d) => !d.unchanged).map((d) => (
                <div key={d.lineup_id} data-testid={`raw-diff-row-${d.idx}`} className="border border-zinc-800 bg-black/60 p-2">
                  <div className="flex items-center gap-3 mb-1.5">
                    <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Lineup #{d.idx + 1}</span>
                    <span className={`mono text-[10px] font-bold ${d.proj_delta >= 0 ? "text-[#39FF14]" : "text-red-400"}`}>
                      {d.proj_delta >= 0 ? "+" : ""}{d.proj_delta.toFixed(1)} proj
                    </span>
                    <span className={`mono text-[10px] ${d.sal_delta >= 0 ? "text-zinc-300" : "text-zinc-500"}`}>
                      {d.sal_delta >= 0 ? "+" : ""}${d.sal_delta} sal
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="mono text-[9px] uppercase tracking-widest text-[#39FF14] mb-1">+ Added</div>
                      {d.added.length === 0 ? (
                        <div className="mono text-[10px] text-zinc-600">none</div>
                      ) : d.added.map((p) => (
                        <div key={p.dk_id} className="mono text-[10px] text-white">
                          <span className="text-zinc-500 mr-1">{p.position}</span>{p.name} <span className="text-zinc-600">({p.team})</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="mono text-[9px] uppercase tracking-widest text-red-400 mb-1">− Removed</div>
                      {d.removed.length === 0 ? (
                        <div className="mono text-[10px] text-zinc-600">none</div>
                      ) : d.removed.map((p) => (
                        <div key={p.dk_id} className="mono text-[10px] text-zinc-400 line-through">
                          <span className="text-zinc-600 mr-1">{p.position}</span>{p.name} <span className="text-zinc-600">({p.team})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {lineupDiff.every((d) => d.unchanged) && (
                <div className="text-center py-8 text-zinc-500 mono text-xs">
                  All {lineups.length} lineups unchanged from previous run.
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
          <RawExposurePanel lineups={lineups} onApplied={(ls) => { setLineups(ls); setExpandedLineups(new Set()); }} />
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full min-w-[720px] text-[10px] mono">
              <thead className="sticky top-0 bg-[#121212] border-b border-zinc-800 z-10">
                <tr className="text-zinc-500 text-left">
                  <th className="p-2 w-6"></th>
                  <th className="p-2 w-6"></th>
                  <th className="p-2">#</th>
                  <th className="p-2">Players</th>
                  <th className="p-2 text-right">Salary</th>
                  <th className="p-2 text-right">Proj</th>
                  <th className="p-2 text-right">Own Σ</th>
                  <th className="p-2 text-right" title="Field Win % — how often this lineup clears the field's winning bar in the correlated simulation, discounted by expected copies. Needs a contest field size.">Field Win</th>
                  <th className="p-2 text-right" title="Expected number of other entries holding this exact lineup.">Copies</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {sortedLineups.map(({ lineup: l, originalIndex: i }, displayIdx) => {
                  const expanded = expandedLineups.has(i);
                  const toggle = () => {
                    setExpandedLineups((prev) => {
                      const next = new Set(prev);
                      if (next.has(i)) next.delete(i); else next.add(i);
                      return next;
                    });
                  };
                  const remove = (e) => {
                    e.stopPropagation();
                    setLineups((prev) => prev.filter((_, idx) => idx !== i));
                    setExpandedLineups((prev) => {
                      const next = new Set();
                      prev.forEach((v) => {
                        if (v < i) next.add(v);
                        else if (v > i) next.add(v - 1);
                      });
                      return next;
                    });
                    toast.success(`Lineup #${i + 1} removed`);
                  };
                  return (
                    <React.Fragment key={i}>
                      <tr
                        onClick={toggle}
                        className="border-b border-zinc-900 cursor-pointer hover:bg-zinc-900/40"
                        data-testid={`raw-lineup-row-${i}`}
                      >
                        <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            data-testid={`raw-lineup-diff-${i}`}
                            checked={diffSelection.has(i)}
                            onChange={(e) => {
                              setDiffSelection((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) {
                                  if (next.size >= 2) {
                                    // Drop the oldest to keep max 2
                                    const iter = next.values();
                                    next.delete(iter.next().value);
                                  }
                                  next.add(i);
                                } else {
                                  next.delete(i);
                                }
                                return next;
                              });
                            }}
                            className="w-3.5 h-3.5 accent-[#39FF14] cursor-pointer"
                            title="Compare (max 2)"
                          />
                        </td>
                        <td className="p-2 text-zinc-500">
                          {expanded ? <CaretDown size={12} /> : <CaretUp size={12} weight="fill" style={{ transform: "rotate(90deg)" }} />}
                        </td>
                        <td className="p-2 text-zinc-500">{displayIdx + 1}</td>
                        <td className="p-2 text-white truncate max-w-lg">
                          {(l.players || []).map((p) => p.name).join(" · ")}
                        </td>
                        <td className="p-2 text-right text-zinc-300">${l.salary?.toLocaleString?.() || 0}</td>
                        <td className="p-2 text-right text-[#39FF14] font-bold">{(l.projection || 0).toFixed(1)}</td>
                        <td className="p-2 text-right text-zinc-400">{(l.own_sum || 0).toFixed(1)}%</td>
                        <td className="p-2 text-right mono" data-testid={`raw-field-win-${i}`}>
                          {l.sim_win_rate != null ? <><span className="text-[#FFCC00] font-bold">{(Number(l.sim_win_rate) * 100).toFixed(1)}%</span>{l.sim_win_rank ? <span className="text-zinc-600 text-[9px]"> #{l.sim_win_rank}</span> : null}</> : <span className="text-zinc-700">—</span>}
                        </td>
                        <td className={`p-2 text-right mono ${l.expected_dupes == null ? "text-zinc-700" : Number(l.expected_dupes) > 1 ? "text-[#FF3B30]" : Number(l.expected_dupes) > 0.2 ? "text-[#FFCC00]" : "text-zinc-400"}`} data-testid={`raw-copies-${i}`}>
                          {l.expected_dupes == null ? "—" : Number(l.expected_dupes).toFixed(2)}
                        </td>
                        <td className="p-2 text-center">
                          <button
                            type="button"
                            onClick={remove}
                            data-testid={`raw-lineup-remove-${i}`}
                            title="Remove this lineup"
                            className="text-zinc-600 hover:text-[#FF3B30] mono text-sm font-black leading-none"
                          >×</button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-b border-zinc-900 bg-black/40">
                          <td colSpan={10} className="p-3">
                            {(l.swap_log || []).length > 0 && (
                              <div className="mb-2 mono text-[9px] text-zinc-400" data-testid={`raw-swap-log-${i}`}>
                                <span className="text-[#FFCC00] uppercase tracking-widest">Peer swaps</span>{" "}
                                {l.swap_log.map((sw, k) => (
                                  <span key={k} className="mr-2">{sw.slot || sw.position || ""} {sw.out_name || sw.out || "?"}{sw.out_proj != null ? <span className="text-zinc-600"> {Number(sw.out_proj).toFixed(1)}</span> : null} → <span className="text-white">{sw.in_name || sw.in || "?"}</span>{sw.in_proj != null ? <span className="text-zinc-600"> {Number(sw.in_proj).toFixed(1)}</span> : null}{sw.reason ? ` (${sw.reason})` : ""}</span>
                                ))}
                              </div>
                            )}
                            <div className="grid grid-cols-3 md:grid-cols-9 gap-2" data-testid={`raw-lineup-roster-${i}`}>
                              {sortPlayersDKOrder(l.players || []).map((p, j) => (
                                <div key={j} className="border border-zinc-800 bg-[#0a0a0a] p-2 relative overflow-hidden">
                                  <div className="absolute -right-3 -top-3 opacity-[0.07] pointer-events-none"><TeamLogo team={p.team} size={64} withGlow={false} /></div>
                                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                                    {p.roster_slot || p.position}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <PlayerHeadshot player={p} sleeperId={headshots[String(p.dk_id)]} size={28} />
                                    <div className="min-w-0">
                                      <div className="mono text-[11px] text-white font-bold truncate">{p.name}</div>
                                      <div className="mono text-[9px] text-zinc-400 mt-0.5 flex items-center gap-1">
                                        {p.position} · <TeamLogo team={p.team} size={14} withGlow={false} /> <span className="font-black" style={{ color: teamTextColor(p.team) }}>{p.team}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mono text-[9px] text-zinc-500 mt-0.5">
                                    ${(p.salary || 0).toLocaleString()} · {Number(p.projection || p.our_proj || 0).toFixed(1)} pts
                                  </div>
                                  <button
                                    type="button"
                                    data-testid={`raw-swap-out-${i}-${p.dk_id}`}
                                    title="Swap this player out — the other 8 stay, the lineup stays unique vs your other lineups"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const r = await http.post("/optimize/raw/fix-lineup", { index: i, drop_dk_ids: [String(p.dk_id)] });
                                        setLineups(r.data.lineups || []);
                                        toast.success(`Lineup #${i + 1}: swapped out ${p.name}`);
                                      } catch (err) {
                                        toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Swap failed");
                                      }
                                    }}
                                    className="mt-1 mono text-[8px] uppercase tracking-widest text-zinc-500 hover:text-[#FFCC00] border border-zinc-800 hover:border-[#FFCC00] px-1.5 py-0.5"
                                  >
                                    ⟲ swap out
                                  </button>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>

      {/* Sprint 60.84 — Lineup Diff Viewer */}
      {diffSelection.size > 0 && (
        <LineupDiffPanel
          lineups={lineups}
          selectedIndexes={Array.from(diffSelection).slice(0, 2)}
          onClear={() => setDiffSelection(new Set())}
        />
      )}

      {/* Sprint 60.79 — Portfolio Insights (team exposure + dupe risk) */}
      {lineups.length > 0 && (
        <>
          <PortfolioInsights lineups={lineups} />
          <TeamStackHeatmap lineups={lineups} />
        </>
      )}

      {/* Sprint 60.81 — Cross-contest simulator */}
      {lineups.length > 0 && (
        <CrossContestSim lineups={lineups} />
      )}

      {/* Sprint 60.77 — DK Entry Template fill (Raw source) */}
      <DkTemplateFill source="raw" />
      </div>

      {/* Sprint 60.132 — Mobile sticky bottom action bar. Only visible
          on <md screens. Pins the two highest-frequency Sunday actions
          (Generate + Randomize) to the bottom so grinders on their phone
          don't have to scroll to the top toolbar every time. */}
      <div
        data-testid="raw-mobile-action-bar"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-black border-t-2 border-[#39FF14] px-3 py-2 flex items-center gap-2"
      >
        <button
          type="button"
          onClick={() => generate()}
          disabled={busy}
          data-testid="raw-mobile-generate-btn"
          className="flex-1 min-h-[44px] bg-[#39FF14] text-black font-black uppercase tracking-widest text-xs disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {busy ? "Building…" : "Generate"}
        </button>
        {lineups.length > 0 && (
          <button
            type="button"
            onClick={randomizeLineups}
            disabled={busy}
            data-testid="raw-mobile-randomize-btn"
            className="min-h-[44px] px-3 border border-[#39FF14]/60 text-[#39FF14] mono uppercase tracking-widest text-[10px] disabled:opacity-30"
          >
            🎲
          </button>
        )}
        <div
          data-testid="raw-mobile-diversity-chip"
          className="mono text-[8px] uppercase tracking-widest text-zinc-500 whitespace-nowrap"
        >
          <div className={cfg.fingerprint_variance_enabled ? "text-[#39FF14]" : "text-zinc-600"}>F {cfg.fingerprint_variance_enabled ? "•" : "○"}</div>
          <div className={cfg.cluster_rotation_enabled ? "text-[#39FF14]" : "text-zinc-600"}>C {cfg.cluster_rotation_enabled ? "•" : "○"}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
      />
    </label>
  );
}

/**
 * Sprint 60.79 — Portfolio Insights.
 * Read-only visualization panel that sits between the lineups list and
 * the DK template fill widget. Two views:
 *   1. Team Exposure — horizontal bars showing which teams the portfolio
 *      leans on. Users spot over-concentration at a glance.
 *   2. Dupe Estimate — rough field-uniqueness score per lineup. Approx:
 *      product_own_est ≈ (own_sum/9/100)^9, then × field_size (Milly = 1M).
 */
function PortfolioInsights({ lineups }) {
  const [fieldSize, setFieldSize] = useState(176_470);

  const teamExposure = React.useMemo(() => {
    const total = lineups.length || 1;
    const counts = new Map();
    lineups.forEach((l) => {
      const teams = new Set();
      (l.players || []).forEach((p) => {
        if (p.team) teams.add(p.team);
      });
      teams.forEach((t) => counts.set(t, (counts.get(t) || 0) + 1));
    });
    return Array.from(counts.entries())
      .map(([team, n]) => ({ team, n, pct: (n / total) * 100 }))
      .sort((a, b) => b.pct - a.pct);
  }, [lineups]);

  const dupeStats = React.useMemo(() => {
    const rows = lineups.map((l) => {
      const own = Number(l.own_sum || 0);
      // avg per-player ownership across 9 slots, expressed as decimal
      const avgOwn = own / 9 / 100;
      // Product of 9 identical ownerships (rough) = avgOwn^9
      const prod = Math.max(0, Math.pow(avgOwn, 9));
      const dupes = prod * fieldSize;
      return { own, dupes };
    });
    const unique = rows.filter((r) => r.dupes < 1).length;
    const highDupe = rows.filter((r) => r.dupes >= 5).length;
    const avgDupes = rows.length ? rows.reduce((s, r) => s + r.dupes, 0) / rows.length : 0;
    return { rows, unique, highDupe, avgDupes };
  }, [lineups, fieldSize]);

  return (
    <section
      data-testid="raw-portfolio-insights"
      className="bg-[#121212] border border-zinc-800 p-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="heading text-lg font-black uppercase">Portfolio Insights</div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
            Team exposure · Dupe estimate
          </div>
        </div>
        <label className="flex items-center gap-2">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">Field size ·</span>
          <select
            value={fieldSize}
            onChange={(e) => setFieldSize(Number(e.target.value))}
            data-testid="raw-insights-field-size"
            className="bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1 text-xs mono text-white"
          >
            <option value={176_470}>Milly Maker · 176K</option>
            <option value={117_647}>Flea Flicker · 118K</option>
            <option value={23_529}>Mid GPP · 23.5K</option>
            <option value={1_500}>Small GPP · 1.5K</option>
            <option value={100}>Double-Up · 100</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Team Exposure */}
        <div className="border border-zinc-900 bg-black p-3">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Team Exposure · Top {Math.min(12, teamExposure.length)}
          </div>
          <div className="space-y-1" data-testid="raw-team-exposure">
            {teamExposure.slice(0, 12).map((t) => (
              <div key={t.team} className="flex items-center gap-2 text-[11px] mono">
                <div className="w-10 text-white">{t.team}</div>
                <div className="flex-1 h-3 bg-zinc-900 relative overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 ${
                      t.pct > 60 ? "bg-[#FF3B30]" : t.pct > 35 ? "bg-[#FFCC00]" : "bg-[#39FF14]"
                    }`}
                    style={{ width: `${Math.min(100, t.pct)}%` }}
                  />
                </div>
                <div className="w-16 text-right text-zinc-400">
                  {t.n} · {t.pct.toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dupe Estimate */}
        <div className="border border-zinc-900 bg-black p-3">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Dupe Estimate · {fieldSize.toLocaleString()} entrants
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="raw-dupe-summary">
            <MetricTile label="Unique (<1)" value={dupeStats.unique} tone="green" />
            <MetricTile label="High risk (≥5)" value={dupeStats.highDupe} tone="red" />
            <MetricTile label="Avg dupes" value={dupeStats.avgDupes.toFixed(1)} tone="zinc" />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-[10px] mono">
              <thead className="text-zinc-500 border-b border-zinc-900 sticky top-0 bg-black">
                <tr className="uppercase tracking-widest text-[9px]">
                  <th className="px-2 py-1 text-left">#</th>
                  <th className="px-2 py-1 text-right">Own Σ</th>
                  <th className="px-2 py-1 text-right">Dupes</th>
                </tr>
              </thead>
              <tbody>
                {dupeStats.rows.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-900">
                    <td className="px-2 py-1 text-zinc-400">{i + 1}</td>
                    <td className="px-2 py-1 text-right text-zinc-300">{r.own.toFixed(1)}%</td>
                    <td className={`px-2 py-1 text-right font-bold ${
                      r.dupes < 1 ? "text-[#39FF14]" :
                      r.dupes < 5 ? "text-[#FFCC00]" : "text-[#FF3B30]"
                    }`}>{r.dupes.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function MetricTile({ label, value, tone = "zinc" }) {
  const color = tone === "green" ? "text-[#39FF14]" : tone === "red" ? "text-[#FF3B30]" : "text-white";
  return (
    <div className="border border-zinc-900 bg-[#0a0a0a] p-2 text-center">
      <div className={`mono text-lg font-black ${color}`}>{value}</div>
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}

/**
 * Sprint 60.84 — Lineup Diff Viewer.
 *
 * User checks up to 2 lineups in the panel table. When 1 is selected we
 * show a "pick one more" bar; when 2 are selected we render side-by-side
 * roster columns with per-slot color coding:
 *   • green → same player at same roster slot
 *   • yellow → different player at same roster slot
 *   • grey → slot appears in one lineup but not the other (rare)
 *
 * Metric header shows delta on Proj / Salary / Own Σ so the user knows
 * exactly why the higher-projected lineup outranks the other.
 */
function LineupDiffPanel({ lineups, selectedIndexes, onClear }) {
  const [a, b] = selectedIndexes;
  const aL = lineups[a];
  const bL = lineups[b];

  // Selecting only 1 — show a hint bar and bail
  if (!aL || !bL) {
    return (
      <section
        data-testid="raw-diff-panel"
        className="bg-[#121212] border border-[#39FF14]/40 p-3 flex items-center justify-between"
      >
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-300">
          Diff Mode · <span className="text-[#39FF14]">Lineup #{(a ?? b) + 1}</span> selected · pick one more to compare (max 2)
        </div>
        <button
          type="button"
          onClick={onClear}
          className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
        >Clear</button>
      </section>
    );
  }

  // Build a per-slot map keyed by roster_slot when available, else by position+index
  const slotOrder = ["QB", "RB1", "RB2", "WR1", "WR2", "WR3", "TE", "FLEX", "DST"];
  const _byPosIndex = (players) => {
    const map = new Map();
    const counters = new Map();
    (players || []).forEach((p) => {
      const key = p.roster_slot ||
        (() => {
          const pos = (p.position || "?").toUpperCase();
          const n = (counters.get(pos) || 0) + 1;
          counters.set(pos, n);
          if (pos === "RB" && n <= 2) return `RB${n}`;
          if (pos === "WR" && n <= 3) return `WR${n}`;
          if (pos === "RB" || pos === "WR" || pos === "TE") return "FLEX";
          return pos;
        })();
      map.set(key, p);
    });
    return map;
  };
  const mapA = _byPosIndex(aL.players);
  const mapB = _byPosIndex(bL.players);
  const slotKeys = Array.from(new Set([...mapA.keys(), ...mapB.keys(), ...slotOrder]))
    .sort((x, y) => (slotOrder.indexOf(x) === -1 ? 99 : slotOrder.indexOf(x)) - (slotOrder.indexOf(y) === -1 ? 99 : slotOrder.indexOf(y)));

  const projDelta = (aL.projection || 0) - (bL.projection || 0);
  const salDelta = (aL.salary || 0) - (bL.salary || 0);
  const ownDelta = (aL.own_sum || 0) - (bL.own_sum || 0);
  const same = slotKeys.filter((k) => mapA.get(k)?.dk_id && mapA.get(k)?.dk_id === mapB.get(k)?.dk_id).length;

  const cellTone = (aP, bP) => {
    if (!aP || !bP) return "border-zinc-900 bg-black/60 text-zinc-500";
    if (aP.dk_id === bP.dk_id) return "border-[#39FF14]/30 bg-[#39FF14]/5 text-white";
    return "border-[#FFCC00]/30 bg-[#FFCC00]/5 text-white";
  };

  const PlayerCell = ({ p, tone }) => (
    <div className={`border ${tone} px-2 py-1.5`}>
      {p ? (
        <>
          <div className="mono text-[11px] font-bold truncate">{p.name}</div>
          <div className="mono text-[9px] text-zinc-400 mt-0.5">
            {p.position} · {p.team} · ${(p.salary || 0).toLocaleString()} · {Number(p.projection || p.our_proj || 0).toFixed(1)}
          </div>
        </>
      ) : (
        <div className="mono text-[9px] text-zinc-600 uppercase tracking-widest">— none —</div>
      )}
    </div>
  );

  return (
    <section
      data-testid="raw-diff-panel"
      className="bg-[#121212] border border-[#39FF14]/40 p-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="heading text-lg font-black uppercase">Lineup Diff</div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
            Comparing #{a + 1} vs #{b + 1} · {same}/9 slots identical
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          data-testid="raw-diff-clear"
          className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white border border-zinc-800 hover:border-zinc-500 px-2 py-1"
        >Clear</button>
      </div>

      {/* Metric header */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <DiffMetric label="Proj" a={aL.projection} b={bL.projection} delta={projDelta} fmt={(v) => v.toFixed(1)} biggerIsBetter />
        <DiffMetric label="Salary" a={aL.salary} b={bL.salary} delta={salDelta} fmt={(v) => `$${Number(v).toLocaleString()}`} />
        <DiffMetric label="Own Σ" a={aL.own_sum} b={bL.own_sum} delta={ownDelta} fmt={(v) => `${Number(v).toFixed(1)}%`} lowerIsBetter />
      </div>

      {/* Side-by-side roster */}
      <div className="grid grid-cols-[80px_1fr_1fr] gap-2">
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center">Slot</div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold">Lineup #{a + 1}</div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold">Lineup #{b + 1}</div>
        {slotKeys.map((slot) => {
          const aP = mapA.get(slot);
          const bP = mapB.get(slot);
          const tone = cellTone(aP, bP);
          return (
            <React.Fragment key={slot}>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center px-2 border border-zinc-900 bg-black/40">{slot}</div>
              <PlayerCell p={aP} tone={tone} />
              <PlayerCell p={bP} tone={tone} />
            </React.Fragment>
          );
        })}
      </div>
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-600 mt-3 leading-relaxed">
        Green cells = same player at same slot. Yellow cells = different players (the delta drivers).
      </div>
    </section>
  );
}

function DiffMetric({ label, a, b, delta, fmt, biggerIsBetter, lowerIsBetter }) {
  const wins =
    (biggerIsBetter && delta > 0) ||
    (lowerIsBetter && delta < 0);
  const loses =
    (biggerIsBetter && delta < 0) ||
    (lowerIsBetter && delta > 0);
  const deltaTone = wins ? "text-[#39FF14]" : loses ? "text-[#FF3B30]" : "text-zinc-400";
  const deltaSign = delta > 0 ? "+" : "";
  return (
    <div className="border border-zinc-800 bg-black p-2">
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="flex items-baseline justify-between gap-2">
        <div className="mono text-sm text-white font-bold">{fmt(a || 0)}</div>
        <div className="mono text-[10px] text-zinc-500">vs</div>
        <div className="mono text-sm text-white font-bold">{fmt(b || 0)}</div>
      </div>
      <div className={`mono text-[10px] uppercase tracking-widest font-black mt-1 ${deltaTone}`}>
        Δ {deltaSign}{fmt(delta || 0)}
      </div>
    </div>
  );
}

/**
 * Sprint 60.81 — Cross-Contest Simulator.
 *
 * "How would this exact portfolio perform in a $3 Milly Maker vs a $20
 * mid GPP vs a Double-Up?" The answer nobody else in the space shows.
 * We reuse the same product-of-ownerships → dupe → cash math as the
 * dupe estimator but project across three canonical contest shapes.
 *
 * Payout curves are rough industry averages (top-heavy for Milly,
 * flatter for Mid GPP, flat for Cash). All figures are estimates —
 * we surface them for triage, not for booking bets.
 */
function CrossContestSim({ lineups }) {
  const [running, setRunning] = useState(false);
  const [scenarios, setScenarios] = useState(null);

  // Contest presets — { key, label, field, entry, cash_line, top_prize, mode }
  const CONTESTS = [
    // Real DK shapes (2025-26 flagship $20 contests): Milly ≈176k entries / $1M top,
    // Flea Flicker ≈118k / $100k, Screen Pass-tier ≈23.5k / $20k.
    { key: "milly",    label: "Milly Maker · $20",  field: 176_470, entry: 20,  cash_line: 135, top_prize: 1_000_000, mode: "gpp" },
    { key: "large_gpp", label: "Flea Flicker · $20", field: 117_647, entry: 20,  cash_line: 133, top_prize: 100_000,   mode: "gpp" },
    { key: "mid_gpp",   label: "Mid GPP · $20",      field: 23_529,  entry: 20,  cash_line: 130, top_prize: 20_000,    mode: "gpp" },
    { key: "small_gpp", label: "Small GPP · $20", field: 1_500,     entry: 20,  cash_line: 128, top_prize: 2_500,     mode: "gpp" },
    { key: "cash",      label: "Double-Up · $20", field: 100,       entry: 20,  cash_line: 125, top_prize: 36,        mode: "cash" },
  ];

  const runSim = () => {
    setRunning(true);
    // Small deferred update so the button state renders before math.
    setTimeout(() => {
      const rows = CONTESTS.map((c) => {
        const per = lineups.map((l) => {
          const own = Number(l.own_sum || 0);
          const proj = Number(l.projection || 0);
          const avgOwn = own / 9 / 100;
          const prod = Math.max(0, Math.pow(avgOwn, 9));
          const dupes = prod * c.field;
          // Cash prob: cash if proj >= cash_line — 90th %ile threshold
          // Bump by projection gap to cash_line
          const gap = proj - c.cash_line;
          const cashHitPct = Math.min(95, Math.max(2, 50 + gap * 3));
          // Milly hit: proj-driven, extremely thin
          const millyHitPct = c.mode === "gpp"
            ? Math.min(1.5, Math.max(0.001, ((proj - 140) / 100) * (1 / (1 + dupes))))
            : 0;
          return { proj, own, dupes, cashHitPct, millyHitPct };
        });

        const avgProj = per.length ? per.reduce((s, r) => s + r.proj, 0) / per.length : 0;
        const avgOwn = per.length ? per.reduce((s, r) => s + r.own, 0) / per.length : 0;
        const uniqueCount = per.filter((r) => r.dupes < 1).length;
        const avgCashPct = per.length ? per.reduce((s, r) => s + r.cashHitPct, 0) / per.length : 0;

        // Expected value estimate:
        //   EV(lineup) = P(cash) * (avg_cash_payout - entry) + P(top) * top_prize
        //   For cash, avg_cash_payout = 1.8× entry (typical double-up)
        //   For gpp, avg_cash_payout = 1.5× entry (min-cash usually 1.5×)
        const avgCashPayout = c.mode === "cash" ? c.entry * 1.8 : c.entry * 1.5;
        const avgTopHit = per.length ? per.reduce((s, r) => s + r.millyHitPct, 0) / per.length / 100 : 0;
        const evPerLineup =
          (avgCashPct / 100) * (avgCashPayout - c.entry) +
          avgTopHit * c.top_prize -
          (1 - avgCashPct / 100) * c.entry;
        const evTotal = evPerLineup * per.length;
        const roi = c.entry > 0 ? (evPerLineup / c.entry) * 100 : 0;

        return {
          ...c,
          avgProj,
          avgOwn,
          uniqueCount,
          avgCashPct,
          avgTopHit,
          evPerLineup,
          evTotal,
          roi,
        };
      });
      setScenarios(rows);
      setRunning(false);
    }, 30);
  };

  return (
    <section
      data-testid="raw-cross-contest-sim"
      className="bg-[#121212] border border-zinc-800 p-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="heading text-lg font-black uppercase">Cross-Contest Simulator</div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
            How this exact {lineups.length}-lineup portfolio grades across contest shapes
          </div>
        </div>
        <button
          type="button"
          onClick={runSim}
          disabled={running}
          data-testid="raw-run-sim"
          className="bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[10px] px-4 py-2 transition-colors"
        >
          {running ? "Simulating…" : scenarios ? "Re-run Sim" : "Run Simulation"}
        </button>
      </div>

      {!scenarios ? (
        <div className="text-center py-8 text-zinc-500 mono text-xs uppercase tracking-widest">
          Click "Run Simulation" to grade this portfolio across 5 canonical contest shapes.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] mono">
            <thead className="border-b border-zinc-800 bg-black">
              <tr className="text-zinc-500 text-left uppercase tracking-widest text-[9px]">
                <th className="px-3 py-2">Contest</th>
                <th className="px-3 py-2 text-right">Field</th>
                <th className="px-3 py-2 text-right">Unique</th>
                <th className="px-3 py-2 text-right">Cash %</th>
                <th className="px-3 py-2 text-right">Top Prize %</th>
                <th className="px-3 py-2 text-right">EV / Lineup</th>
                <th className="px-3 py-2 text-right">EV Total</th>
                <th className="px-3 py-2 text-right">ROI</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => (
                <tr key={s.key} className="border-b border-zinc-900" data-testid={`raw-sim-row-${s.key}`}>
                  <td className="px-3 py-2 text-white font-bold">{s.label}</td>
                  <td className="px-3 py-2 text-right text-zinc-400">{s.field.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-[#39FF14]">{s.uniqueCount}/{lineups.length}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{s.avgCashPct.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right text-zinc-300">
                    {s.mode === "cash" ? "—" : `${(s.avgTopHit * 100).toFixed(3)}%`}
                  </td>
                  <td className={`px-3 py-2 text-right font-bold ${s.evPerLineup >= 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                    ${s.evPerLineup.toFixed(2)}
                  </td>
                  <td className={`px-3 py-2 text-right font-bold ${s.evTotal >= 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                    ${s.evTotal.toFixed(0)}
                  </td>
                  <td className={`px-3 py-2 text-right font-black ${s.roi >= 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                    {s.roi > 0 ? "+" : ""}{s.roi.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] mono uppercase tracking-widest text-zinc-600 mt-3 leading-relaxed">
            Estimates. Cash % is projection-vs-cash-line based; top-prize % is (proj-140)/100 × 1/(1+dupes); payout curves are industry averages.
            Positive ROI on Milly requires ceiling + uniqueness — chalk-heavy portfolios flip red here.
          </div>
        </div>
      )}
    </section>
  );
}



// Sprint 62.6 — Exposure loop done right: change caps, KEEP every lineup that
// already satisfies them, re-solve only the excess (the over-cap player is
// swapped out, the other 8 stay, uniqueness preserved).
function RawExposurePanel({ lineups, onApplied }) {
  const [caps, setCaps] = useState({});
  const [globalCap, setGlobalCap] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => {
    const m = {};
    (lineups || []).forEach((l) => (l.players || []).forEach((p) => {
      const k = String(p.dk_id);
      m[k] = m[k] || { dk_id: k, name: p.name, position: p.position, team: p.team, count: 0 };
      m[k].count += 1;
    }));
    return Object.values(m).sort((a, b) => b.count - a.count);
  }, [lineups]);
  const n = (lineups || []).length;
  if (!n) return null;
  const pending = Object.entries(caps).filter(([k, v]) => v !== "" && v != null && Number(v) < (rows.find((r) => r.dk_id === k)?.count || 0));
  const apply = async () => {
    setBusy(true);
    try {
      const body = { max_exposures: Object.fromEntries(pending.map(([k, v]) => [k, Number(v)])) };
      if (globalCap !== "") body.max_exposure_pct = Number(globalCap);
      const r = await http.post("/optimize/raw/rebuild-excess", body);
      toast.success(`Rebuilt ${r.data.rebuilt.length} lineup${r.data.rebuilt.length === 1 ? "" : "s"} · ${n - r.data.rebuilt.length} kept as-is${r.data.failed.length ? ` · ${r.data.failed.length} couldn't be re-solved` : ""}`);
      setCaps({});
      onApplied?.(r.data.lineups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Rebuild failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-zinc-800 bg-[#0e0e0e] p-3 mb-3" data-testid="raw-exposure-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold">Exposure · edit caps, rebuild only the excess</div>
          <div className="mono text-[9px] text-zinc-500">Type a new max (lineups) next to any player. Lineups already under the cap are kept exactly as they are.</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            Global cap %
            <input type="number" min={1} max={100} value={globalCap} placeholder="—" onChange={(e) => setGlobalCap(e.target.value)}
              data-testid="raw-exposure-global-cap" className="w-14 bg-black border border-zinc-800 px-1 py-0.5 mono text-[11px] text-white" />
          </label>
          <button type="button" onClick={apply} disabled={busy || (pending.length === 0 && globalCap === "")}
            data-testid="raw-exposure-apply"
            className="bg-[#FFCC00] hover:bg-[#e0b400] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[10px] px-3 py-1.5">
            {busy ? "Re-solving…" : `Apply & rebuild excess${pending.length ? ` (${pending.length})` : ""}`}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1 max-h-56 overflow-y-auto">
        {rows.slice(0, 48).map((r) => {
          const pct = (r.count / n) * 100;
          return (
            <div key={r.dk_id} className="flex items-center gap-2 border border-zinc-900 px-2 py-1" data-testid={`raw-exposure-row-${r.dk_id}`}>
              <div className="flex-1 min-w-0">
                <div className="mono text-[10px] text-zinc-200 truncate">{r.name} <span className="text-zinc-600">{r.position}</span></div>
                <div className="h-1 bg-zinc-900 mt-0.5"><div className={`h-full ${pct > 50 ? "bg-[#FF3B30]" : pct > 30 ? "bg-[#FFCC00]" : "bg-[#39FF14]/70"}`} style={{ width: `${pct}%` }} /></div>
              </div>
              <span className="mono text-[10px] text-zinc-400 w-12 text-right">{r.count}/{n}</span>
              <input type="number" min={0} max={n} value={caps[r.dk_id] ?? ""} placeholder="max"
                onChange={(e) => setCaps({ ...caps, [r.dk_id]: e.target.value })}
                data-testid={`raw-exposure-cap-${r.dk_id}`}
                className="w-12 bg-black border border-zinc-800 px-1 py-0.5 mono text-[10px] text-white placeholder:text-zinc-700" />
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Sprint 62.6 — save / load the entire Manual rule set (localStorage, per browser).
function RulePresets({ cfg, setCfg }) {
  const KEY = "wp_raw_rule_presets";
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } };
  const [presets, setPresets] = useState(read);
  const [name, setName] = useState("");
  const save = () => {
    const n = name.trim();
    if (!n) { toast.error("Name the preset first"); return; }
    const next = { ...presets, [n]: cfg };
    localStorage.setItem(KEY, JSON.stringify(next));
    setPresets(next); setName("");
    toast.success(`Saved rules as "${n}"`);
  };
  const load = (n) => { if (presets[n]) { setCfg({ ...cfg, ...presets[n] }); toast.success(`Loaded "${n}"`); } };
  const remove = (n) => { const next = { ...presets }; delete next[n]; localStorage.setItem(KEY, JSON.stringify(next)); setPresets(next); };
  return (
    <div className="border border-zinc-800 bg-[#0e0e0e] p-3" data-testid="raw-rule-presets">
      <div className="mono text-[10px] uppercase tracking-widest text-zinc-300 mb-2">Rule presets</div>
      <div className="flex items-center gap-2 mb-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My Milly 150"
          data-testid="raw-preset-name" className="flex-1 bg-black border border-zinc-800 px-2 py-1 mono text-[11px] placeholder:text-zinc-700" />
        <button type="button" onClick={save} data-testid="raw-preset-save"
          className="border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black mono uppercase tracking-widest text-[9px] px-2 py-1">Save</button>
      </div>
      <div className="flex flex-wrap gap-1">
        {Object.keys(presets).length === 0 && <span className="mono text-[9px] text-zinc-600">No presets yet — save your current stacks / exposure / diversity rules to reuse every week.</span>}
        {Object.keys(presets).map((n) => (
          <span key={n} className="inline-flex items-center border border-zinc-800">
            <button type="button" onClick={() => load(n)} data-testid={`raw-preset-load-${n.replace(/\s+/g, "-")}`}
              className="mono text-[9px] uppercase tracking-widest text-zinc-300 hover:text-[#39FF14] px-2 py-1">{n}</button>
            <button type="button" onClick={() => remove(n)} title="Delete preset" className="text-zinc-600 hover:text-[#FF3B30] px-1.5">×</button>
          </span>
        ))}
      </div>
    </div>
  );
}
